/**
 * Client workspace · Content tab.
 *
 * Surfaces (each gated by permissions):
 *
 *   • LIST view — read-only browse of `content_items` (Task 030 reads).
 *   • Calendar view — Task 068 month grid wired against
 *     `list-calendar-entries-by-month`.
 *   • AI calendar draft generator (Task 067) — `generate-content-
 *     calendar-draft` → editable preview → `save-content-calendar-draft`.
 *   • Manual calendar add (Task 067) — same editable template, no AI.
 *   • Edit a draft calendar entry (Task 068) — atomic via
 *     `update-content-calendar-draft` (only when item.status='draft').
 *   • Workflow transitions (Task 068) — `transition-content-item-
 *     status`. State-machine: draft ↔ pending_internal_review ↔
 *     internal_approved ↔ pending_client_content_approval ↔
 *     client_content_approved. NO publishing, NO platform execution.
 *
 * Frontend permission gates mirror the SECURITY DEFINER RPCs that
 * back each write surface; RLS / the RPCs are the load-bearing
 * security gates.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { isApiError, type KmiApiError } from '../../api/client';
import { SendToWritingPanel } from './SendToWritingPanel';
import {
  archiveContentCreativeAsset,
  cancelPublishJob,
  completeCreativeAssetUpload,
  createCreativeAssetUpload,
  generateContentCalendarDraft,
  getClientOperatingSetup,
  getContentItem,
  listCalendarEntriesByMonth,
  listContentApprovals,
  listContentCreativeAssets,
  listContentItems,
  listContentVariants,
  listPendingClientApprovals,
  listProductionTasksForContentItem,
  listPublishJobs,
  markPublishJobFailed,
  markPublishJobPublished,
  prepareProductionTaskUpload,
  putToSignedUploadUrl,
  saveContentCalendarDraft,
  scheduleContentItemPublish,
  startProductionTask,
  submitProductionTask,
  transitionContentItemStatus,
  updateContentCalendarDraft,
} from '../../api/endpoints';
import type {
  CalendarEntryRow,
  ClientOperatingSetup,
  ContentApprovalDecision,
  ContentApprovalRow,
  ContentApprovalStage,
  ContentCalendarMode,
  ContentDiscipline,
  ContentItemDetailRow,
  ContentItemListRow,
  ContentItemStatus,
  ContentItemTransitionTarget,
  ContentVariantPlatform,
  ContentVariantProductionBrief,
  ContentVariantRow,
  ContentVariantStatus,
  CreativeAssetCardRow,
  CreativeAssetTargetSource,
  CreativeAssetUploadKind,
  GenerateContentCalendarDraftData,
  PendingClientApprovalDeliverable,
  PendingClientApprovalRow,
  PendingClientApprovalStage,
  ProductionTaskRow,
  PublishJobRow,
  PublishWorkflowPlatform,
  SaveContentCalendarDraftItemInput,
  SaveContentCalendarDraftPlatformSpecific,
  SaveContentCalendarDraftVariantInput,
} from '../../api/types';
import { PUBLISH_WORKFLOW_PLATFORMS } from '../../api/types';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';
import { useMeContext } from '../../context/MeContextProvider';
import { hasAnyPermission, hasClientAssignment, hasRoleCategory, PERM } from '../../permissions';
import { ProductionRoutingControls } from './ProductionRoutingControls';
import type { ClientWorkspaceOutletContext } from '../ClientWorkspaceLayout';
import {
  deriveContentCalendarPrefill,
  mergeContentCalendarPrefill,
  PLATFORMS_MAX,
  type EntryCountReason,
} from './contentCalendarDefaults';
import { applyScheduleSlots, buildScheduleSlots } from './scheduleSlots';
import {
  ASSET_FORMATS,
  resolveCompatibleTargets,
  resolveRequestedOutputs,
  targetToPlatform,
  type AssetFormat,
  type SocialPlatform,
} from '@kmi/contracts';

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: ReadonlyArray<ContentItemListRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly item: ContentItemDetailRow;
      readonly variants: ReadonlyArray<ContentVariantRow>;
      readonly approvals: ReadonlyArray<ContentApprovalRow>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

// ─────────────────────────────────────────────────────────────────────
// Task 290B — client-facing PUBLIC status groups.
//
// Client-category viewers must never see agency-internal workflow
// vocabulary. RLS (20260821120000) already limits their rows to six
// statuses; this deterministic mapping folds those into the five
// public concepts (الكل / بانتظار موافقتك / جاهز للنشر / مجدول /
// منشور). It is DISPLAY + client-side filtering only: the fetch and
// its params are untouched (zero data-scope change), and agency/staff
// rendering keeps the full internal vocabulary unchanged.
//
// Mapping decision (documented + pinned): `client_content_approved`
// (content approved, production ongoing) groups under «جاهز للنشر»
// with `client_asset_approved` — the pipeline is out of the client's
// hands in both. Statuses outside the RLS window have NO public label
// (fail-closed: no pill renders) — they cannot occur for clients.
// ─────────────────────────────────────────────────────────────────────
type ClientStatusGroupValue =
  | 'all'
  | 'awaiting_approval'
  | 'ready_to_publish'
  | 'scheduled'
  | 'published';

const CLIENT_STATUS_GROUPS: ReadonlyArray<{
  readonly value: ClientStatusGroupValue;
  readonly label: string;
  readonly statuses: ReadonlyArray<ContentItemStatus>;
}> = [
  { value: 'all', label: 'الكل', statuses: [] },
  {
    value: 'awaiting_approval',
    label: 'بانتظار موافقتك',
    statuses: ['pending_client_content_approval', 'pending_client_asset_approval'],
  },
  {
    value: 'ready_to_publish',
    label: 'جاهز للنشر',
    statuses: ['client_content_approved', 'client_asset_approved'],
  },
  { value: 'scheduled', label: 'مجدول', statuses: ['scheduled'] },
  { value: 'published', label: 'منشور', statuses: ['published'] },
];

/** Public group label for a status, or null when the status has no
 *  client-facing concept (unreachable for clients under RLS). */
function clientPublicStatusLabel(status: string): string | null {
  for (const group of CLIENT_STATUS_GROUPS) {
    if (group.value !== 'all' && (group.statuses as ReadonlyArray<string>).includes(status)) {
      return group.label;
    }
  }
  return null;
}

function clientPublicStatusVariant(status: string): string {
  switch (status) {
    case 'pending_client_content_approval':
    case 'pending_client_asset_approval':
      return 'review';
    case 'client_content_approved':
    case 'client_asset_approved':
    case 'scheduled':
      return 'info';
    case 'published':
      return 'active';
    default:
      return 'draft';
  }
}

function itemMatchesClientGroup(status: string, group: ClientStatusGroupValue): boolean {
  if (group === 'all') return true;
  const def = CLIENT_STATUS_GROUPS.find((g) => g.value === group);
  return def ? (def.statuses as ReadonlyArray<string>).includes(status) : true;
}

// Filter chips track the statuses items can actually reach today.
// Status (date wired):
//   * `draft`, `pending_internal_review`, `internal_approved`,
//     `pending_client_content_approval`, `client_content_approved`
//     — Task 070 / 102 (re-route fix landed in 104B but the
//     content-stage path is still wired).
//   * `in_production` — Task 101B production handoff (Task 101C
//     wired the FE CTA).
//   * `pending_client_asset_approval`, `client_asset_approved`
//     — Task 104B final asset approval (step 5 of source-of-truth).
//   * `scheduled`, `published` — Task 104C publishing workflow V1
//     (manual mark-published path; the schedule/cancel/mark RPCs
//     drive the parent transitions). Chips added so the operator can
//     filter the per-platform job board out of the calendar list.
const STATUS_FILTERS: ReadonlyArray<{
  readonly value: ContentItemStatus | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'الكل' },
  { value: 'draft', label: 'مسودة' },
  { value: 'pending_internal_review', label: 'مراجعة داخلية' },
  { value: 'internal_approved', label: 'معتمد داخلياً' },
  { value: 'pending_client_content_approval', label: 'مراجعة محتوى' },
  { value: 'client_content_approved', label: 'موافقة العميل' },
  // Task 101C — re-added after the backend handoff went live.
  { value: 'in_production', label: 'قيد الإنتاج' },
  // Task 104B — final asset approval (step 5).
  { value: 'pending_client_asset_approval', label: 'مراجعة الأصل النهائي' },
  { value: 'client_asset_approved', label: 'اعتماد الأصل النهائي' },
  // Task 104C — publishing workflow V1.
  { value: 'scheduled', label: 'مجدول للنشر' },
  { value: 'published', label: 'منشور' },
];

const DISCIPLINE_FILTERS: ReadonlyArray<{
  readonly value: ContentDiscipline | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'كل التخصصات' },
  { value: 'content', label: 'محتوى كتابي' },
  { value: 'design', label: 'تصميم' },
  { value: 'video', label: 'فيديو' },
];

type CalendarView =
  | { readonly kind: 'list' }
  | { readonly kind: 'mode-chooser' }
  // Task 105A — manual content is now a SINGLE-POST modal, not the
  // pre-105A empty month-grid that mirrored the AI calendar batch.
  // `manual-single-post` opens `ManualSinglePostCard` which collects
  // one post (title + discipline + date/time + per-platform copy)
  // and saves exactly ONE content_item via save-content-calendar-draft.
  // The pre-105A `manual-form` view is retired — the empty month grid
  // confused operators who wanted to add a single post and ended up
  // creating month-style batches with placeholder rows.
  | { readonly kind: 'manual-single-post' }
  | {
      readonly kind: 'generating';
      readonly form: AiCalendarFormState;
    }
  | {
      readonly kind: 'draft';
      readonly draft: DraftState;
      readonly saveError: string | null;
      readonly saving: boolean;
    };

interface AiCalendarFormState {
  readonly mode: ContentCalendarMode;
  readonly month: string;
  readonly entryCount: number;
  // Task 283 — the user picks parent platforms FIRST, then one or more asset
  // formats; `platforms` is the DERIVED publish_target_v1[] (= the deduped union
  // of resolveRequestedOutputs(...).targets) and stays the request source of
  // truth + the variant-tiling source. assetFormats / parentPlatforms are FE-only
  // and are NEVER sent in the generate payload.
  readonly assetFormats: ReadonlyArray<AssetFormat>;
  readonly parentPlatforms: ReadonlyArray<SocialPlatform>;
  // Task 281/283 — true once the user toggles a parent platform directly. Used as
  // the manual-override sentinel for the operating-setup platform prefill INSTEAD
  // of array identity. (Formats are not in the operating setup, so they are only
  // normalized to stay compatible with the selected platforms, never seeded.)
  readonly parentPlatformsManuallyEdited: boolean;
  readonly platforms: ReadonlyArray<ContentVariantPlatform>;
  readonly instruction: string;
  readonly timezone: string;
}

export interface DraftItemState {
  readonly key: string;
  readonly title: string;
  readonly body: string;
  readonly scheduledDate: string;
  readonly scheduledTimeLocal: string;
  readonly variants: ReadonlyArray<DraftVariantState>;
}

// Task 108A — production brief fields carried alongside the existing
// caption / body / hashtags. Every field is optional. Empty strings
// are dropped on save so the persisted `platform_specific` jsonb
// stays compact and the FE can treat absent ↔ empty interchangeably
// when reading.
interface DraftVariantBriefState {
  // Static / image-like platforms.
  readonly onImageText: string;
  readonly visualDirection: string;
  readonly creativeBrief: string;
  readonly assetFormat: string;
  // Video-like platforms.
  readonly hook: string;
  readonly script: string;
  readonly shotList: string;
  readonly onScreenText: string;
  readonly audioDirection: string;
  // Optional integer 1..3600. Stored as a string here so the
  // controlled `<input type="number">` can keep an empty value
  // distinct from `0`. We coerce on save.
  readonly durationSeconds: string;
}

export interface DraftVariantState {
  readonly platform: ContentVariantPlatform;
  readonly headline: string;
  readonly caption: string;
  readonly body: string;
  readonly hashtags: ReadonlyArray<string>;
  // Task 108A — production brief. Always present on the state shape
  // (initialised empty by `defaultEmptyDraftItem` /
  // `aiResponseToDraft`) so the JSX can read `v.brief.<field>`
  // without a null check.
  readonly brief: DraftVariantBriefState;
  // Task 286A — per-variant publish status, present only when this shape is
  // reused as the canonical review-card variant on the SAVED content detail
  // (adapted from ContentVariantRow). The draft flow leaves it undefined.
  readonly status?: ContentVariantStatus;
  // Task 286F — the production call-to-action (`platform_specific.cta`) + the
  // destination link (`platform_specific.link`, e.g. a swipe-up / bio URL the design
  // points to), surfaced read-only on the saved / staff card so production staff
  // don't lose them (the old designer brief showed BOTH via
  // DESIGNER_STATIC_BRIEF_FIELDS). Optional: the AI draft + the client-safe adapter
  // leave them undefined, so the row stays a placeholder and is always client-gated.
  readonly cta?: string;
  readonly link?: string;
}

interface DraftState {
  readonly origin: 'manual' | 'ai_assisted';
  readonly mode: ContentCalendarMode | null;
  readonly aiRunId: string | null;
  readonly month: string;
  readonly timezone: string;
  readonly platforms: ReadonlyArray<ContentVariantPlatform>;
  readonly items: ReadonlyArray<DraftItemState>;
  readonly aiSummary: AiSummary | null;
  readonly aiWarnings: ReadonlyArray<{ readonly code: string; readonly message: string }>;
}

interface AiSummary {
  readonly model: string;
  readonly costUsd: number;
  readonly tokensInput: number;
  readonly tokensOutput: number;
  readonly durationMs: number;
}

// Task 068 — calendar surface state shapes.
type CalendarLoadState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly entries: ReadonlyArray<CalendarEntryRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

// Task 069 — client approval inbox state shape.
type InboxLoadState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: ReadonlyArray<PendingClientApprovalRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

// Task 104C — async load state for the per-content-item publish-jobs
// fetch. Wraps the `list-publish-jobs?content_item_id=` response, which
// returns one PublishJobRow per platform (instagram / tiktok / snapchat
// for V1) including the embedded publish_target / content_variant /
// content_item triple.
type PublishJobsLoadState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly jobs: ReadonlyArray<PublishJobRow> }
  | { readonly status: 'error'; readonly error: unknown };

// Task 104C — schedule-publishing modal state. Held at page level so
// the modal can survive detail re-renders triggered by the optimistic
// transition flow. `selectedPlatforms` is a Set so toggling is O(1);
// converted to an array on submit.
//
// Approval boundary (round 2 fix): per-platform caption overrides
// were REMOVED. Captions are customer-approved at the
// `client_asset_approved` boundary and the schedule modal cannot
// rewrite them. Only operator notes are accepted.
//
// Round 3 fix: `availablePlatforms` is computed from the loaded
// content_variants and threaded through. The modal disables
// platforms that don't have a matching variant on the parent item
// — the schedule RPC raises 23514 for missing variants, so the FE
// mirrors that gate.
interface ScheduleModalState {
  readonly contentItemId: string;
  readonly selectedPlatforms: ReadonlySet<PublishWorkflowPlatform>;
  readonly availablePlatforms: ReadonlySet<PublishWorkflowPlatform>;
  readonly scheduledLocalDate: string;
  readonly scheduledLocalTime: string;
  readonly plannedScheduledForIso: string | null;
  readonly timezone: string;
  readonly notes: string;
  readonly saving: boolean;
  readonly error: string | null;
}

// Task 105A — structured client request-changes feedback. Replaces
// the pre-105A `window.prompt('سبب طلب التعديل')`. Captures three
// fields: required `feedbackNotes`, optional `rejectionCategory`
// (free choice from a small enum), and optional `editedVersion`
// (suggested rewrite). The three fields are serialized into the
// existing `reason` text on the transition RPC so the existing
// content_approvals decision audit (Task 068) carries the structured
// feedback verbatim. Storing them as separate columns / metadata
// jsonb keys is a follow-up task.
type ClientRejectionCategory = '' | 'content_quality' | 'tone' | 'timing' | 'imagery' | 'other';

interface RequestChangesModalState {
  readonly contentItemId: string;
  readonly contentItemTitle: string;
  readonly feedbackNotes: string;
  readonly rejectionCategory: ClientRejectionCategory;
  readonly editedVersion: string;
  readonly saving: boolean;
  readonly error: string | null;
}

interface EditModalState {
  readonly entry: CalendarEntryRow;
  readonly title: string;
  readonly body: string;
  readonly headline: string;
  readonly caption: string;
  readonly variantBody: string;
  readonly hashtags: string;
  readonly cta: string;
  readonly link: string;
  readonly scheduledLocalDate: string;
  readonly scheduledLocalTime: string;
  readonly timezone: string;
  readonly saving: boolean;
  readonly error: string | null;
}

const DEFAULT_TIMEZONE = 'Asia/Riyadh';

// Task 290A — stable DOM anchor for the production-task panel so the
// /app/me/tasks deep link can scroll to it once loaded (repo precedent:
// OnboardingWizardPage section anchors + getElementById/scrollIntoView).
const PRODUCTION_TASK_PANEL_ANCHOR_ID = 'production-task-panel';
const DEFAULT_PLATFORMS: ReadonlyArray<ContentVariantPlatform> = ['instagram_feed'];

// Task 107D — the AI calendar generator's renderable platform allow-list
// (Facebook excluded). Task 280B filters seeded platforms to this set so a
// seeded platform is always visible/removable in the picker.
const AI_V1_PLATFORM_OPTIONS: ReadonlyArray<ContentVariantPlatform> = [
  'instagram_feed',
  'instagram_story',
  'instagram_reel',
  'tiktok_video',
  'snapchat_story',
];

// Task 281/283 — the AI calendar picker is platform-level, not placement-level,
// and platform-FIRST: the user chooses parent platforms, then one or more asset
// formats; the placement targets (publish_target_v1[]) are resolved from the
// Task 280L/282 matrix via resolveRequestedOutputs. Facebook stays out of AI V1,
// so it is absent from the parent-platform options.
const DEFAULT_ASSET_FORMATS: ReadonlyArray<AssetFormat> = ['static_image'];
const DEFAULT_PARENT_PLATFORMS: ReadonlyArray<SocialPlatform> = ['instagram'];
const AI_V1_PARENT_PLATFORMS: ReadonlyArray<SocialPlatform> = ['instagram', 'tiktok', 'snapchat'];
const AI_ASSET_FORMAT_OPTIONS: ReadonlyArray<{
  readonly value: AssetFormat;
  readonly label: string;
}> = [
  { value: 'static_image', label: 'صورة ثابتة' },
  { value: 'carousel', label: 'كاروسيل' },
  { value: 'vertical_video', label: 'فيديو عمودي' },
  { value: 'story', label: 'ستوري' },
];

function parentPlatformLabelAr(platform: SocialPlatform): string {
  switch (platform) {
    case 'instagram':
      return 'إنستجرام';
    case 'tiktok':
      return 'تيك توك';
    case 'snapchat':
      return 'سناب شات';
    default:
      return platform;
  }
}

/** Task 283 — Arabic label for an asset format (reuses the picker option labels). */
function assetFormatLabelAr(format: AssetFormat): string {
  return AI_ASSET_FORMAT_OPTIONS.find((o) => o.value === format)?.label ?? format;
}

/**
 * Task 281 — translate placement-level publish targets (e.g. seeded from the
 * operating-setup) into the AI-V1 parent platforms for the picker. Dedupes and
 * keeps only AI-V1 parents (Facebook is excluded from AI V1).
 */
function aiParentsFromTargets(targets: ReadonlyArray<ContentVariantPlatform>): SocialPlatform[] {
  const out: SocialPlatform[] = [];
  const seen = new Set<SocialPlatform>();
  for (const t of targets) {
    const parent = targetToPlatform(t);
    if (AI_V1_PARENT_PLATFORMS.includes(parent) && !seen.has(parent)) {
      seen.add(parent);
      out.push(parent);
    }
  }
  return out;
}

/**
 * Task 283 — the asset formats the selected parent platforms can actually carry
 * (per the Task 280L/282 matrix), e.g. [instagram] → all four, [tiktok] →
 * [carousel, vertical_video], [snapchat] → [vertical_video, story]. Platform-first:
 * the format chips render ONLY these, so a user can never pick a format that
 * resolves to nothing. Returned in canonical ASSET_FORMATS order.
 */
function compatibleAiAssetFormats(platforms: ReadonlyArray<SocialPlatform>): AssetFormat[] {
  return ASSET_FORMATS.filter((f) => resolveCompatibleTargets(f, platforms).length > 0);
}

/** Task 283 — the given formats in canonical ASSET_FORMATS order (stable groups). */
function orderedAssetFormats(formats: ReadonlyArray<AssetFormat>): AssetFormat[] {
  return ASSET_FORMATS.filter((f) => formats.includes(f));
}

/**
 * Task 283 — the deduped, order-stable union of publish targets for the selected
 * (formats × platforms). This is the `platforms` request field + the variant-tiling
 * source; multi-format grouping is FE-only display (see resolveRequestedOutputs).
 */
function aiResolvedTargets(
  formats: ReadonlyArray<AssetFormat>,
  platforms: ReadonlyArray<SocialPlatform>,
): ContentVariantPlatform[] {
  const out: ContentVariantPlatform[] = [];
  const seen = new Set<ContentVariantPlatform>();
  for (const group of resolveRequestedOutputs(orderedAssetFormats(formats), platforms)) {
    for (const t of group.targets) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
  }
  return out;
}

/**
 * Task 283 — keep the selected formats compatible with the selected platforms:
 * drop any format the platforms can't carry. If that empties the set but platforms
 * are still selected, fall back to the first compatible format so the picker is
 * never silently empty (formats follow platforms in the platform-first model;
 * formats are normalized, never seeded from the operating setup).
 */
function normalizeAssetFormats(
  formats: ReadonlyArray<AssetFormat>,
  platforms: ReadonlyArray<SocialPlatform>,
): AssetFormat[] {
  const compatible = compatibleAiAssetFormats(platforms);
  const compatibleSet = new Set(compatible);
  const kept = orderedAssetFormats(formats).filter((f) => compatibleSet.has(f));
  if (kept.length > 0) return kept;
  return compatible.length > 0 ? [compatible[0]] : [];
}

// Task 107D — the global `PLATFORM_OPTIONS` was retired. Pre-107D it
// drove the AI calendar generator chip group + the manual form chip
// group, both of which now use scoped V1 allow-lists
// (`AI_V1_PLATFORM_OPTIONS` + `MANUAL_V1_PLATFORM_OPTIONS`) that
// exclude Facebook. The cross-surface labels live on
// `variantPlatformLabel` (legacy variants display) and the aspect
// map `PLATFORM_ASPECT` — both of those still cover all 7 supported
// platforms for backward compatibility with previously-saved data.

function nextMonthYYYYMM(now = new Date()): string {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 2; // next month, 1-indexed
  const yyyy = month > 12 ? year + 1 : year;
  const mm = ((month - 1) % 12) + 1;
  return `${yyyy.toString().padStart(4, '0')}-${mm.toString().padStart(2, '0')}`;
}

function genKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Task 108A — empty production brief. Used as the initial value for
// every variant created via `defaultEmptyDraftItem` /
// `aiResponseToDraft` / `togglePlatform`. Keeping this in one place
// guarantees the brief shape is stable across all entry points.
function defaultEmptyBrief(): DraftVariantBriefState {
  return {
    onImageText: '',
    visualDirection: '',
    creativeBrief: '',
    assetFormat: '',
    hook: '',
    script: '',
    shotList: '',
    onScreenText: '',
    audioDirection: '',
    durationSeconds: '',
  };
}

// Task 108A — V1 video-format platforms. Used to drive the
// platform-aware production brief sections in
// `ManualSinglePostCard` + `DraftEditor`. instagram_feed /
// instagram_story / snapchat_story / facebook_* are treated as
// static / image-like surfaces.
function isVideoPlatform(platform: ContentVariantPlatform): boolean {
  return platform === 'instagram_reel' || platform === 'tiktok_video';
}

// Task 272 — Instagram surfaces. Drives the read-only post-appearance
// preview, which is Instagram-first in V1 (feed / story / reel). All
// other platforms render no preview yet — see
// `tasks/272-clarify-post-request-handoff-ui.md`.
function isInstagramPlatform(platform: ContentVariantPlatform): boolean {
  return (
    platform === 'instagram_feed' || platform === 'instagram_story' || platform === 'instagram_reel'
  );
}

// Task 285A/294A — story-like surfaces publish WITHOUT a feed caption: the copy
// rides on the design itself (Task 284C body-routing → «نص الستوري»). This
// is a DISPLAY-only cue, not a video/static split — `isVideoPlatform` is
// unchanged and these stay on the static brief field set.
function isStoryLikePlatform(platform: ContentVariantPlatform): boolean {
  return (
    platform === 'instagram_story' || platform === 'snapchat_story' || platform === 'facebook_story'
  );
}

// Task 108A — Arabic labels for the production brief fields. Kept
// adjacent to the `DraftVariantBriefState` keys so the JSX can pull
// the visible string without sprinkling Arabic literals across
// dozens of inline labels.
const BRIEF_LABELS_AR = {
  onImageText: 'نص التصميم',
  visualDirection: 'التوجه البصري',
  creativeBrief: 'موجز التصميم',
  cta: 'الدعوة للإجراء',
  assetFormat: 'نوع الأصل',
  hook: 'الخطاف',
  script: 'السكربت',
  shotList: 'قائمة اللقطات',
  onScreenText: 'النصوص على الشاشة',
  audioDirection: 'توجيه الصوت',
  durationSeconds: 'مدة الفيديو (ثوانٍ)',
} as const;

// Task 108A — `asset_format` soft enum exposed in the manual form
// for static / image-like platforms. Stored as an Arabic-labelled
// `<select>` whose option values match the enum-like strings the
// production task pipeline reads from `platform_specific`.
const ASSET_FORMAT_OPTIONS_AR: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'static_image', label: 'صورة ثابتة' },
  { value: 'story', label: 'ستوري' },
  { value: 'carousel', label: 'كاروسيل' },
];

// Task 108A — fold the per-platform production brief + cta into the
// `platform_specific` jsonb that ships in the save payload. Empty
// strings are dropped so the persisted jsonb stays compact and the
// SQL allow-list never sees a key with an empty value.
//
// Branching on `isVideoPlatform` keeps the wire shape clean: video
// variants don't ship the static-only `on_image_text` /
// `creative_brief` keys (and vice versa). The shared `cta` +
// `visual_direction` keys land for both families when populated.
function briefToPlatformSpecific(
  platform: ContentVariantPlatform,
  brief: DraftVariantBriefState,
  cta: string,
): SaveContentCalendarDraftPlatformSpecific {
  const out: Record<string, unknown> = {};
  const trim = (s: string): string | null => {
    const t = s.trim();
    return t.length > 0 ? t : null;
  };
  const ctaTrim = trim(cta);
  if (ctaTrim) out['cta'] = ctaTrim;
  const visualDirection = trim(brief.visualDirection);
  if (visualDirection) out['visual_direction'] = visualDirection;
  // Task 289B — `asset_format` describes the ASSET, not the copy
  // family, so it is emitted for BOTH families (it was previously
  // static-branch-only, which silently dropped the canonical format
  // for instagram_reel / tiktok_video variants at save time and made
  // the monthly report group them under «غير محدد»). Every downstream
  // allow-list (save EF, save RPC, report projection) already accepts
  // it platform-agnostically.
  const assetFormat = trim(brief.assetFormat);
  if (assetFormat) out['asset_format'] = assetFormat;
  if (isVideoPlatform(platform)) {
    const hook = trim(brief.hook);
    if (hook) out['hook'] = hook;
    const script = trim(brief.script);
    if (script) out['script'] = script;
    const shotList = trim(brief.shotList);
    if (shotList) out['shot_list'] = shotList;
    const onScreenText = trim(brief.onScreenText);
    if (onScreenText) out['on_screen_text'] = onScreenText;
    const audioDirection = trim(brief.audioDirection);
    if (audioDirection) out['audio_direction'] = audioDirection;
    const dur = brief.durationSeconds.trim();
    if (dur.length > 0) {
      const parsed = Number.parseInt(dur, 10);
      if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 3600) {
        out['duration_seconds'] = parsed;
      }
    }
  } else {
    const onImageText = trim(brief.onImageText);
    if (onImageText) out['on_image_text'] = onImageText;
    const creativeBrief = trim(brief.creativeBrief);
    if (creativeBrief) out['creative_brief'] = creativeBrief;
  }
  return out as SaveContentCalendarDraftPlatformSpecific;
}

// Task 108A — manual-form variant → platform_specific. Same shape
// as `briefToPlatformSpecific` plus the legacy `hashtags` array.
function buildPlatformSpecificFromManual(
  v: ManualSinglePostVariantState,
  hashtags: ReadonlyArray<string>,
): SaveContentCalendarDraftPlatformSpecific {
  const base = briefToPlatformSpecific(v.platform, v.brief, v.cta);
  const out: Record<string, unknown> = { ...base };
  if (hashtags.length > 0) out['hashtags'] = hashtags;
  return out as SaveContentCalendarDraftPlatformSpecific;
}

// Task 108A — Read an AI-supplied production brief (when the
// platform-adapted worker filled it in) into the local
// `DraftVariantBriefState` shape. Missing fields default to empty
// strings so the JSX can read them directly.
function briefStateFromAiResponse(
  brief: ContentVariantProductionBrief | undefined,
): DraftVariantBriefState {
  if (!brief) return defaultEmptyBrief();
  const dur = typeof brief.duration_seconds === 'number' ? String(brief.duration_seconds) : '';
  return {
    onImageText: brief.on_image_text ?? '',
    visualDirection: brief.visual_direction ?? '',
    creativeBrief: brief.creative_brief ?? '',
    assetFormat: brief.asset_format ?? '',
    hook: brief.hook ?? '',
    script: brief.script ?? '',
    shotList: brief.shot_list ?? '',
    onScreenText: brief.on_screen_text ?? '',
    audioDirection: brief.audio_direction ?? '',
    durationSeconds: dur,
  };
}

function defaultEmptyDraftItem(
  platforms: ReadonlyArray<ContentVariantPlatform>,
  month: string,
): DraftItemState {
  return {
    key: genKey(),
    title: '',
    body: '',
    scheduledDate: `${month}-01`,
    scheduledTimeLocal: '10:00',
    variants: platforms.map((p) => ({
      platform: p,
      headline: '',
      caption: '',
      body: '',
      hashtags: [],
      brief: defaultEmptyBrief(),
    })),
  };
}

function describeApiError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') return 'تم الإلغاء';
  if (isApiError(err)) {
    return err.message || err.code || 'تعذر تنفيذ الطلب';
  }
  if (err instanceof Error) return err.message;
  return 'تعذر تنفيذ الطلب';
}

// ─────────────────────────────────────────────────────────────────────
// Task 288A — field-aware Arabic copy for calendar-draft SAVE failures.
//
// The save EFs answer `validation_error` (HTTP 400) with a per-field
// `error.fields = { '<path>': ['<code>'] }` map and NO message on the
// wire, so rendering `err.message` produced the opaque
// `KMI API validation_error (HTTP 400)` banner. `describeSaveError`
// translates the actual failing field + machine code into Arabic and
// is wired into the two draft-save catch sites only (AI/unified draft
// save + manual single-post save). Every non-validation error family
// keeps the EXACT pre-288A behavior by delegating to
// `describeApiError`. Transitions keep their own Task 171 mapper.
// ─────────────────────────────────────────────────────────────────────

// Arabic labels for the leaf field names the save EF's `fields` paths
// can end with (paths look like `items[0].variants[1].caption`).
const SAVE_VALIDATION_FIELD_LABEL_AR: Readonly<Record<string, string>> = {
  title: 'العنوان',
  body: 'النص',
  scheduled_for: 'موعد الجدولة',
  timezone: 'المنطقة الزمنية',
  platform: 'المنصة',
  headline: 'العنوان الرئيسي',
  caption: 'التعليق',
  variants: 'المنصات',
  items: 'عناصر المسودة',
  hashtags: 'الوسوم',
  client_id: 'العميل',
  origin: 'نوع الحفظ',
  ai_run_id: 'مرجع التوليد',
  mode: 'وضع الحفظ',
  platform_specific: 'تفاصيل المنصة',
  duration_seconds: 'مدة الفيديو',
};

// Arabic copy for the machine codes the save EF emits in
// `error.fields`. Unknown codes fall back to `<label>: <code>` so the
// user still sees which field failed instead of a bare envelope name.
const SAVE_VALIDATION_CODE_AR: Readonly<Record<string, string>> = {
  must_be_iso8601: 'صيغة التاريخ غير صحيحة (يلزم ISO 8601).',
  must_be_iana_zone: 'المنطقة الزمنية غير صحيحة (يلزم اسم IANA مثل Asia/Riyadh).',
  must_be_non_empty: 'حقل مطلوب لا يمكن أن يكون فارغاً.',
  must_be_string: 'القيمة يجب أن تكون نصاً.',
  must_be_string_or_null: 'القيمة يجب أن تكون نصاً أو تُترك فارغة.',
  must_be_array: 'القيمة يجب أن تكون قائمة.',
  must_be_object: 'القيمة يجب أن تكون كائناً.',
  must_be_number: 'القيمة يجب أن تكون رقماً.',
  must_be_integer: 'القيمة يجب أن تكون عدداً صحيحاً.',
  must_be_uuid: 'المعرّف غير صحيح.',
  not_in_allow_list: 'حقل غير مسموح به في طلب الحفظ.',
  unknown_platform: 'المنصة غير مدعومة.',
  duplicate_within_item: 'المنصة مكررة داخل العنصر نفسه.',
  must_have_at_least_one_text_field: 'أضف عنواناً رئيسياً أو تعليقاً أو نصاً واحداً على الأقل.',
  expected_object: 'شكل البيانات غير صحيح.',
  must_be_manual_or_ai_assisted: 'نوع الحفظ غير صحيح.',
  must_be_absent_for_manual: 'هذا الحقل لا يُرسل مع الحفظ اليدوي.',
};

// The save RPC (`save_content_calendar_draft_v1`) still carries its own
// past-date RAISE (`... scheduled_for must not be in the past`, routed
// to `fields._rpc[0]` by the EF's SQLSTATE-22023 mapping). Until Task
// 288B removes that mirror via migration, detect the message family and
// render actionable Arabic instead of leaking the raw SQL text.
const SAVE_RPC_PAST_TIMESTAMP_PATTERN = /scheduled_for must not be in the past/;
const SAVE_PAST_TIMESTAMP_MESSAGE_AR =
  'موعد الجدولة أصبح في الماضي ولا يقبله الخادم حالياً. عدّل التاريخ إلى موعد مستقبلي ثم أعد الحفظ.';
const GENERIC_SAVE_VALIDATION_MESSAGE_AR = 'تعذر التحقق من بيانات الحفظ. راجع القيم وأعد المحاولة.';
// Cap the banner at a handful of field lines — one bad bulk save can
// carry dozens of entries and the first few are what the user fixes.
const SAVE_VALIDATION_MAX_LINES = 3;

/** `items[0].variants[1].caption` → `العنصر رقم 1 — المنصة رقم 2 — التعليق`. */
function humanizeSaveFieldPath(path: string): string {
  const parts: string[] = [];
  const itemMatch = /items\[(\d+)\]/.exec(path);
  if (itemMatch) parts.push(`العنصر رقم ${Number(itemMatch[1]) + 1}`);
  const variantMatch = /variants\[(\d+)\]/.exec(path);
  if (variantMatch) parts.push(`المنصة رقم ${Number(variantMatch[1]) + 1}`);
  const leafSegment = path.split('.').pop() ?? path;
  const leaf = leafSegment.replace(/\[\d+\]$/, '');
  const label = SAVE_VALIDATION_FIELD_LABEL_AR[leaf];
  if (label && leaf !== 'items') parts.push(label);
  return parts.length > 0 ? parts.join(' — ') : path;
}

function describeSaveValidationLine(path: string, code: string): string {
  const label = humanizeSaveFieldPath(path);
  let message = SAVE_VALIDATION_CODE_AR[code];
  if (!message && code.startsWith('max_')) {
    message = `النص أطول من الحد المسموح (${code.slice(4)} حرفاً).`;
  }
  if (!message && /^must_be_\d+_to_\d+$/.test(code)) {
    message = 'العدد خارج النطاق المسموح.';
  }
  return message ? `${label}: ${message}` : `${label}: ${code}`;
}

function describeSaveError(err: unknown): string {
  if (isApiError(err) && err.code === 'validation_error') {
    const fields = err.body.fields;
    if (fields) {
      const rpc = fields['_rpc'];
      if (rpc && rpc.length > 0 && typeof rpc[0] === 'string') {
        // RPC RAISE text is English SQL — never surfaced raw.
        return SAVE_RPC_PAST_TIMESTAMP_PATTERN.test(rpc[0])
          ? SAVE_PAST_TIMESTAMP_MESSAGE_AR
          : GENERIC_SAVE_VALIDATION_MESSAGE_AR;
      }
      const lines: string[] = [];
      for (const [path, codes] of Object.entries(fields)) {
        if (path.startsWith('_')) continue; // _body/_method carry English text
        if (!Array.isArray(codes)) continue;
        for (const code of codes) {
          if (typeof code !== 'string') continue;
          lines.push(describeSaveValidationLine(path, code));
          if (lines.length >= SAVE_VALIDATION_MAX_LINES) break;
        }
        if (lines.length >= SAVE_VALIDATION_MAX_LINES) break;
      }
      if (lines.length > 0) return lines.join(' · ');
    }
    return GENERIC_SAVE_VALIDATION_MESSAGE_AR;
  }
  // Non-validation families keep the exact pre-288A behavior.
  return describeApiError(err);
}

// Task 112B — friendly Arabic copy for the "phantom 404" stale-list /
// RLS-visibility-flipped scenario flagged in
// `20260508120000_manual_content_workflow_v1.sql §40-58`. The v2 RPC
// collapses both "row missing" and "row exists in another tenant" to
// `23503 → wire 'not_found'` to avoid a cross-tenant existence
// oracle, so the operator-facing 404 needs a friendly translation
// (no leak of the raw `KMI API not_found (HTTP 404)` envelope text).
// Used by every content-approval mutation that goes through
// `transitionContentItemStatus`.
const STALE_LIST_NOT_FOUND_MESSAGE_AR =
  'العنصر غير متاح أو تم تحديث الصلاحيات. يرجى تحديث القائمة وإعادة المحاولة.';

// Task 171 — Arabic copy for the new workflow error families that the
// auto-chain can produce. The backend fix in
// `20260613120000_content_workflow_auto_chain_actor_fix.sql` is the
// real cure for the agency-actor case; the FE copy here is the
// last-mile guarantee that the user never sees raw envelope strings
// like `KMI API validation_error (HTTP 400)` or trigger names like
// `validate_publish_target_scope`.
const NO_AGENCY_OPERATOR_MESSAGE_AR =
  'تعذر إكمال الأتمتة لأن العميل لا يملك مسؤول وكالة صالح لتنفيذ إجراءات النشر أو الإنتاج.';
const GENERIC_VALIDATION_MESSAGE_AR =
  'تعذر تنفيذ الانتقال بسبب تحقق داخلي. جرّب تحديث الصفحة، وإذا استمرت المشكلة أرسلها للدعم.';
const PERMISSION_DENIED_MESSAGE_AR = 'لا تملك صلاحية تنفيذ هذا الانتقال.';
const CONFLICT_MESSAGE_AR = 'تعارض في الحالة الحالية. أعد تحميل الصفحة وحاول مرة أخرى.';
const RATE_LIMITED_MESSAGE_AR = 'تم تجاوز الحد المسموح به مؤقتاً. حاول بعد قليل.';
const GENERIC_TRANSITION_FAILURE_MESSAGE_AR =
  'تعذر تنفيذ الطلب. حاول مجدداً، وإذا استمرت المشكلة أرسلها للدعم.';

// Task 171 / Codex Step A round-1 P2 — narrow allowlist of the SPECIFIC
// backend RAISE messages that the auto-chain can produce on the
// `client_content_approved` transition. An earlier draft used a broad
// substring match (the literal text "agency role") — the schema has
// many unrelated "must hold an active agency role" RAISES (contracts,
// strategy, supervised access, tasks routing, etc.); a future
// transition path that touched one of those would mis-route the user
// to the no-agency-operator copy. The tightened pattern matches ONLY:
//   1. The Task 171 resolver RAISE
//      (`content workflow automation requires an active agency operator`).
//   2. The validate_publish_target_scope RAISE on
//      `publish_targets.created_by_user_id`.
//   3. The validate_publish_job_scope RAISE on
//      `publish_jobs.requested_by_user_id`.
//   4. The validate_task_scope RAISE on `tasks.created_by_user_id`.
//
// These are the only four paths that the post-Task-171 transition RPC
// can surface as `validation_error` with the auto-chain actor as the
// root cause. Any other `must hold an active agency role` RAISE
// surfacing here would be a NEW bug — the generic validation copy is
// the correct fallback.
const WORKFLOW_AGENCY_ACTOR_FAILURE_PATTERN =
  /content workflow automation requires an active agency operator|publish_targets\.created_by_user_id must hold an active agency role|publish_jobs\.requested_by_user_id must hold an active agency role|tasks\.created_by_user_id must hold an active agency role/;

function isNotFoundApiError(err: unknown): boolean {
  return isApiError(err) && err.code === 'not_found';
}

/**
 * Task 171 — workflow-aware error mapper for
 * `transitionContentItemStatus`.
 *
 * The frontend MUST NOT surface `err.message` for a typed `KmiApiError`
 * — that field is hard-coded to `KMI API ${code} (HTTP ${status})` by
 * `apps/web/src/api/client.ts`, which leaks both the technical envelope
 * name and the HTTP status into the user-visible alert. Instead, this
 * function inspects `err.code` and the structured `err.body` and
 * returns Arabic copy keyed to the failure family.
 *
 * Special case: when the wire is `validation_error` with
 * `fields._rpc[0]` carrying either the new Task 171 resolver RAISE
 * (`active agency operator`) or the legacy Task 164 trigger RAISES
 * (`agency role`), the mapper returns the dedicated "no agency
 * operator" Arabic copy. The legacy match is defensive — once the
 * Task 171 backend migration is applied, the resolver RAISE is the
 * only path that can land here, but stale environments could still
 * produce the legacy strings.
 *
 * Pair with `logTransitionFailure(err, context)` for the developer-
 * visible diagnostic (code, status, fields, detail, target_status,
 * content_item_id).
 */
function describeTransitionError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') return 'تم الإلغاء';
  if (!isApiError(err)) {
    return GENERIC_TRANSITION_FAILURE_MESSAGE_AR;
  }
  switch (err.code) {
    case 'not_found':
      return STALE_LIST_NOT_FOUND_MESSAGE_AR;
    case 'permission_denied':
      return PERMISSION_DENIED_MESSAGE_AR;
    case 'conflict':
      return CONFLICT_MESSAGE_AR;
    case 'rate_limited':
      return RATE_LIMITED_MESSAGE_AR;
    case 'validation_error': {
      const rpcDetail = extractTransitionRpcDetail(err);
      if (rpcDetail && WORKFLOW_AGENCY_ACTOR_FAILURE_PATTERN.test(rpcDetail)) {
        return NO_AGENCY_OPERATOR_MESSAGE_AR;
      }
      return GENERIC_VALIDATION_MESSAGE_AR;
    }
    case 'unauthenticated':
    case 'entitlement_denied':
    case 'internal_error':
    default:
      return GENERIC_TRANSITION_FAILURE_MESSAGE_AR;
  }
}

/**
 * Pull the raw backend RAISE text out of the `validation_error`
 * envelope's `fields._rpc[0]` slot. The EF at
 * `supabase/functions/transition-content-item-status/index.ts` stuffs
 * the PostgreSQL `RAISE` message into that field for SQLSTATE `22023`
 * and `23514` paths (`new ValidationError(message, { _rpc: [message] })`).
 * Returns null when the body shape doesn't carry it, so the caller can
 * fall back to the generic Arabic copy without leaking anything raw.
 */
function extractTransitionRpcDetail(err: KmiApiError): string | null {
  const fields = err.body.fields;
  if (!fields) return null;
  const rpc = fields['_rpc'];
  if (!rpc || rpc.length === 0) return null;
  const first = rpc[0];
  return typeof first === 'string' && first.length > 0 ? first : null;
}

interface TransitionFailureContext {
  readonly contentItemId?: string;
  readonly targetStatus?: string;
}

/**
 * Structured developer diagnostic for transition failures. Emits one
 * `console.error` line with the wire envelope details + the call-site
 * context so a developer hitting the page in dev tools can trace the
 * failure without needing to inspect the network panel. Intentionally
 * NEVER renders to the UI — the user-visible copy comes from
 * `describeTransitionError` only.
 */
function logTransitionFailure(err: unknown, context: TransitionFailureContext): void {
  if (err instanceof DOMException && err.name === 'AbortError') return;
  if (isApiError(err)) {
    console.error('[content-workflow-transition]', {
      code: err.code,
      status: err.status,
      fields: err.body.fields ?? null,
      detail: err.body.detail ?? null,
      feature_key: err.body.feature_key ?? null,
      target_status: context.targetStatus ?? null,
      content_item_id: context.contentItemId ?? null,
    });
    return;
  }
  console.error('[content-workflow-transition]', {
    code: 'unknown',
    error: err instanceof Error ? { name: err.name, message: err.message } : String(err),
    target_status: context.targetStatus ?? null,
    content_item_id: context.contentItemId ?? null,
  });
}

export function ContentPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const workspace = useOutletContext<ClientWorkspaceOutletContext>();
  const [statusFilter, setStatusFilter] = useState<ContentItemStatus | 'all'>('all');
  const [disciplineFilter, setDisciplineFilter] = useState<ContentDiscipline | 'all'>('all');
  // Task 290B — client viewers filter by PUBLIC group, purely
  // client-side over the already-RLS-scoped list. `statusFilter` stays
  // 'all' for clients (they never see the agency chips), so the fetch
  // and its params are byte-identical for every viewer.
  const [clientStatusGroup, setClientStatusGroup] = useState<ClientStatusGroupValue>('all');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Task 290A — `?contentItem=<id>` deep link from /app/me/tasks
  // («افتح المهمة»). Consumed exactly ONCE after the authorized list
  // loads: the item is selected only if it appears in that RLS-scoped
  // list (fail-closed — no direct fetch by id, no existence oracle);
  // otherwise the page silently falls back to the default selection.
  // The param is removed from the URL either way so filters, tab
  // switches and browser navigation behave normally afterwards.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkItemIdRef = useRef<string | null>(searchParams.get('contentItem'));
  // Once the deep-linked item is selected, remember it so the
  // production-task panel can be scrolled into view when (and only
  // when) ITS task loads; cleared if the user selects another row.
  const [pendingScrollItemId, setPendingScrollItemId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState<CalendarView>({ kind: 'list' });
  const [aiForm, setAiForm] = useState<AiCalendarFormState>(() => ({
    mode: 'unified',
    month: nextMonthYYYYMM(),
    entryCount: 8,
    assetFormats: DEFAULT_ASSET_FORMATS,
    parentPlatforms: DEFAULT_PARENT_PLATFORMS,
    parentPlatformsManuallyEdited: false,
    platforms: DEFAULT_PLATFORMS,
    instruction: '',
    timezone: DEFAULT_TIMEZONE,
  }));
  const [aiError, setAiError] = useState<string | null>(null);
  // Task 280B — prefill the AI generator from package + operating-setup defaults
  // (advisory; manual overrides win). Keyed to clientId so one client's defaults
  // never leak into another's generator when this routed page is reused.
  const [operatingSetupState, setOperatingSetupState] = useState<{
    readonly clientId: string;
    readonly setup: ClientOperatingSetup | null;
  } | null>(null);
  const aiSeededClientRef = useRef<string | null>(null);
  const aiInitialMonthRef = useRef(aiForm.month);
  // Task 1D — why entryCount was derived vs fell back (UI explainability only).
  // null until the prefill has run for the current client.
  const [aiEntryCountReason, setAiEntryCountReason] = useState<EntryCountReason | null>(null);

  // Reset the generator form + prefill state whenever the client changes, so the
  // new client seeds from scratch and never inherits the previous client's values.
  useEffect(() => {
    const freshMonth = nextMonthYYYYMM();
    aiInitialMonthRef.current = freshMonth;
    setOperatingSetupState(null);
    setAiEntryCountReason(null);
    setAiForm({
      mode: 'unified',
      month: freshMonth,
      entryCount: 8,
      assetFormats: DEFAULT_ASSET_FORMATS,
      parentPlatforms: DEFAULT_PARENT_PLATFORMS,
      parentPlatformsManuallyEdited: false,
      platforms: DEFAULT_PLATFORMS,
      instruction: '',
      timezone: DEFAULT_TIMEZONE,
    });
  }, [clientId]);

  // Read-only fetch of the operating setup for the current client (non-fatal).
  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    getClientOperatingSetup(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setOperatingSetupState({ clientId, setup: data.operating_setup });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Non-fatal: still seed from package-only / fall back to form defaults.
        setOperatingSetupState({ clientId, setup: null });
      });
    return () => ctrl.abort();
  }, [clientId]);

  // One-time seeding per client, once the operating setup for THIS client is
  // loaded and its active package assignment is known. All gates require the
  // current clientId so a stale fetch/context never seeds the wrong client.
  useEffect(() => {
    if (!clientId) return;
    if (aiSeededClientRef.current === clientId) return;
    if (workspace.clientId !== clientId) return;
    if (workspace.capabilityStatus === 'loading') return;
    if (!operatingSetupState || operatingSetupState.clientId !== clientId) return;
    aiSeededClientRef.current = clientId;
    const prefill = deriveContentCalendarPrefill(
      workspace.activeAssignment,
      operatingSetupState.setup,
      { defaultMonth: aiInitialMonthRef.current, allowedPlatforms: AI_V1_PLATFORM_OPTIONS },
    );
    // Only surface a fallback reason when the assignment lookup actually
    // succeeded. The workspace layout degrades RLS-denied / not-found to a
    // 'ready' status with a null assignment, so `capabilityStatus === 'ready'`
    // is NOT enough — it would let a blocked lookup show a misleading
    // 'no_assignment' hint ("no active package") when the package may well
    // exist. `assignmentLookupConfirmed` is true only when the RPC returned
    // data; on a blocked lookup or 'error' it is false → leave the reason null
    // (reset default) → no hint. entryCount seeding itself is unchanged.
    if (workspace.assignmentLookupConfirmed) {
      setAiEntryCountReason(prefill.entryCountReason);
    }
    setAiForm((prev) => {
      const merged = mergeContentCalendarPrefill(prev, prefill, {
        entryCount: 8,
        platforms: DEFAULT_PLATFORMS,
        timezone: DEFAULT_TIMEZONE,
        month: aiInitialMonthRef.current,
        instruction: '',
      });
      // Task 283 — platform-FIRST seeding. Translate the RAW operating-setup
      // targets (`prefill.platforms`, placement-level) to parent platforms — NOT
      // `merged.platforms` — and seed them only while the user has NOT directly
      // toggled a platform (the flag, not array identity, is the override sentinel,
      // so a format change before the prefill arrives does not suppress seeding).
      // Formats are not in the operating setup: they are only normalized to stay
      // compatible with the (seeded) platforms — never seeded — so the resolved
      // union is never silently empty. `platforms` is re-derived as the union of
      // resolveRequestedOutputs. contentCalendarDefaults stays unchanged.
      const seededParents = prefill.platforms ? aiParentsFromTargets(prefill.platforms) : [];
      const parentPlatforms =
        !prev.parentPlatformsManuallyEdited && seededParents.length > 0
          ? seededParents
          : prev.parentPlatforms;
      const assetFormats = normalizeAssetFormats(prev.assetFormats, parentPlatforms);
      return {
        ...merged,
        parentPlatforms,
        assetFormats,
        platforms: aiResolvedTargets(assetFormats, parentPlatforms),
      };
    });
  }, [clientId, operatingSetupState, workspace]);
  // Task 068 — calendar surface state.
  const [screen, setScreen] = useState<'list' | 'calendar' | 'inbox'>('list');
  const [calendarMonth, setCalendarMonth] = useState<string>(() => nextMonthYYYYMM());
  const [calendarTimezone, setCalendarTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [calendar, setCalendar] = useState<CalendarLoadState>({ status: 'idle' });
  const [calendarReloadKey, setCalendarReloadKey] = useState(0);
  // Task 068 — edit modal + transition feedback.
  const [editModal, setEditModal] = useState<EditModalState | null>(null);
  const [transitionInFlight, setTransitionInFlight] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  // Task 101C — short-lived success note for the production handoff.
  // Page-level (NOT scoped to the active ContentDetail item) so the
  // message survives the common workflow where the transitioned row
  // leaves the current filter and the auto-selection retargets to a
  // sibling. Codex round 2 P2 fix — round 1 introduced an item-
  // scoped `{content_item_id, message}` shape with a JSX gate; that
  // gate then dropped the message in the very workflow it was meant
  // to protect. The right primitive is a page-level banner.
  // Cleared on (a) the next transition, (b) any filter change,
  // (c) any screen/tab change (Codex round 3 P3 fix — without (c)
  // the JSX gate `screen === 'list'` only HIDES the banner when
  // the user navigates to calendar/inbox; the underlying state
  // persists and the stale message resurfaces when they return to
  // the list later).
  const [transitionSuccess, setTransitionSuccess] = useState<string | null>(null);
  // Task 101D — production-task panel state. Loaded when a detail
  // item is in_production; cleared on item change. The panel
  // routes start / submit / upload through dedicated edge functions
  // (NEVER direct PostgREST writes against tasks).
  const [productionTask, setProductionTask] = useState<ProductionTaskRow | null>(null);
  const [productionTaskInFlight, setProductionTaskInFlight] = useState(false);
  const [productionTaskError, setProductionTaskError] = useState<string | null>(null);
  const [productionTaskSuccess, setProductionTaskSuccess] = useState<string | null>(null);
  const [productionTaskReloadKey, setProductionTaskReloadKey] = useState(0);
  // Task 175 PR-A · Issue 1 — client-side deliverable fallback. The
  // detail-view production-task loader hits
  // `list-production-tasks-for-content-item` under user JWT, which is
  // gated by `tasks_select` RLS requiring `task.read` — a permission
  // NOT seeded for any client-category role. For client-category
  // viewers that EF returns `{ tasks: [] }`, leaving the Instagram /
  // TikTok / Snapchat platform card with no `latest_production_deliverable`
  // to render in `MediaPreviewSlot`. The inbox EF
  // (`list-pending-client-approvals`, stage=asset) ALREADY drops to
  // service-role for the deliverable enrichment after a user-scoped
  // content_items visibility pre-flight — we reuse it here as a
  // targeted fallback so the client can see the produced creative
  // before approving. No EF / RLS / seed change.
  const [clientFallbackDeliverable, setClientFallbackDeliverable] =
    useState<PendingClientApprovalDeliverable | null>(null);
  // Task 101D Codex P2 — preserve the prepared storage_key across
  // submit retries. The submit RPC is idempotent on (task_id,
  // storage_key); if PUT succeeds but submit fails (timeout, transient
  // 5xx, etc.) the FE must reuse the SAME storage_key on the next
  // attempt, otherwise the bucket is left with an orphan object and
  // the idempotency key in the next submit call cannot match. Cleared
  // on submit success or when the displayed item changes.
  const [productionPendingSubmit, setProductionPendingSubmit] = useState<{
    readonly taskId: string;
    readonly storageKey: string;
    readonly fileName: string;
    readonly fileSize: number;
    // Codex P2 (round 10): include `lastModified` so the retry-reuse
    // key can't collide on a coincidentally-same name+size for a
    // different file the user picked between attempts. Without this,
    // a swapped file would silently reuse the OLD storage_key and
    // submit the previous bytes while the UI claims the new file.
    readonly fileLastModified: number;
  } | null>(null);
  // Task 069 — client approval inbox state.
  // Task 104B — `inboxStage` selects which queue the inbox shows:
  //   'content' (default; pre-104B behavior) — pre-production approval.
  //   'asset' — final produced-asset approval (Task 104B step 5).
  // The shape of the loaded items is identical for both stages; the
  // stage drives the fetch filter + the approve handler's transition
  // target. The request-changes target is `draft` for both.
  const [inbox, setInbox] = useState<InboxLoadState>({ status: 'idle' });
  const [inboxReloadKey, setInboxReloadKey] = useState(0);
  const [inboxStage, setInboxStage] = useState<PendingClientApprovalStage>('content');

  // Task 104C — publishing workflow V1 state. The publish-jobs panel
  // hangs off the page-level state so the per-job mark/cancel
  // mutations can re-trigger a single fetch + re-render. State shape:
  //   * `publishJobsState` — async load state for the per-item job list.
  //   * `publishJobsReloadKey` — bump to refresh after a mutation.
  //   * `publishInFlight` — disables every per-job button while a
  //     mutation is in-flight (prevents double-clicks).
  //   * `publishError` — surfaced inline beneath the panel buttons.
  //   * `scheduleModal` — when non-null, renders the schedule form
  //     (platforms / scheduled_for / per-platform caption / notes).
  const [publishJobsState, setPublishJobsState] = useState<PublishJobsLoadState>({
    status: 'idle',
  });
  const [publishJobsReloadKey, setPublishJobsReloadKey] = useState(0);
  const [publishInFlight, setPublishInFlight] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [scheduleModal, setScheduleModal] = useState<ScheduleModalState | null>(null);
  const [requestChangesModal, setRequestChangesModal] = useState<RequestChangesModalState | null>(
    null,
  );

  const canSaveDraft = useMemo(
    () =>
      !!me &&
      !me.system_admin &&
      hasAnyPermission(me.permissions, PERM.CONTENT_CREATE) &&
      hasAnyPermission(me.permissions, PERM.CONTENT_VARIANT_MANAGE) &&
      hasAnyPermission(me.permissions, PERM.CONTENT_SCHEDULE),
    [me],
  );
  const canAiGenerate = useMemo(
    () => canSaveDraft && !!me && hasAnyPermission(me.permissions, PERM.AI_RUN),
    [canSaveDraft, me],
  );
  // Task 068 — review-workflow capability flags. Each flag mirrors
  // the matching SECURITY DEFINER predicate in the transition RPC so
  // the FE buttons match the server gate exactly.
  const isLeadership = useMemo(() => {
    if (!me) return false;
    for (const r of me.roles) {
      if (r.category === 'agency' && (r.key === 'owner' || r.key === 'marketing_manager')) {
        return true;
      }
    }
    return false;
  }, [me]);
  const canInternalReviewBase = useMemo(
    () =>
      !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.PRODUCTION_REVIEW_ANY_OF),
    [me],
  );
  const canApproveContentDiscipline = useMemo(
    () => !!me && hasAnyPermission(me.permissions, PERM.CONTENT_WRITING_APPROVE),
    [me],
  );
  const canApproveDesignDiscipline = useMemo(
    () => !!me && hasAnyPermission(me.permissions, PERM.CONTENT_DESIGN_APPROVE),
    [me],
  );
  // Codex round 1 P2 fix — the `pending_client_content_approval →
  // client_content_approved` transition admits ONLY client-category
  // users in V1. The existing `validate_content_approval_scope`
  // trigger refuses agency approvers on client stages; the
  // transition RPC mirrors that strict gate. Owner / marketing_
  // manager hold `approval.client.decide` in the seed but cannot
  // approve through this V1 path (they'd use `approval_overrides`,
  // out of Task 068 scope). The FE button must mirror the same
  // gate so leadership doesn't see a button that will always 4xx.
  const hasClientCategoryRole = useMemo(() => {
    if (!me) return false;
    for (const r of me.roles) {
      if (r.category === 'client') return true;
    }
    return false;
  }, [me]);
  // Task 277 — AI provenance (origin) is internal metadata. Show the origin
  // label only to agency-category viewers; client-category users never see it.
  // Mirrors the server-side agency guard (app.current_user_has_agency_role()).
  const canSeeContentOrigin = useMemo(() => !!me && hasRoleCategory(me, 'agency'), [me]);
  // Task 286G — a restricted CLIENT-category viewer: has a client role AND is NOT
  // agency. Drives client-safe rendering across EVERY client-reachable content
  // surface (list, calendar, filters, detail). A dual-role agency+client user is
  // treated as agency (canSeeContentOrigin true ⇒ false here), never restricted.
  const viewerIsClient = hasClientCategoryRole && !canSeeContentOrigin;
  const canDecideClientApproval = useMemo(
    () =>
      !!me &&
      !me.system_admin &&
      hasClientCategoryRole &&
      hasAnyPermission(me.permissions, PERM.APPROVAL_CLIENT_DECIDE),
    [me, hasClientCategoryRole],
  );
  // Task 101C — production-handoff CTA gate. Mirrors the
  // `transition_content_item_status_v2` `client_content_approved →
  // in_production` branch in
  // `supabase/migrations/20260505000000_content_workflow_production_handoff.sql`:
  //   agency role + (leadership OR active client_assignment for the
  //   parent client) + `production.queue.manage` permission.
  // Account managers (no `production.queue.manage` in seed) and
  // client-category users (no agency role) are intentionally
  // excluded — same shape as the server gate, so the button only
  // shows when a real call would succeed.
  const canStartProduction = useMemo(() => {
    if (!me) return false;
    if (me.system_admin) return false;
    if (!hasRoleCategory(me, 'agency')) return false;
    if (!hasAnyPermission(me.permissions, PERM.PRODUCTION_QUEUE_MANAGE)) return false;
    if (isLeadership) return true;
    if (!clientId) return false;
    return hasClientAssignment(me, clientId);
  }, [me, clientId, isLeadership]);

  // Task 117 / TEAM-2 PR-2 — production-routing caller gate.
  // Mirrors the SECURITY DEFINER RPC `assign_production_task_v1`'s
  // locked caller set EXACTLY (Task 111 §5.0 D4):
  //   - owner unconditional
  //   - OR marketing_manager unconditional
  //   - OR (production_manager AND active client_assignment to the
  //     task's parent client)
  // No `task.assign`-based path. No AM / DM / senior_* / executor
  // admission. The flag controls whether the "تعيين / إعادة تعيين"
  // affordance renders inside `ProductionTaskPanel`; the RPC re-checks
  // the same gate server-side.
  const canManageProductionRouting = useMemo(() => {
    if (!me) return false;
    if (me.system_admin) return false;
    if (!hasRoleCategory(me, 'agency')) return false;
    if (isLeadership) return true;
    // production_manager — needs active CAT to the parent client.
    let isProductionManager = false;
    for (const r of me.roles) {
      if (r.category === 'agency' && r.key === 'production_manager') {
        isProductionManager = true;
        break;
      }
    }
    if (!isProductionManager) return false;
    if (!clientId) return false;
    return hasClientAssignment(me, clientId);
  }, [me, clientId, isLeadership]);

  // Task 105A (+ Task 160 AM tier) — trusted manual content handoff.
  // Mirrors the RPC gate on the `draft → pending_client_content_approval`
  // edge exactly, with a per-role-tier admission split:
  //   * Leadership tier (owner / marketing_manager): require
  //     content.create, the historical content-creation anchor.
  //     Leadership bypasses the assignment check.
  //   * account_manager tier (Task 160): role + active
  //     client_assignment, NO perm check (AM doesn't hold
  //     content.create by seed — mirrors the contracts_insert AM
  //     pattern). Decision-maker layer per task-160 spec.
  //   * senior_content_writer tier: require content.writing.approve
  //     AND an active client_assignment for the parent client. The
  //     seed catalogue does NOT grant content.create to this role,
  //     so the prior uniform content.create check made this branch
  //     unreachable; the round-2 uniform content.writing.approve
  //     check went the other direction and quietly lowered the
  //     leadership bar — this split keeps each tier on its own
  //     historical permission anchor.
  //
  // The action builder ALSO checks `item.origin === 'manual' ||
  // item.origin === 'imported'` per call so AI-generated/AI-assisted
  // items don't show the direct button (the RPC raises 23514 for
  // non-allowed origin).
  const canDirectManualClientHandoff = useMemo(() => {
    if (!me) return false;
    if (me.system_admin) return false;
    if (!hasRoleCategory(me, 'agency')) return false;
    if (isLeadership) {
      return hasAnyPermission(me.permissions, PERM.CONTENT_CREATE);
    }
    if (!clientId) return false;
    // Task 160 — account_manager tier (role + active assignment, no perm
    // check; AM doesn't hold content.create by seed). The RPC's new
    // matching branch on the `draft → pending_client_content_approval`
    // edge mirrors this. The v_item_origin in ('manual','imported')
    // AI-safety gate is enforced server-side AND by the action builder's
    // per-item origin check below.
    for (const r of me.roles) {
      if (r.category === 'agency' && r.key === 'account_manager') {
        return hasClientAssignment(me, clientId);
      }
    }
    // senior_content_writer tier — needs CONTENT_WRITING_APPROVE AND an
    // active client_assignment (matches RPC's
    // `v_actor_is_senior_content_writer + v_actor_assigned` branch).
    let isSeniorContentWriter = false;
    for (const r of me.roles) {
      if (r.category === 'agency' && r.key === 'senior_content_writer') {
        isSeniorContentWriter = true;
        break;
      }
    }
    if (!isSeniorContentWriter) return false;
    return (
      hasAnyPermission(me.permissions, PERM.CONTENT_WRITING_APPROVE) &&
      hasClientAssignment(me, clientId)
    );
  }, [me, clientId, isLeadership]);

  // Task 292C1 — «إرسال للكتابة» gate. Mirrors the SECURITY DEFINER predicate
  // `app.require_writing_handoff_authz` exactly (house rule above: the FE flag
  // matches the server gate, it does not invent one). owner / marketing_manager
  // reach any client in the org; account_manager / senior_content_writer are
  // limited to clients they are actively assigned to. Both tiers require
  // `task.assign` — the handoff IS a task assignment, and SECURITY.md requires
  // an explicit permission check on every action.
  //
  // NOTE the FE cannot reproduce the server's CLIENT-SCOPED resolution for the
  // scoped tier: `me.permissions` is the org-wide effective set, so a Task 122A
  // client-scoped DENY is invisible here. That is deliberate and safe in this
  // direction only — the button may appear for a caller the RPC then refuses
  // with 42501, which the panel renders as the Arabic denied state. It can
  // never do the reverse and grant anything.
  const canSendToWriting = useMemo(() => {
    if (!me) return false;
    if (me.system_admin) return false;
    if (!hasRoleCategory(me, 'agency')) return false;
    if (!hasAnyPermission(me.permissions, PERM.TASK_ASSIGN)) return false;
    if (isLeadership) return true;
    if (!clientId) return false;
    for (const r of me.roles) {
      if (
        r.category === 'agency' &&
        (r.key === 'account_manager' || r.key === 'senior_content_writer')
      ) {
        return hasClientAssignment(me, clientId);
      }
    }
    return false;
  }, [me, clientId, isLeadership]);

  // Task 160 — one-click "approve internal + send to client" gate. Mirrors
  // the new pending_internal_review → pending_client_{content,asset}_approval
  // RPC branches exactly: agency role + (leadership OR account_manager with
  // an active client_assignment). Senior tiers stay on the old two-step
  // path (pending_internal_review → internal_approved → client_*) and are
  // intentionally NOT admitted here — per task spec, AM is the new
  // first-class decision-maker tier alongside owner / marketing_manager.
  const canApproveAndSendToClient = useMemo(() => {
    if (!me) return false;
    if (me.system_admin) return false;
    if (!hasRoleCategory(me, 'agency')) return false;
    if (isLeadership) return true;
    if (!clientId) return false;
    if (!hasClientAssignment(me, clientId)) return false;
    for (const r of me.roles) {
      if (r.category === 'agency' && r.key === 'account_manager') return true;
    }
    return false;
  }, [me, clientId, isLeadership]);

  // Task 104C — publish-workflow gate (FE mirror of the four V1 RPCs:
  // schedule_content_item_publish_v1, mark_publish_job_published_v1,
  // mark_publish_job_failed_v1, cancel_publish_job_v1). All four
  // require: agency role + (leadership OR active client_assignment) +
  // `publish.schedule`. Same shape as canStartProduction; client-
  // category callers never see the schedule / mark / cancel buttons.
  const canSchedulePublishing = useMemo(() => {
    if (!me) return false;
    if (me.system_admin) return false;
    if (!hasRoleCategory(me, 'agency')) return false;
    if (!hasAnyPermission(me.permissions, PERM.PUBLISH_SCHEDULE)) return false;
    if (isLeadership) return true;
    if (!clientId) return false;
    return hasClientAssignment(me, clientId);
  }, [me, clientId, isLeadership]);

  // Task 101D — production-deliverable submit gate (FE-side). The
  // backend RPCs (start_production_task_v1, prepare_production_task_
  // upload_v1, submit_production_task_v1) all enforce the same chain
  // (agency role + active assignee + production.asset.submit + parent
  // content_item.status='in_production'). The FE flag below covers
  // the role + permission half so we can hide the panel buttons for
  // callers who would be 4xx'd. The "active assignee" check is done
  // by the panel itself comparing me.actor.user_id with
  // task.active_assignee_user_id.
  // Codex P2 (round 11): the panel gate must mirror the backend's
  // discipline-specific `app.require_task_discipline_perm` and the
  // `task.submit` check. Returns the SET of disciplines this caller
  // can deliver for; the panel's per-item gate later checks
  // `set.has(item.discipline)`. Without this, a content_writer
  // viewing a design task would see Start/Submit buttons that all
  // 4xx at the backend.
  const productionDeliveryAllowedDisciplines = useMemo(() => {
    const set = new Set<ContentDiscipline>();
    if (!me) return set;
    if (me.system_admin) return set;
    if (!hasRoleCategory(me, 'agency')) return set;
    if (!hasAnyPermission(me.permissions, PERM.PRODUCTION_ASSET_SUBMIT)) return set;
    if (!hasAnyPermission(me.permissions, PERM.TASK_SUBMIT)) return set;
    // Task 119 — mirror Task 117F Phase 1 backend gates. Both
    // `content` and `design` discipline now require `task.design.execute`
    // (writers were taken out of production end-to-end). Without this
    // mirror, the designer who is the active assignee on a content
    // task sees the "no permission" deny message instead of the Start
    // button — because `productionDeliveryAllowedDisciplines.has('content')`
    // returns false and `canStartProductionPerm` collapses to false.
    if (hasAnyPermission(me.permissions, PERM.TASK_DESIGN_EXECUTE)) {
      set.add('content');
      set.add('design');
    }
    if (hasAnyPermission(me.permissions, PERM.TASK_VIDEO_EXECUTE)) set.add('video');
    return set;
  }, [me]);
  const currentUserId = me?.actor.user_id ?? null;

  // Task 073 — creative asset upload + attach. Atomic upload-and-
  // attach requires BOTH `creative.intelligence.asset.manage` AND
  // `creative.intelligence.match.manage` (see migration RPC). Client-
  // category users never hold both keys; agency leadership +
  // marketing_manager do.
  const canManageCreativeAttachments = useMemo(() => {
    if (!me || me.system_admin) return false;
    let hasAgencyRole = false;
    for (const r of me.roles) {
      if (r.category === 'agency') {
        hasAgencyRole = true;
        break;
      }
    }
    if (!hasAgencyRole) return false;
    return (
      hasAnyPermission(me.permissions, PERM.CREATIVE_INTELLIGENCE_ASSET_MANAGE) &&
      hasAnyPermission(me.permissions, PERM.CREATIVE_INTELLIGENCE_MATCH_MANAGE)
    );
  }, [me]);

  // Asset map keyed by `${target_source}:${target_id}`. Single page-
  // level cache so list cards, calendar rows, detail variants, and
  // inbox rows can all read without separate fetches.
  const [creativeAssetsByTarget, setCreativeAssetsByTarget] = useState<
    Map<string, CreativeAssetCardRow>
  >(() => new Map());
  const [assetReloadKey, setAssetReloadKey] = useState(0);

  // Task 101C — Codex round 3 P3 fix.
  // Clear the page-level production-handoff success banner whenever
  // the user navigates between screens. The banner is bound to the
  // workflow moment that produced it; the JSX gate
  // `view.kind === 'list' && screen === 'list'` only HIDES it on
  // departure, but the underlying state survives. Without this
  // effect the stale "تم إنشاء مهمة الإنتاج (#xxx)" message would
  // reappear when the user returns to the list screen later after
  // unrelated work in calendar / inbox. Single-source-of-truth for
  // the screen-change clear; covers every present and future
  // setScreen call site without requiring per-handler duplication.
  useEffect(() => {
    setTransitionSuccess(null);
  }, [screen]);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listContentItems({
      clientId,
      status: statusFilter === 'all' ? undefined : statusFilter,
      discipline: disciplineFilter === 'all' ? undefined : disciplineFilter,
      limit: 50,
      signal: ctrl.signal,
    })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', items: data.content_items });
        // Task 290A — one-shot deep-link selection. Only an item that
        // the authorized (RLS-scoped) list actually returned can be
        // selected; unknown/inaccessible ids fall through to the
        // default behavior below. The param is cleared in both cases.
        const deepLinkId = deepLinkItemIdRef.current;
        if (deepLinkId) {
          deepLinkItemIdRef.current = null;
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('contentItem');
              return next;
            },
            { replace: true },
          );
          if (data.content_items.some((i) => i.id === deepLinkId)) {
            setSelectedId(deepLinkId);
            setScreen('list');
            setPendingScrollItemId(deepLinkId);
            return;
          }
        }
        if (data.content_items.length > 0 && data.content_items[0]) {
          setSelectedId((prev) =>
            prev && data.content_items.some((i) => i.id === prev)
              ? prev
              : data.content_items[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setList({ status: 'not-found' });
          return;
        }
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, statusFilter, disciplineFilter, reloadKey]);

  // Task 290B — the client status group filters at render time only (no
  // refetch), so the post-fetch selection reconciliation above never
  // sees it: switching groups (or an inbox decision moving the selected
  // item out of the current group after a reloadKey refetch) could
  // leave the detail pane on an item the visible list excludes. Mirror
  // the reconciliation against the group-filtered view. Agency viewers
  // filter via statusFilter → refetch → the effect above; this one is a
  // no-op for them.
  useEffect(() => {
    if (!viewerIsClient) return;
    if (list.status !== 'ready') return;
    const visible = list.items.filter((i) => itemMatchesClientGroup(i.status, clientStatusGroup));
    if (visible.length > 0 && visible[0]) {
      setSelectedId((prev) => (prev && visible.some((i) => i.id === prev) ? prev : visible[0]!.id));
    } else {
      setSelectedId(null);
    }
  }, [viewerIsClient, list, clientStatusGroup]);

  // Task 290A — after a valid task deep link, bring the production-task
  // panel into view once ITS task has loaded (the loader only fires for
  // post-handoff statuses; pre-handoff items simply keep the row
  // selection). One-shot: cleared on scroll or when the user selects a
  // different row. Pattern mirrors OnboardingWizardPage's
  // getElementById + scrollIntoView precedent.
  useEffect(() => {
    if (!pendingScrollItemId) return;
    if (selectedId !== pendingScrollItemId) {
      setPendingScrollItemId(null);
      return;
    }
    if (!productionTask || productionTask.source_id !== pendingScrollItemId) return;
    setPendingScrollItemId(null);
    if (typeof document !== 'undefined') {
      const el = document.getElementById(PRODUCTION_TASK_PANEL_ANCHOR_ID);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pendingScrollItemId, selectedId, productionTask]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    Promise.all([
      getContentItem(selectedId, { signal: ctrl.signal }),
      listContentVariants(selectedId, { signal: ctrl.signal }),
      listContentApprovals(selectedId, { signal: ctrl.signal }),
    ])
      .then(([itemDetail, variants, approvals]) => {
        if (ctrl.signal.aborted) return;
        setDetail({
          status: 'ready',
          item: itemDetail.content_item,
          variants: variants.content_variants,
          approvals: approvals.content_approvals,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
    // Codex round 7 P2 (Task 102): also re-fire on `reloadKey` bump.
    // Task 102's submit handler bumps reloadKey unconditionally on
    // submit success because the backend RPC auto-advances
    // `content_items.status`. Without `reloadKey` in this effect's
    // deps, the list refetches and shows the new status badge but
    // the SELECTED detail (driven by `selectedId` alone) keeps
    // showing the stale `in_production` until the user changes
    // selection. Adding `reloadKey` here makes the detail refetch
    // alongside the list.
  }, [selectedId, reloadKey]);

  // Task 068 — calendar load effect. Re-fires when the user changes
  // month / timezone or calls `setCalendarReloadKey` (post-mutation).
  useEffect(() => {
    if (!clientId) return;
    if (screen !== 'calendar') return;
    const ctrl = new AbortController();
    setCalendar({ status: 'loading' });
    listCalendarEntriesByMonth({
      clientId,
      month: calendarMonth,
      timezone: calendarTimezone,
      signal: ctrl.signal,
    })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setCalendar({ status: 'ready', entries: data.entries });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setCalendar({ status: 'not-found' });
          return;
        }
        setCalendar({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, screen, calendarMonth, calendarTimezone, calendarReloadKey]);

  // Task 069 — inbox load effect. Re-fires when the user enters the
  // inbox tab, switches inbox sub-stage (Task 104B), or after an
  // approve / request-changes action.
  useEffect(() => {
    if (!clientId) return;
    if (screen !== 'inbox') return;
    const ctrl = new AbortController();
    setInbox({ status: 'loading' });
    listPendingClientApprovals({ clientId, stage: inboxStage, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setInbox({ status: 'ready', items: data.items });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setInbox({ status: 'not-found' });
          return;
        }
        setInbox({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, screen, inboxReloadKey, inboxStage]);

  // Task 073 — fetch creative assets attached to whatever set of
  // content_items / content_variants is currently visible. Fires
  // after each surface (list, detail, calendar, inbox) loads.
  // De-duplicated by ids; skipped when nothing visible. The signed
  // URLs returned here are short-lived (60s) — re-fetched on
  // reload / surface change.
  useEffect(() => {
    if (!clientId) return;
    const itemIds = new Set<string>();
    const variantIds = new Set<string>();
    if (list.status === 'ready') {
      for (const i of list.items) itemIds.add(i.id);
    }
    if (detail.status === 'ready') {
      itemIds.add(detail.item.id);
      for (const v of detail.variants) variantIds.add(v.id);
    }
    if (calendar.status === 'ready') {
      for (const e of calendar.entries) {
        const it = e.content_variants?.content_items;
        if (it) itemIds.add(it.id);
        if (e.content_variants) variantIds.add(e.content_variants.id);
      }
    }
    if (inbox.status === 'ready') {
      for (const it of inbox.items) {
        itemIds.add(it.id);
        for (const v of it.content_variants) variantIds.add(v.id);
      }
    }
    if (itemIds.size === 0 && variantIds.size === 0) {
      setCreativeAssetsByTarget(new Map());
      return;
    }
    const ctrl = new AbortController();
    listContentCreativeAssets(
      {
        client_id: clientId,
        content_item_ids: Array.from(itemIds),
        content_variant_ids: Array.from(variantIds),
      },
      { signal: ctrl.signal },
    )
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const next = new Map<string, CreativeAssetCardRow>();
        // Codex round 2 P2 — first-writer-wins. The endpoint
        // returns matches sorted newest-first per target; keeping
        // only the first occurrence per key gives a deterministic
        // pick of the most recent attached asset, even if any
        // stale rows somehow slipped through the active filter.
        for (const a of data.assets) {
          const key = `${a.target_source}:${a.target_id}`;
          if (!next.has(key)) {
            next.set(key, a);
          }
        }
        setCreativeAssetsByTarget(next);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Don't escalate — failure to fetch previews shouldn't break
        // the page. Slots fall back to placeholders.
        console.warn('list-content-creative-assets failed', err);
      });
    return () => ctrl.abort();
  }, [clientId, list, detail, calendar, inbox, assetReloadKey]);

  // Task 101D — load the production task linked to the displayed
  // content_item. Task 164 widens this beyond `in_production` to the
  // full post-handoff status set (pending_internal_review through
  // published) so the new deliverable preview wiring can surface the
  // produced asset inside the platform card at every review stage,
  // not just during in_production. Pre-production statuses
  // (draft / pending_client_content_approval / client_content_approved)
  // and the terminal `cancelled` state continue to skip the load.
  // Cleared on item change so a stale task from a previous selection
  // never surfaces. Endpoint is read-only and RLS-bound; callers
  // without read access on the task simply get an empty array.
  //
  // Codex 101D-2 round 1 (P3): the same effect runs on BOTH
  // `detail` change AND `productionTaskReloadKey` bump. The latter
  // is triggered by Start/Submit success handlers and must NOT wipe
  // the success banner / pending submit / stale-task clears that the
  // detail-change path needs. Resolution: a ref tracks the previous
  // displayed item id so the effect can distinguish "item switched"
  // from "reload-only refresh" and preserve the success banner /
  // panel state on the latter.
  const prevDetailItemIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentItemId = detail.status === 'ready' ? detail.item.id : null;
    const itemChanged = prevDetailItemIdRef.current !== currentItemId;
    prevDetailItemIdRef.current = currentItemId;

    // Errors always reset (a fresh fetch / reload deserves a fresh
    // error slot — stale failures should not persist).
    setProductionTaskError(null);

    if (itemChanged) {
      // Item switch: wipe everything from the previous item.
      // Includes success banner (Codex round 11), stale productionTask
      // (round 8 — would otherwise render the old panel under the
      // new item until the fetch settles), pending submit retry
      // stash. The success-banner clear here is FOR ITEM CHANGES
      // ONLY — reload-only re-runs preserve it.
      setProductionTaskSuccess(null);
      setProductionTask(null);
      setProductionPendingSubmit(null);
    }

    // Task 164 — production task loads at any post-handoff status
    // so the deliverable preview surfaces inside the platform card
    // for the agency review flow. Pre-production statuses
    // (draft / pending_client_content_approval / client_content_approved
    // pre-auto-chain) and `cancelled` keep the early-return / clear.
    const POST_HANDOFF_STATUSES: ReadonlySet<ContentItemStatus> = new Set<ContentItemStatus>([
      'in_production',
      'pending_internal_review',
      'internal_approved',
      'pending_client_asset_approval',
      'client_asset_approved',
      'scheduled',
      'published',
    ]);
    if (detail.status !== 'ready' || !POST_HANDOFF_STATUSES.has(detail.item.status)) {
      setProductionTask(null);
      setProductionPendingSubmit(null);
      return;
    }
    const itemId = detail.item.id;
    const ctrl = new AbortController();
    listProductionTasksForContentItem({ contentItemId: itemId, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const next = data.tasks[0] ?? null;
        setProductionTask(next);
        // If the loaded task no longer matches the stashed pending
        // submit (different task id, or task already moved past the
        // upload-allowed states), clear it so retries don't reuse a
        // stale storage_key against the wrong task.
        setProductionPendingSubmit((prev) => {
          if (!prev) return prev;
          if (!next) return null;
          if (prev.taskId !== next.id) return null;
          if (
            next.status !== 'open' &&
            next.status !== 'in_progress' &&
            next.status !== 'blocked'
          ) {
            return null;
          }
          return prev;
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Don't break the page — the panel is supplementary.
        console.warn('list-production-tasks-for-content-item failed', err);
        setProductionTask(null);
      });
    return () => ctrl.abort();
  }, [detail, productionTaskReloadKey]);

  // Task 175 PR-A · Issue 1 — client-category-only deliverable fallback.
  // Fires alongside the production-task loader above (NOT instead of)
  // so the agency render path keeps using the primary source. When the
  // viewer is a client-category user AND the displayed item is at an
  // asset-stage status, we query the inbox EF for the matching row and
  // stash its `latest_production_deliverable`. The platform card
  // consumer prefers `productionTask.latest_production_deliverable`
  // (works for agency viewers) and falls through to
  // `clientFallbackDeliverable` (works for client viewers blocked by
  // `tasks_select` RLS).
  useEffect(() => {
    const ASSET_STAGE_STATUSES: ReadonlySet<ContentItemStatus> = new Set<ContentItemStatus>([
      'pending_client_asset_approval',
      'client_asset_approved',
      'scheduled',
      'published',
    ]);
    if (
      detail.status !== 'ready' ||
      !hasClientCategoryRole ||
      !clientId ||
      !ASSET_STAGE_STATUSES.has(detail.item.status)
    ) {
      setClientFallbackDeliverable(null);
      return;
    }
    const itemId = detail.item.id;
    const ctrl = new AbortController();
    listPendingClientApprovals({ clientId, stage: 'asset', signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const row = data.items.find((r) => r.id === itemId);
        setClientFallbackDeliverable(row?.latest_production_deliverable ?? null);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Supplementary loader — don't break the page on failure. The
        // approval CTA still works (the transition RPC has its own
        // visibility gate); the slot just renders the discipline
        // placeholder, same as today's bug.
        console.warn('list-pending-client-approvals fallback failed', err);
        setClientFallbackDeliverable(null);
      });
    return () => ctrl.abort();
  }, [detail, hasClientCategoryRole, clientId, productionTaskReloadKey]);

  // Task 104C — publish-jobs panel loader. Fires whenever the displayed
  // item is at status='scheduled' or 'published' (the two states that
  // surface the per-platform job table). All other statuses keep the
  // state at idle so the panel renders the schedule CTA only.
  useEffect(() => {
    if (detail.status !== 'ready') {
      setPublishJobsState({ status: 'idle' });
      return;
    }
    const itemStatus = detail.item.status;
    if (itemStatus !== 'scheduled' && itemStatus !== 'published') {
      setPublishJobsState({ status: 'idle' });
      return;
    }
    const itemId = detail.item.id;
    const ctrl = new AbortController();
    setPublishJobsState({ status: 'loading' });
    setPublishError(null);
    listPublishJobs({ contentItemId: itemId, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setPublishJobsState({ status: 'ready', jobs: data.jobs });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.warn('list-publish-jobs failed', err);
        setPublishJobsState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [detail, publishJobsReloadKey]);

  // Task 073 — upload-and-attach handler. Composed of three calls:
  //   1. create-creative-asset-upload  → signed PUT URL
  //   2. PUT to signed URL (storage gateway)
  //   3. complete-creative-asset-upload → atomic asset + match insert
  // Errors at any step surface with a polished message; nothing is
  // recovered automatically (V1).
  const handleAttachCreativeAsset = useCallback(
    async (args: {
      target_source: CreativeAssetTargetSource;
      target_id: string;
      file: File;
    }): Promise<void> => {
      if (!clientId) throw new Error('client missing');
      const file = args.file;
      const mime = (file.type || '').toLowerCase();
      let assetType: CreativeAssetUploadKind;
      if (mime.startsWith('image/')) assetType = 'image';
      else if (mime.startsWith('video/')) assetType = 'video';
      else throw new Error('نوع الملف غير مدعوم. الصور أو الفيديو فقط.');

      // Probe dimensions / duration client-side. Used to populate
      // the `creative_assets` columns; required by the table CHECK.
      const probe = await probeMediaDimensions(file, assetType);

      const created = await createCreativeAssetUpload({
        client_id: clientId,
        target_source: args.target_source,
        target_id: args.target_id,
        asset_type: assetType,
        mime_type: mime,
        byte_size: file.size,
        filename: file.name,
      });

      await putToSignedUploadUrl({
        url: created.upload_url,
        file,
        contentType: mime,
      });

      await completeCreativeAssetUpload({
        upload_intent_id: created.upload_intent_id,
        canonical_name: file.name,
        width_px: probe.width,
        height_px: probe.height,
        duration_ms: probe.durationMs,
      });

      // Re-fetch the page-level asset map.
      setAssetReloadKey((n) => n + 1);
    },
    [clientId],
  );

  // Task 076 — archive (remove) an attached creative asset from a
  // target. Same permission requirement as upload (both creative
  // manage perms + agency role + leadership-or-assigned). The
  // server RPC re-validates; this UI gate just hides the button.
  const handleRemoveCreativeAsset = useCallback(
    async (args: {
      target_source: CreativeAssetTargetSource;
      target_id: string;
      creative_asset_id: string;
    }): Promise<void> => {
      await archiveContentCreativeAsset({
        target_source: args.target_source,
        target_id: args.target_id,
        creative_asset_id: args.creative_asset_id,
      });
      // Re-fetch the page-level asset map so the card re-renders to
      // the empty / placeholder state.
      setAssetReloadKey((n) => n + 1);
    },
    [],
  );

  if (!clientId) return null;

  const onGenerateAi = async () => {
    if (!canAiGenerate || !clientId) return;
    // Task 284C — resolve the picker's (platforms × formats) into output GROUPS, then
    // submit the additive `requested_outputs` contract (Task 284A/B) for ANY valid
    // picker selection (>= 1 resolved group). The grouped worker then produces one
    // shared asset per format — including a single format fanned across multiple
    // targets (e.g. one vertical_video for reel + tiktok + snapchat). `platforms`
    // stays the flattened union (the legacy publish/storage currency) for backward
    // compatibility. Legacy mode-based generation now applies only to non-picker
    // callers that send no requested_outputs.
    const groups = resolveRequestedOutputs(
      orderedAssetFormats(aiForm.assetFormats),
      aiForm.parentPlatforms,
    );
    if (groups.length < 1) return;
    setAiError(null);
    setView({ kind: 'generating', form: aiForm });
    try {
      const data = await generateContentCalendarDraft({
        input: {
          client_id: clientId,
          mode: aiForm.mode,
          month: aiForm.month,
          platforms: aiForm.platforms,
          entry_count: aiForm.entryCount,
          instruction: aiForm.instruction.trim().length === 0 ? null : aiForm.instruction.trim(),
          timezone: aiForm.timezone,
          // groups.length >= 1 is guaranteed by the no-target guard above.
          requested_outputs: groups.map((g) => ({
            asset_format: g.format,
            targets: g.targets,
          })),
        },
      });
      const draft = aiResponseToDraft(data, aiForm);
      // Task 280J — timing comes from package count + operating setup, not the
      // AI: override each draft entry's date/time with deterministic planner
      // slots (the user still edits any slot before saving; the save payload is
      // unchanged). `count` is the actual returned item count so slots map 1:1.
      const setup =
        operatingSetupState && operatingSetupState.clientId === clientId
          ? operatingSetupState.setup
          : null;
      const slots = buildScheduleSlots({
        count: draft.items.length,
        targetMonth: aiForm.month,
        timezone: aiForm.timezone,
        firstPublishDate: setup?.first_publish_date ?? null,
        productionLeadDays: setup?.production_lead_time_days ?? 0,
        approvalBufferDays: setup?.approval_buffer_days ?? 0,
        preferredDays: setup?.preferred_days ?? null,
        preferredTimesLocal: setup?.preferred_times_local ?? null,
      });
      const scheduledDraft = { ...draft, items: applyScheduleSlots(draft.items, slots) };
      setView({ kind: 'draft', draft: scheduledDraft, saveError: null, saving: false });
    } catch (err: unknown) {
      setAiError(describeApiError(err));
      setView({ kind: 'mode-chooser' });
    }
  };

  // Task 068 — open the edit modal seeded from a calendar entry. Only
  // entries whose parent item.status='draft' are editable; we expose
  // the button conditionally and the RPC re-checks server-side.
  const openEditModal = useCallback((entry: CalendarEntryRow) => {
    const variant = entry.content_variants;
    const item = variant?.content_items ?? null;
    if (!variant || !item) return;
    const tz = entry.timezone && entry.timezone.length > 0 ? entry.timezone : DEFAULT_TIMEZONE;
    const { date, time } = splitUtcIntoLocalParts(entry.scheduled_for, tz);
    const ps = (variant.platform_specific ?? {}) as Record<string, unknown>;
    const hashtagsRaw = Array.isArray(ps['hashtags']) ? (ps['hashtags'] as unknown[]) : [];
    const hashtags = hashtagsRaw
      .filter((h): h is string => typeof h === 'string')
      .map((h) => h.replace(/^#/, ''))
      .join(', ');
    setEditModal({
      entry,
      title: item.title,
      body: item.body ?? '',
      headline: variant.headline ?? '',
      caption: variant.caption ?? '',
      variantBody: variant.body ?? '',
      hashtags,
      cta: typeof ps['cta'] === 'string' ? (ps['cta'] as string) : '',
      link: typeof ps['link'] === 'string' ? (ps['link'] as string) : '',
      scheduledLocalDate: date,
      scheduledLocalTime: time,
      timezone: tz,
      saving: false,
      error: null,
    });
  }, []);

  const onSubmitEditModal = async () => {
    if (!editModal || editModal.saving) return;
    if (!canSaveDraft) {
      setEditModal({ ...editModal, error: 'لا تملك صلاحية تعديل المسودة.' });
      return;
    }
    const validationError = validateEditModalBeforeSave(editModal);
    if (validationError) {
      setEditModal({ ...editModal, error: validationError });
      return;
    }
    const variant = editModal.entry.content_variants;
    const item = variant?.content_items ?? null;
    if (!variant || !item) return;
    setEditModal({ ...editModal, saving: true, error: null });
    try {
      const offsetIso = composeIsoFromLocalParts(
        editModal.scheduledLocalDate,
        editModal.scheduledLocalTime,
        editModal.timezone,
      );
      const hashtags = editModal.hashtags
        .split(',')
        .map((h) => h.trim().replace(/^#/, ''))
        .filter((h) => h.length > 0);
      // Task 289C — merge semantics: the RPC now PRESERVES omitted keys
      // (so a reschedule can no longer wipe the production brief /
      // asset_format), which means clearing must be EXPLICIT. The modal
      // edits all three allowed keys, so it always sends all three:
      // a value sets, null clears.
      const platformSpecific: {
        hashtags: ReadonlyArray<string> | null;
        cta: string | null;
        link: string | null;
      } = {
        hashtags: hashtags.length > 0 ? hashtags : null,
        cta: editModal.cta.trim().length > 0 ? editModal.cta.trim() : null,
        link: editModal.link.trim().length > 0 ? editModal.link.trim() : null,
      };
      await updateContentCalendarDraft({
        input: {
          content_item_id: item.id,
          content_variant_id: variant.id,
          calendar_entry_id: editModal.entry.id,
          title: editModal.title.trim(),
          body: editModal.body.trim().length > 0 ? editModal.body.trim() : null,
          headline: editModal.headline.trim().length > 0 ? editModal.headline.trim() : null,
          caption: editModal.caption.trim().length > 0 ? editModal.caption.trim() : null,
          variant_body:
            editModal.variantBody.trim().length > 0 ? editModal.variantBody.trim() : null,
          platform_specific: platformSpecific,
          scheduled_for: offsetIso,
          timezone: editModal.timezone,
        },
      });
      setEditModal(null);
      setCalendarReloadKey((n) => n + 1);
      setReloadKey((n) => n + 1);
    } catch (err: unknown) {
      // Task 288B — field-aware Arabic copy for the update/edit path too.
      setEditModal({
        ...editModal,
        saving: false,
        error: describeSaveError(err),
      });
    }
  };

  const runTransition = useCallback(
    async (target: ContentItemTransitionTarget, reason: string) => {
      if (transitionInFlight) return;
      if (detail.status !== 'ready') return;
      const item = detail.item;
      setTransitionError(null);
      setTransitionSuccess(null);
      setTransitionInFlight(true);
      try {
        const data = await transitionContentItemStatus({
          input: {
            content_item_id: item.id,
            target_status: target,
            reason,
          },
        });
        // Task 101C — surface the production-handoff outcome inline.
        // The wire response carries `task_id` (non-null only on the
        // production-handoff edge) and `task_already_existed` (true
        // on idempotent retries). Other transitions return both
        // null/false — we leave the success note empty for them so
        // the UI stays unchanged.
        //
        // Codex round 2 P2 fix — render the message at PAGE level,
        // not inside ContentDetail. The transition removes the row
        // from the `client_content_approved` filter and auto-
        // selection moves to a sibling, so an item-scoped banner
        // would silently drop the message in the primary workflow.
        // The page-level slot survives selection changes; it gets
        // cleared on the next transition or any filter change.
        if (target === 'in_production' && data.task_id) {
          const shortId = data.task_id.slice(0, 8);
          setTransitionSuccess(
            data.task_already_existed
              ? `مهمة الإنتاج موجودة مسبقاً (#${shortId}). لا تكرار.`
              : `تم إنشاء مهمة الإنتاج (#${shortId}).`,
          );
        }
        // Refresh detail + list + calendar.
        setReloadKey((n) => n + 1);
        setCalendarReloadKey((n) => n + 1);
        // Re-fetch detail explicitly to update the status pill.
        try {
          const fresh = await getContentItem(item.id);
          const variants = await listContentVariants(item.id);
          const approvals = await listContentApprovals(item.id);
          setDetail({
            status: 'ready',
            item: fresh.content_item,
            variants: variants.content_variants,
            approvals: approvals.content_approvals,
          });
        } catch {
          /* swallow — list reload will repopulate */
        }
      } catch (err: unknown) {
        // Task 171 — structured developer diagnostic + user-visible
        // Arabic copy. The mapper guarantees no `KMI API ... (HTTP
        // ...)` envelope text leaks into the UI; the logger preserves
        // the full envelope shape for dev-tools debugging.
        logTransitionFailure(err, { contentItemId: item.id, targetStatus: target });
        setTransitionError(describeTransitionError(err));
        // Task 112B — `not_found` from the v2 RPC is the documented
        // stale-list / cross-org defense; bumping the reload keys
        // makes the now-invisible row drop out of the list/calendar
        // so a retry can't keep firing the same 404.
        if (isNotFoundApiError(err)) {
          setReloadKey((n) => n + 1);
          setCalendarReloadKey((n) => n + 1);
        }
      } finally {
        setTransitionInFlight(false);
      }
    },
    [detail, transitionInFlight],
  );

  // Task 101D — production-task action handlers.
  // Every state change routes through a dedicated edge function
  // (start-production-task / prepare-production-task-upload /
  // submit-production-task) which calls a SECURITY DEFINER RPC.
  // Direct PostgREST writes against `tasks` are explicitly NOT used.
  const handleStartProductionTask = useCallback(
    async (taskId: string) => {
      if (productionTaskInFlight) return;
      setProductionTaskError(null);
      setProductionTaskSuccess(null);
      setProductionTaskInFlight(true);
      try {
        const data = await startProductionTask({ input: { task_id: taskId } });
        setProductionTaskSuccess(
          data.task_already_started
            ? 'المهمة قيد التنفيذ بالفعل.'
            : 'تم بدء المهمة. حالة المهمة الآن: قيد التنفيذ.',
        );
        setProductionTaskReloadKey((n) => n + 1);
      } catch (err: unknown) {
        setProductionTaskError(describeApiError(err));
      } finally {
        setProductionTaskInFlight(false);
      }
    },
    [productionTaskInFlight],
  );

  const handleSubmitProductionDeliverable = useCallback(
    async (args: {
      readonly taskId: string;
      readonly file: File;
      readonly title: string;
      readonly summary: string;
    }): Promise<boolean> => {
      // Task 101D Codex P2 (round 6) — return value contract. The
      // panel needs to know whether THIS submit call succeeded so it
      // can clear the staged form fields. Reading `props.success` in
      // the panel after `await onSubmit` is unsafe: that prop may
      // still hold a STALE success message (e.g. from a prior Start
      // banner) because React props don't refresh inside the closure.
      // Returning `true` only on a fresh-this-call success is the
      // narrow contract that lets the panel decide correctly.
      if (productionTaskInFlight) return false;
      setProductionTaskError(null);
      setProductionTaskSuccess(null);
      setProductionTaskInFlight(true);
      try {
        const file = args.file;
        const mime = (file.type || '').toLowerCase() || 'application/octet-stream';
        // Task 101D Codex P2 — Idempotent retry path. If a previous
        // submit attempt put the bytes at a known storage_key but the
        // submit RPC call itself failed (timeout, transient 5xx, etc.),
        // reuse the SAME storage_key so the (task_id, storage_key)
        // idempotency key in `submit_production_task_v1` matches and
        // the bucket object is not orphaned. Match strictly on the
        // (taskId, fileName, fileSize, fileLastModified) tuple —
        // including `lastModified` (round 10 P2) defends against the
        // user swapping in a DIFFERENT file with a coincidentally-
        // same name+size between attempts (which would otherwise
        // silently submit the previous bytes).
        let storageKey: string;
        const reuse =
          productionPendingSubmit &&
          productionPendingSubmit.taskId === args.taskId &&
          productionPendingSubmit.fileName === file.name &&
          productionPendingSubmit.fileSize === file.size &&
          productionPendingSubmit.fileLastModified === file.lastModified
            ? productionPendingSubmit
            : null;
        if (reuse) {
          storageKey = reuse.storageKey;
        } else {
          // Step 1 — server gates + mints signed PUT URL + canonical
          // storage_key. The storage_key is the only payload we keep
          // for step 3.
          const prepared = await prepareProductionTaskUpload({
            input: {
              task_id: args.taskId,
              filename: file.name,
              mime_type: mime,
              byte_size: file.size,
            },
          });
          // Step 2 — direct browser PUT to storage. The bucket is
          // private; this signed URL is the only path that can write.
          // If the PUT itself fails the bucket has no orphan to worry
          // about, so we don't preserve any state — caller can retry
          // and prepare will mint a fresh storage_key.
          await putToSignedUploadUrl({ url: prepared.upload_url, file, contentType: mime });
          storageKey = prepared.storage_key;
          // PUT succeeded — stash the key BEFORE calling submit so
          // any failure on the next line still leaves a recoverable
          // pointer for the retry.
          setProductionPendingSubmit({
            taskId: args.taskId,
            storageKey,
            fileName: file.name,
            fileSize: file.size,
            fileLastModified: file.lastModified,
          });
        }
        // Step 3 — atomic DB write (insert task_files + task_submissions
        // + flip task.status='submitted'). Idempotent on
        // (task_id, storage_key). Task 102: a FRESH submit also
        // atomically auto-advances `content_items.status` from
        // `in_production` → `pending_internal_review` inside the same
        // transaction; idempotent retries leave content_item.status
        // unchanged.
        const data = await submitProductionTask({
          input: {
            task_id: args.taskId,
            storage_key: storageKey,
            title: args.title,
            mime_type: mime,
            summary: args.summary,
          },
        });
        // Submit landed (either a fresh insert or an idempotent
        // already-submitted hit) — drop the stash so a follow-up
        // upload starts a fresh session.
        setProductionPendingSubmit(null);
        const shortId = data.task_file_id.slice(0, 8);
        setProductionTaskSuccess(
          data.task_already_submitted
            ? `تم تسليم هذا الملف مسبقاً (#${shortId}).`
            : `تم تسليم الملف للمراجعة الداخلية (#${shortId}).`,
        );
        setProductionTaskReloadKey((n) => n + 1);
        // Task 102 (Codex round 6 P2): bump the page-level `reloadKey`
        // unconditionally on a successful submit response. On a FRESH
        // submit the backend RPC auto-advanced
        // `content_items.status` from `in_production` →
        // `pending_internal_review` and the FE must refetch to see
        // the new state. On an IDEMPOTENT RETRY (the original submit
        // committed but the response was lost) the backend has
        // ALREADY auto-advanced — and the FE state may be even more
        // stale than before, since reviewer activity could have
        // moved the item further (internal_approved, draft, etc.)
        // between commits. Refetching costs one cheap network round-
        // trip and guarantees the UI reflects current truth.
        setReloadKey((n) => n + 1);
        return true;
      } catch (err: unknown) {
        setProductionTaskError(describeApiError(err));
        return false;
      } finally {
        setProductionTaskInFlight(false);
      }
    },
    [productionTaskInFlight, productionPendingSubmit],
  );

  // ── Task 104C — publishing workflow handlers ────────────────────────

  // Open the inline schedule modal preset to a sensible default
  // (instagram + tomorrow at 10:00 local). All four publish handlers
  // bail early when `publishInFlight` is set so concurrent clicks on
  // mark / cancel / schedule from a stale render can't race the same
  // job through two RPCs.
  const handleOpenScheduleModal = useCallback(
    (
      contentItemId: string,
      availablePlatforms: ReadonlySet<PublishWorkflowPlatform>,
      plannedScheduledFor: { iso: string; timezone: string | null } | null,
    ) => {
      if (publishInFlight) return;
      if (availablePlatforms.size === 0) return;
      // Task 105A — prefill date/time/timezone from the item's
      // earliest non-archived calendar_entries.scheduled_for when
      // present. Only fall back to tomorrow 10:00 if the item has
      // no calendar entry on file. Both branches keep the
      // YYYY-MM-DD + HH:MM split the modal expects.
      let prefillDate: string;
      let prefillTime: string;
      let prefillTimezone: string;
      if (plannedScheduledFor) {
        const tz = plannedScheduledFor.timezone ?? DEFAULT_TIMEZONE;
        const parts = splitUtcIntoLocalParts(plannedScheduledFor.iso, tz);
        prefillDate = parts.date;
        prefillTime = parts.time;
        prefillTimezone = tz;
      } else {
        const now = new Date();
        const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        const yyyy = tomorrow.getFullYear();
        const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const dd = String(tomorrow.getDate()).padStart(2, '0');
        prefillDate = `${yyyy}-${mm}-${dd}`;
        prefillTime = '10:00';
        prefillTimezone = DEFAULT_TIMEZONE;
      }
      // Default selection: instagram if it's available, otherwise the
      // first available platform. Never preselect an unavailable
      // platform — the schedule RPC would 23514.
      const defaultSelection = new Set<PublishWorkflowPlatform>();
      if (availablePlatforms.has('instagram')) {
        defaultSelection.add('instagram');
      } else {
        for (const p of PUBLISH_WORKFLOW_PLATFORMS) {
          if (availablePlatforms.has(p)) {
            defaultSelection.add(p);
            break;
          }
        }
      }
      setScheduleModal({
        contentItemId,
        selectedPlatforms: defaultSelection,
        availablePlatforms,
        scheduledLocalDate: prefillDate,
        scheduledLocalTime: prefillTime,
        plannedScheduledForIso: plannedScheduledFor?.iso ?? null,
        timezone: prefillTimezone,
        notes: '',
        saving: false,
        error: null,
      });
    },
    [publishInFlight],
  );

  const handleSubmitScheduleModal = useCallback(async () => {
    if (!scheduleModal) return;
    if (publishInFlight) return;
    if (scheduleModal.selectedPlatforms.size === 0) {
      setScheduleModal({ ...scheduleModal, error: 'اختر منصة واحدة على الأقل.' });
      return;
    }
    if (
      !/^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(scheduleModal.scheduledLocalDate)
    ) {
      setScheduleModal({ ...scheduleModal, error: 'تاريخ النشر غير صالح.' });
      return;
    }
    if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(scheduleModal.scheduledLocalTime)) {
      setScheduleModal({ ...scheduleModal, error: 'وقت النشر غير صالح.' });
      return;
    }
    // Use the approved planned timestamp exactly when it exists. The
    // displayed local date/time fields are disabled in that mode, so
    // this avoids silently drifting the customer-approved schedule by
    // recomposing the instant from editable controls.
    let isoLocal = scheduleModal.plannedScheduledForIso;
    if (!isoLocal) {
      // Compose ISO from local date+time plus the active timezone
      // offset. Reuse the existing helper used by the calendar edit
      // modal so the semantics match (DST-aware via Intl.DateTimeFormat).
      try {
        const offset = computeTzOffsetMinutes(
          scheduleModal.scheduledLocalDate,
          scheduleModal.scheduledLocalTime,
          scheduleModal.timezone,
        );
        const sign = offset >= 0 ? '+' : '-';
        const abs = Math.abs(offset);
        const offsetH = String(Math.floor(abs / 60)).padStart(2, '0');
        const offsetM = String(abs % 60).padStart(2, '0');
        isoLocal = `${scheduleModal.scheduledLocalDate}T${scheduleModal.scheduledLocalTime}:00${sign}${offsetH}:${offsetM}`;
      } catch {
        setScheduleModal({ ...scheduleModal, error: 'تعذر حساب المنطقة الزمنية.' });
        return;
      }
    }
    if (Date.parse(isoLocal) <= Date.now()) {
      setScheduleModal({ ...scheduleModal, error: 'وقت النشر يجب أن يكون في المستقبل.' });
      return;
    }
    const platforms = Array.from(scheduleModal.selectedPlatforms);
    if (platforms.some((p) => !scheduleModal.availablePlatforms.has(p))) {
      setScheduleModal({
        ...scheduleModal,
        error: 'اختر منصات لها متغيّرات موجودة.',
      });
      return;
    }
    setScheduleModal({ ...scheduleModal, saving: true, error: null });
    setPublishInFlight(true);
    try {
      await scheduleContentItemPublish({
        input: {
          content_item_id: scheduleModal.contentItemId,
          scheduled_for: isoLocal,
          platforms,
          // Captions are NOT sent — they are immutable after
          // client_asset_approved (Task 104C round 2 fix).
          notes: scheduleModal.notes.trim() || null,
        },
      });
      setScheduleModal(null);
      setReloadKey((n) => n + 1);
      setPublishJobsReloadKey((n) => n + 1);
    } catch (err: unknown) {
      setScheduleModal((prev) =>
        prev ? { ...prev, saving: false, error: describeApiError(err) } : prev,
      );
    } finally {
      setPublishInFlight(false);
    }
  }, [scheduleModal, publishInFlight]);

  const handleMarkPublishJobPublished = useCallback(
    async (jobId: string) => {
      if (publishInFlight) return;
      const url = window.prompt('رابط المنشور (اختياري):');
      if (url === null) return;
      const externalId = window.prompt('معرّف المنشور الخارجي (اختياري):');
      if (externalId === null) return;
      setPublishInFlight(true);
      setPublishError(null);
      try {
        await markPublishJobPublished({
          input: {
            publish_job_id: jobId,
            external_post_url: url.trim() || null,
            external_post_id: externalId.trim() || null,
          },
        });
        setPublishJobsReloadKey((n) => n + 1);
        setReloadKey((n) => n + 1);
      } catch (err: unknown) {
        setPublishError(describeApiError(err));
      } finally {
        setPublishInFlight(false);
      }
    },
    [publishInFlight],
  );

  const handleMarkPublishJobFailed = useCallback(
    async (jobId: string) => {
      if (publishInFlight) return;
      const reason = window.prompt('سبب الفشل (مطلوب):');
      if (reason === null) return;
      const trimmed = reason.trim();
      if (trimmed.length === 0) {
        window.alert('السبب مطلوب.');
        return;
      }
      setPublishInFlight(true);
      setPublishError(null);
      try {
        await markPublishJobFailed({
          input: { publish_job_id: jobId, reason: trimmed },
        });
        setPublishJobsReloadKey((n) => n + 1);
      } catch (err: unknown) {
        setPublishError(describeApiError(err));
      } finally {
        setPublishInFlight(false);
      }
    },
    [publishInFlight],
  );

  const handleCancelPublishJob = useCallback(
    async (jobId: string) => {
      if (publishInFlight) return;
      if (!window.confirm('سيتم إلغاء مهمة النشر. هل تؤكد؟')) return;
      const reason = window.prompt('سبب الإلغاء (اختياري):');
      if (reason === null) return;
      setPublishInFlight(true);
      setPublishError(null);
      try {
        await cancelPublishJob({
          input: { publish_job_id: jobId, reason: reason.trim() || null },
        });
        setPublishJobsReloadKey((n) => n + 1);
        setReloadKey((n) => n + 1);
      } catch (err: unknown) {
        setPublishError(describeApiError(err));
      } finally {
        setPublishInFlight(false);
      }
    },
    [publishInFlight],
  );

  // Task 069 + Task 104B — client inbox approve / request-changes
  // handlers. Both delegate to the existing transition RPC; the RPC
  // enforces client-category role for both `client_content_approved`
  // and `client_asset_approved`. The inbox sub-stage selects the
  // approve target so the client UI works for both step 2 (pre-prod
  // content approval) and step 5 (final asset approval).
  const onInboxApprove = useCallback(
    async (item: PendingClientApprovalRow) => {
      if (!canDecideClientApproval) return;
      if (transitionInFlight) return;
      // Pull the row's CURRENT status so the dispatched target matches
      // the parent's actual state (defensive — `inboxStage` is the UX
      // hint; the status field is authoritative). Mismatches degrade
      // gracefully into the v2 RPC's edge-validation error rather than
      // a wrong target_status.
      const isAssetStage = item.status === 'pending_client_asset_approval';
      const target: ContentItemTransitionTarget = isAssetStage
        ? 'client_asset_approved'
        : 'client_content_approved';
      // Task 290A — the native browser confirmation gate is REMOVED
      // for both inbox stages (content + final asset): production QA found
      // it added friction without protection (the decision is already
      // guarded by permission + in-flight state, and «طلب تعديل» is
      // the reversal path clients actually use). One click on
      // «اعتماد» dispatches immediately; double-submit protection
      // below is unchanged.
      // Task 105A — generic `window.prompt('سبب الاعتماد')` removed.
      // The inbox approve action is a normal client-side approval; the
      // RPC's audit row is satisfied by a deterministic reason string.
      // The asset/content split keeps the audit log readable when
      // dashboards filter by stage.
      const reason = isAssetStage
        ? 'موافقة العميل النهائية على الأصل من صندوق الوارد'
        : 'موافقة العميل على المحتوى من صندوق الوارد';
      setTransitionInFlight(true);
      setTransitionError(null);
      try {
        await transitionContentItemStatus({
          input: {
            content_item_id: item.id,
            target_status: target,
            reason,
          },
        });
        setInboxReloadKey((n) => n + 1);
        setReloadKey((n) => n + 1);
        setCalendarReloadKey((n) => n + 1);
      } catch (err: unknown) {
        // Task 171 — structured logger + workflow-aware mapper. See the
        // page-level handler for the rationale.
        logTransitionFailure(err, { contentItemId: item.id, targetStatus: target });
        setTransitionError(describeTransitionError(err));
        // Task 112B — same stale-list defense as the page-level
        // transition. Refresh the inbox + list + calendar so the
        // disappeared row stops triggering the same 404.
        if (isNotFoundApiError(err)) {
          setInboxReloadKey((n) => n + 1);
          setReloadKey((n) => n + 1);
          setCalendarReloadKey((n) => n + 1);
        }
      } finally {
        setTransitionInFlight(false);
      }
    },
    [canDecideClientApproval, transitionInFlight],
  );

  // Task 105A — client request-changes is now a structured in-app
  // modal instead of a generic browser prompt. The modal collects
  // feedback_notes (required), an optional rejection_category, and
  // an optional edited_version. The three fields are serialized into
  // the existing `reason` text field on the transition RPC so the
  // existing content_approvals decision audit (Task 068) carries the
  // structured feedback verbatim. Storing these as separate columns
  // / metadata jsonb keys is a follow-up task — per the 105A spec
  // ("do not invent a large new subsystem in this task"), the
  // serialized text in `reason` is the V1 storage path.
  const onInboxRequestChanges = useCallback(
    (item: PendingClientApprovalRow) => {
      // Mirror the approve gate. The transition RPC's
      // `pending_client_content_approval → draft` branch admits both
      // agency-with-content.create and client-with-approval.client.decide
      // actors, but the inbox is a CLIENT-side surface only — the
      // agency uses the calendar/list TransitionPanel for the
      // request-changes / return-to-draft path. Without this guard
      // a non-`approval.client.decide` reader on the inbox could
      // click the button and fire a request the RPC will reject; the
      // FE-side guard turns that into a no-op rather than a 4xx.
      if (!canDecideClientApproval) return;
      if (transitionInFlight) return;
      setRequestChangesModal({
        contentItemId: item.id,
        contentItemTitle: item.title,
        feedbackNotes: '',
        rejectionCategory: '',
        editedVersion: '',
        saving: false,
        error: null,
      });
    },
    [canDecideClientApproval, transitionInFlight],
  );

  const handleSubmitRequestChangesModal = useCallback(async () => {
    setRequestChangesModal((prev) => {
      if (!prev) return prev;
      if (prev.saving) return prev;
      const notes = prev.feedbackNotes.trim();
      if (notes.length === 0) {
        return { ...prev, error: 'الملاحظات مطلوبة قبل إرسال طلب التعديل.' };
      }
      const reason = buildRequestChangesReason(prev);
      if (reason.length > 1000) {
        return {
          ...prev,
          error: 'إجمالي الملاحظات + النسخة المعدّلة طويل جداً (الحد الأقصى 1000 حرف لسجل القرار).',
        };
      }
      // Side-effect dispatch — kick the RPC asynchronously after the
      // state guard has set saving=true.
      void (async () => {
        setTransitionInFlight(true);
        setTransitionError(null);
        try {
          await transitionContentItemStatus({
            input: {
              content_item_id: prev.contentItemId,
              target_status: 'draft',
              reason,
            },
          });
          setRequestChangesModal(null);
          setInboxReloadKey((n) => n + 1);
          setReloadKey((n) => n + 1);
          setCalendarReloadKey((n) => n + 1);
        } catch (err: unknown) {
          // Task 171 — structured logger + workflow-aware mapper. The
          // modal surfaces the same Arabic copy so the user-visible
          // text is consistent across all three transition entry
          // points.
          logTransitionFailure(err, {
            contentItemId: prev.contentItemId,
            targetStatus: 'draft',
          });
          const message = describeTransitionError(err);
          setTransitionError(message);
          setRequestChangesModal((s) => (s ? { ...s, saving: false, error: message } : s));
          // Task 112B — stale-list defense: the request-changes click
          // can race the same RLS flip, so bump the reload keys on
          // not_found so the inbox / list / calendar drop the
          // now-invisible row instead of leaving the modal stuck.
          if (isNotFoundApiError(err)) {
            setInboxReloadKey((n) => n + 1);
            setReloadKey((n) => n + 1);
            setCalendarReloadKey((n) => n + 1);
          }
        } finally {
          setTransitionInFlight(false);
        }
      })();
      return { ...prev, saving: true, error: null };
    });
  }, []);

  const onSaveDraft = async () => {
    if (!clientId || view.kind !== 'draft' || view.saving) return;
    // Codex round 1 P2 — frontend permission re-check before save.
    // RLS + the SECURITY DEFINER RPC are the load-bearing gates, but
    // the action button must not fire if the actor can't satisfy
    // the same predicates the server enforces. A user who lost
    // `content.create` between mount and save would otherwise hit a
    // 403 with a confusing "حاول مجدداً" UX.
    if (!canSaveDraft) {
      setView({ ...view, saveError: 'لا تملك صلاحية حفظ الكالندر.', saving: false });
      return;
    }
    const validationError = validateDraftBeforeSave(view.draft);
    if (validationError) {
      setView({ ...view, saveError: validationError, saving: false });
      return;
    }
    setView({ ...view, saveError: null, saving: true });
    try {
      const items: ReadonlyArray<SaveContentCalendarDraftItemInput> = view.draft.items.map((item) =>
        buildSaveItem(item, view.draft.timezone),
      );
      await saveContentCalendarDraft({
        input: {
          client_id: clientId,
          origin: view.draft.origin,
          ai_run_id: view.draft.aiRunId ?? undefined,
          mode: view.draft.mode ?? undefined,
          items,
        },
      });
      setView({ kind: 'list' });
      setReloadKey((n) => n + 1);
    } catch (err: unknown) {
      // Task 288A — field-aware Arabic copy for save failures.
      setView({ ...view, saveError: describeSaveError(err), saving: false });
    }
  };

  return (
    <div className="stack-md">
      {/*
        Task 107A — Content workspace UI foundation.
        Page header + primary actions block. Replaces the pre-107A
        single `toolbar-row` that mixed view-switcher chips with the
        create buttons. The 107A redesign promotes the create
        actions to the header (per `design_handoff/mvp/client-workspace.html`
        `.page-head .actions` pattern), and moves the list /
        calendar / inbox view switcher into the dedicated `.tabs`
        bar below the header. Status + discipline filter chips
        stay where they are because they're per-screen filters,
        not page-level navigation.
      */}
      {view.kind === 'list' ? (
        <header className="page-head">
          <div>
            <h1>المحتوى</h1>
            <p className="sub">
              مساحة عمل المحتوى لهذا العميل — إنشاء، مراجعة، اعتماد، وجدولة المنشورات.
            </p>
          </div>
          {canAiGenerate || canSaveDraft ? (
            <div className="actions">
              {canAiGenerate ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setAiError(null);
                    setView({ kind: 'mode-chooser' });
                  }}
                >
                  إنشاء كالندر بالذكاء الاصطناعي
                </button>
              ) : null}
              {canSaveDraft ? (
                <button
                  type="button"
                  className="btn btn-accent"
                  onClick={() => {
                    setView({ kind: 'manual-single-post' });
                  }}
                >
                  إضافة منشور يدوي
                </button>
              ) : null}
            </div>
          ) : null}
        </header>
      ) : null}

      {/*
        Task 107A — view switcher promoted to `.tabs` (per
        design_handoff/mvp/client-workspace.html). Each entry
        keeps the same onClick handler the pre-107A chips used,
        so screen state + downstream rendering is byte-identical.
      */}
      {view.kind === 'list' ? (
        <nav className="tabs" aria-label="عرض المحتوى">
          <button
            type="button"
            className={screen === 'list' ? 'tab active' : 'tab'}
            onClick={() => setScreen('list')}
          >
            قائمة
          </button>
          <button
            type="button"
            className={screen === 'calendar' ? 'tab active' : 'tab'}
            onClick={() => setScreen('calendar')}
          >
            تقويم
          </button>
          {hasClientCategoryRole ? (
            <button
              type="button"
              className={screen === 'inbox' ? 'tab active' : 'tab'}
              onClick={() => setScreen('inbox')}
            >
              صندوق الموافقات
            </button>
          ) : null}
        </nav>
      ) : null}

      {view.kind === 'mode-chooser' ? (
        <ModeChooserCard
          form={aiForm}
          onChange={setAiForm}
          onCancel={() => {
            setView({ kind: 'list' });
            setAiError(null);
          }}
          onGenerate={onGenerateAi}
          error={aiError}
          entryCountReason={aiEntryCountReason}
        />
      ) : null}

      {view.kind === 'manual-single-post' && clientId ? (
        <ManualSinglePostCard
          clientId={clientId}
          onCancel={() => setView({ kind: 'list' })}
          onSaved={(contentItemId) => {
            // Round 105A — reload + select the freshly-created item.
            setSelectedId(contentItemId);
            setReloadKey((n) => n + 1);
            setCalendarReloadKey((n) => n + 1);
            setView({ kind: 'list' });
          }}
        />
      ) : null}

      {view.kind === 'generating' ? (
        <div className="card" style={{ padding: 22 }}>
          <LoadingState title="جاري توليد الكالندر بالذكاء الاصطناعي…" />
          <p className="tiny" style={{ color: 'var(--muted)', marginTop: 8 }}>
            هذا يأخذ عادةً من 30 ثانية إلى دقيقتين. لا تُغلق الصفحة. لن تُحفظ أي بيانات حتى تضغط
            «حفظ».
          </p>
        </div>
      ) : null}

      {view.kind === 'draft' ? (
        <DraftEditor
          state={view.draft}
          onChange={(next) =>
            setView({ kind: 'draft', draft: next, saveError: null, saving: view.saving })
          }
          onCancel={() => {
            setView({ kind: 'list' });
            setAiError(null);
          }}
          onSave={onSaveDraft}
          saveError={view.saveError}
          saving={view.saving}
        />
      ) : null}

      {view.kind === 'list' && screen === 'calendar' ? (
        <CalendarView
          state={calendar}
          month={calendarMonth}
          timezone={calendarTimezone}
          canEdit={canSaveDraft}
          canSeeOrigin={canSeeContentOrigin}
          viewerIsClient={viewerIsClient}
          onMonthChange={setCalendarMonth}
          onTimezoneChange={setCalendarTimezone}
          onReload={() => setCalendarReloadKey((n) => n + 1)}
          onSelect={(itemId) => {
            setSelectedId(itemId);
            setScreen('list');
          }}
          onEdit={openEditModal}
          creativeAssetsByTarget={creativeAssetsByTarget}
        />
      ) : null}

      {view.kind === 'list' && screen === 'inbox' && hasClientCategoryRole ? (
        <div className="client-inbox">
          {/* Task 104B — sub-tabs inside the inbox to switch between
              the pre-production approval queue (step 2) and the final
              produced-asset approval queue (step 5). The sub-stage
              also drives `onInboxApprove`'s transition target.
              Task 107E — restyled as a proper tab strip (not filter
              chips) so the two-stage flow is unambiguous. */}
          <div className="client-inbox__tabs" role="tablist" aria-label="مرحلة الموافقة">
            <button
              type="button"
              role="tab"
              aria-selected={inboxStage === 'content'}
              className={
                inboxStage === 'content' ? 'client-inbox__tab is-active' : 'client-inbox__tab'
              }
              onClick={() => setInboxStage('content')}
            >
              موافقة المحتوى
              <span className="client-inbox__tab-hint">قبل الإنتاج</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={inboxStage === 'asset'}
              className={
                inboxStage === 'asset' ? 'client-inbox__tab is-active' : 'client-inbox__tab'
              }
              onClick={() => setInboxStage('asset')}
            >
              موافقة الأصل النهائي
              <span className="client-inbox__tab-hint">بعد الإنتاج</span>
            </button>
          </div>
          <ClientApprovalInbox
            state={inbox}
            canDecide={canDecideClientApproval}
            inFlight={transitionInFlight}
            error={transitionError}
            stage={inboxStage}
            onApprove={onInboxApprove}
            onRequestChanges={onInboxRequestChanges}
            onReload={() => setInboxReloadKey((n) => n + 1)}
            creativeAssetsByTarget={creativeAssetsByTarget}
            rowViewerIsClient={viewerIsClient}
          />
        </div>
      ) : null}

      {/*
        Task 107A — filter row redesign. Two grouped chip rows
        (status + discipline) sit under the page tabs. Each group
        gets a small `.lbl` (uppercase tracked label) so the chip
        intent is clear at a glance, matching the design system's
        section-label pattern. Behaviour, handlers, filter values,
        and the success-banner clearing rule are all preserved.
      */}
      {view.kind === 'list' && screen === 'list' ? (
        <div className="content-filters">
          {/* Task 290B — client viewers get the five PUBLIC status
              groups; the agency chip row (full internal vocabulary)
              is unchanged and never renders for clients. */}
          {viewerIsClient ? (
            <div className="content-filter-group">
              <span className="lbl">الحالة</span>
              <div className="toolbar-row" style={{ marginBottom: 0 }}>
                {CLIENT_STATUS_GROUPS.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    className={clientStatusGroup === g.value ? 'chip on' : 'chip'}
                    onClick={() => setClientStatusGroup(g.value)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="content-filter-group">
              <span className="lbl">الحالة</span>
              <div className="toolbar-row" style={{ marginBottom: 0 }}>
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    className={statusFilter === f.value ? 'chip on' : 'chip'}
                    onClick={() => {
                      // Task 101C — clear the production-handoff success
                      // banner whenever the user changes filter context.
                      // The banner is bound to the previous workflow
                      // moment; carrying it across a different filter is
                      // misleading.
                      if (f.value !== statusFilter) setTransitionSuccess(null);
                      setStatusFilter(f.value);
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Task 286G — the discipline filter is internal routing tooling; hidden
              from clients. */}
          {viewerIsClient ? null : (
            <div className="content-filter-group">
              <span className="lbl">التخصص</span>
              <div className="toolbar-row" style={{ marginBottom: 0 }}>
                {DISCIPLINE_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    className={disciplineFilter === f.value ? 'chip on' : 'chip'}
                    onClick={() => {
                      // Task 101C — same rationale as the status-filter
                      // chips: clear the success banner on filter context
                      // change.
                      if (f.value !== disciplineFilter) setTransitionSuccess(null);
                      setDisciplineFilter(f.value);
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/*
       * Task 101C — page-level production-handoff success banner.
       * Sits above the workspace split so it survives the auto-
       * selection retarget that happens when the transitioned row
       * leaves the current filter (e.g. moves out of
       * `client_content_approved`). Codex round 2 P2 fix replaced
       * the item-scoped inline banner inside ContentDetail with
       * this page-level slot. Cleared on (a) the next transition
       * (start of `runTransition`), (b) any filter chip change.
       * Visible only on the list screen because the production CTA
       * lives on the list-screen detail panel.
       */}
      {view.kind === 'list' && screen === 'list' && transitionSuccess ? (
        <div
          className="banner banner-success"
          role="status"
          style={{ marginTop: 12, marginBottom: 12 }}
        >
          {transitionSuccess}
        </div>
      ) : null}

      {view.kind === 'list' && screen === 'list' && list.status === 'loading' ? (
        <LoadingState title="جاري تحميل المحتوى…" />
      ) : null}
      {view.kind === 'list' && screen === 'list' && list.status === 'not-found' ? (
        <NotFoundState
          title="لا يمكن الوصول إلى محتوى هذا العميل"
          description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح محتواه."
        />
      ) : null}
      {view.kind === 'list' && screen === 'list' && list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل المحتوى"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {view.kind === 'list' && screen === 'list' && list.status === 'ready' ? (
        // Task 290B — client viewers see the RLS-scoped list filtered
        // by their selected PUBLIC group (client-side only; the
        // fetched data set is identical). Agency viewers render the
        // raw list exactly as before.
        (viewerIsClient
          ? list.items.filter((i) => itemMatchesClientGroup(i.status, clientStatusGroup))
          : list.items
        ).length === 0 ? (
          <EmptyState
            title="لا يوجد محتوى مطابق"
            description={
              viewerIsClient
                ? 'لا يوجد محتوى في هذه المجموعة حالياً.'
                : 'جرّب فلتر آخر، أو ابدأ بإضافة أول قطعة محتوى للعميل.'
            }
            // Task 107A — when the operator has create permission,
            // surface the manual-post action inline on the empty
            // state so they don't have to scroll back up to the
            // page header.
            action={
              canSaveDraft ? (
                <button
                  type="button"
                  className="btn btn-accent btn-sm"
                  onClick={() => setView({ kind: 'manual-single-post' })}
                >
                  إضافة منشور يدوي
                </button>
              ) : null
            }
          />
        ) : (
          <div className="workspace-split">
            <aside className="workspace-list">
              <div className="sec-title">
                <h2>قائمة المحتوى</h2>
                <span className="meta">
                  {
                    (viewerIsClient
                      ? list.items.filter((i) =>
                          itemMatchesClientGroup(i.status, clientStatusGroup),
                        )
                      : list.items
                    ).length
                  }
                </span>
              </div>
              <ul className="row-list">
                {(viewerIsClient
                  ? list.items.filter((i) => itemMatchesClientGroup(i.status, clientStatusGroup))
                  : list.items
                ).map((item) => {
                  // Task 070 — surface the latest client request-
                  // changes decision when the item is sitting in
                  // draft because of it. The status guard keeps the
                  // badge off items that have moved forward; the
                  // helper checks that the latest approval row is a
                  // client request-changes decision.
                  const latestRequestChanges =
                    item.status === 'draft'
                      ? latestClientChangesRequest(item.content_approvals)
                      : null;
                  const reasonShort = truncateReason(latestRequestChanges?.reason ?? null);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={
                          item.id === selectedId
                            ? 'row-card row-card-active content-list-card'
                            : 'row-card content-list-card'
                        }
                        aria-current={item.id === selectedId ? 'true' : undefined}
                        onClick={() => setSelectedId(item.id)}
                      >
                        <div className="content-list-card__media">
                          <MediaPreviewSlot
                            platforms={[]}
                            discipline={item.discipline}
                            clientSafe={viewerIsClient}
                            size="sm"
                            state={mediaStateFromAsset(
                              creativeAssetsByTarget.get(`content_item:${item.id}`),
                            )}
                          />
                        </div>
                        <div className="content-list-card__body">
                          <div className="content-list-card__head">
                            <strong className="content-list-card__title">{item.title}</strong>
                            {/* Task 290B — clients see the PUBLIC group
                                label; unmapped statuses render no pill
                                (fail-closed). Agency unchanged. */}
                            {viewerIsClient ? (
                              clientPublicStatusLabel(item.status) ? (
                                <span
                                  className={`pill pill-${clientPublicStatusVariant(item.status)}`}
                                >
                                  {clientPublicStatusLabel(item.status)}
                                </span>
                              ) : null
                            ) : (
                              <span className={`pill pill-${contentStatusVariant(item.status)}`}>
                                {contentStatusLabel(item.status)}
                              </span>
                            )}
                          </div>
                          <div className="content-list-card__chips">
                            {/* Task 286G — discipline is internal routing metadata;
                                hidden from clients (origin already gated). */}
                            {viewerIsClient ? null : (
                              <span className="chip chip-soft">
                                {disciplineLabel(item.discipline)}
                              </span>
                            )}
                            {canSeeContentOrigin ? (
                              <span className="chip chip-ghost">{originLabel(item.origin)}</span>
                            ) : null}
                            <span className="chip chip-ghost">
                              {new Date(item.updated_at).toLocaleDateString('ar-SA')}
                            </span>
                          </div>
                          {latestRequestChanges ? (
                            <div className="content-list-card__alert">
                              <span className="pill pill-review">طلب العميل تعديلات</span>
                              {reasonShort ? (
                                <p className="content-list-card__reason">{reasonShort}</p>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
            <section className="workspace-detail">
              <ContentDetail
                state={detail}
                isLeadership={isLeadership}
                canCreate={canSaveDraft}
                canInternalReviewBase={canInternalReviewBase}
                canApproveContentDiscipline={canApproveContentDiscipline}
                canApproveDesignDiscipline={canApproveDesignDiscipline}
                canDecideClientApproval={canDecideClientApproval}
                canSeeOrigin={canSeeContentOrigin}
                viewerIsClient={viewerIsClient}
                canStartProduction={canStartProduction}
                canDirectManualClientHandoff={canDirectManualClientHandoff}
                canSendToWriting={canSendToWriting}
                canApproveAndSendToClient={canApproveAndSendToClient}
                onTransition={runTransition}
                transitionInFlight={transitionInFlight}
                transitionError={transitionError}
                productionTask={productionTask}
                clientFallbackDeliverable={clientFallbackDeliverable}
                productionTaskInFlight={productionTaskInFlight}
                productionTaskError={productionTaskError}
                productionTaskSuccess={productionTaskSuccess}
                currentUserId={currentUserId}
                canStartProductionPerm={
                  /* Codex P2 (round 11) — combine the global allowed-
                   * disciplines set with the displayed item's discipline.
                   * If we don't have a ready item or the user lacks the
                   * matching discipline perm, the panel falls back to the
                   * "no permission" message instead of showing buttons
                   * that would 4xx at the backend. */
                  detail.status === 'ready' &&
                  productionDeliveryAllowedDisciplines.has(detail.item.discipline)
                }
                canManageProductionRouting={canManageProductionRouting}
                onProductionRoutingReload={() => setProductionTaskReloadKey((k) => k + 1)}
                clientIdForRouting={clientId ?? null}
                onStartProductionTask={handleStartProductionTask}
                onSubmitProductionDeliverable={handleSubmitProductionDeliverable}
                creativeAssetsByTarget={creativeAssetsByTarget}
                canManageCreativeAttachments={canManageCreativeAttachments}
                onAttachCreativeAsset={handleAttachCreativeAsset}
                onRemoveCreativeAsset={handleRemoveCreativeAsset}
                publishJobsState={publishJobsState}
                publishInFlight={publishInFlight}
                publishError={publishError}
                canSchedulePublishing={canSchedulePublishing}
                onOpenScheduleModal={handleOpenScheduleModal}
                onMarkPublishJobPublished={handleMarkPublishJobPublished}
                onMarkPublishJobFailed={handleMarkPublishJobFailed}
                onCancelPublishJob={handleCancelPublishJob}
              />
            </section>
          </div>
        )
      ) : null}

      {scheduleModal ? (
        <SchedulePublishingModal
          state={scheduleModal}
          onChange={(next) => setScheduleModal(next)}
          onCancel={() => setScheduleModal(null)}
          onSave={handleSubmitScheduleModal}
        />
      ) : null}

      {requestChangesModal ? (
        <RequestChangesModal
          state={requestChangesModal}
          onChange={(next) => setRequestChangesModal(next)}
          onCancel={() => setRequestChangesModal(null)}
          onSave={handleSubmitRequestChangesModal}
        />
      ) : null}

      {editModal ? (
        <EditDraftModal
          state={editModal}
          onChange={(next) => setEditModal(next)}
          onCancel={() => setEditModal(null)}
          onSave={onSubmitEditModal}
        />
      ) : null}
    </div>
  );
}

interface ContentDetailProps {
  readonly state: DetailState;
  readonly isLeadership: boolean;
  readonly canCreate: boolean;
  readonly canInternalReviewBase: boolean;
  readonly canApproveContentDiscipline: boolean;
  readonly canApproveDesignDiscipline: boolean;
  readonly canDecideClientApproval: boolean;
  // Task 277 — show the AI-origin label only to agency-category viewers.
  readonly canSeeOrigin: boolean;
  // Task 286G — a restricted CLIENT-category viewer (client role AND not agency).
  // When true, ContentDetail renders a strictly client-safe presentation: the
  // shared card switches to mode="client" and every internal-only panel (status
  // timeline, internal idea body, production task, transition actions, publish
  // jobs, internal approvals log) is hidden. Agency viewers (false) are unchanged.
  readonly viewerIsClient: boolean;
  // Task 101C — production-handoff CTA gate (mirrors v2 RPC).
  readonly canStartProduction: boolean;
  // Task 105A — trusted manual content direct-handoff CTA gate.
  readonly canDirectManualClientHandoff: boolean;
  // Task 292C1 — «إرسال للكتابة» gate (mirrors app.require_writing_handoff_authz).
  readonly canSendToWriting: boolean;
  // Task 160 — one-click "approve internal + send to client" gate.
  readonly canApproveAndSendToClient: boolean;
  readonly onTransition: (target: ContentItemTransitionTarget, reason: string) => Promise<void>;
  readonly transitionInFlight: boolean;
  readonly transitionError: string | null;
  // Task 101C note — the success note for the production-handoff
  // edge is rendered at the PAGE level (above the workspace split),
  // not inside ContentDetail. See the runTransition handler comment
  // for the full reasoning. ContentDetail therefore does not carry
  // a `transitionSuccess` prop.

  // Task 101D — production-task panel data + lifecycle callbacks.
  // `productionTask` is the (at most one) non-cancelled task linked
  // to the displayed item, fetched by the page from
  // `list-production-tasks-for-content-item`. Null when the item is
  // not in_production OR the caller has no read access to the task.
  // The `onProductionTask*` callbacks bubble UI actions back to the
  // page, which routes them through the dedicated edge functions
  // (start, prepare-upload, submit) — direct PostgREST writes from
  // the FE are explicitly NOT used.
  readonly productionTask: ProductionTaskRow | null;
  // Task 175 PR-A · Issue 1 — fallback deliverable loaded from the
  // client inbox EF for client-category viewers (the primary
  // production-task loader is RLS-blocked for them). Non-null only on
  // asset-stage items when the inbox row carries a deliverable.
  readonly clientFallbackDeliverable: PendingClientApprovalDeliverable | null;
  readonly productionTaskInFlight: boolean;
  readonly productionTaskError: string | null;
  readonly productionTaskSuccess: string | null;
  readonly currentUserId: string | null;
  readonly canStartProductionPerm: boolean;
  // Task 117 / TEAM-2 PR-2 — production-routing caller gate (locked
  // to owner / marketing_manager / production_manager-with-CAT). When
  // true, ProductionTaskPanel renders the manual assign/reassign
  // affordance via <ProductionRoutingControls>.
  readonly canManageProductionRouting: boolean;
  /** Bubble a request to reload the production task after an assign /
   *  reassign succeeds. The page bumps `productionTaskReloadKey` so the
   *  panel's assignee chip picks up the new state. */
  readonly onProductionRoutingReload: () => void;
  /** The parent client id for the routing dialog's empty-state CTA
   *  (`/app/clients/<id>/team`). Null when the page hasn't resolved a
   *  client yet — the dialog suppresses the CTA in that case. */
  readonly clientIdForRouting: string | null;
  readonly onStartProductionTask: (taskId: string) => Promise<void>;
  readonly onSubmitProductionDeliverable: (args: {
    readonly taskId: string;
    readonly file: File;
    readonly title: string;
    readonly summary: string;
  }) => Promise<boolean>;
  // Task 073 — creative asset rendering + upload.
  readonly creativeAssetsByTarget: Map<string, CreativeAssetCardRow>;
  readonly canManageCreativeAttachments: boolean;
  readonly onAttachCreativeAsset: (args: {
    target_source: CreativeAssetTargetSource;
    target_id: string;
    file: File;
  }) => Promise<void>;
  // Task 076 — remove an existing creative attachment.
  readonly onRemoveCreativeAsset: (args: {
    target_source: CreativeAssetTargetSource;
    target_id: string;
    creative_asset_id: string;
  }) => Promise<void>;
  // Task 104C — publish workflow surface. The page-level state owns
  // the per-item job list + 4 mutation callbacks (schedule open,
  // mark-published, mark-failed, cancel). The detail panel just
  // renders the PublishJobsPanel for items at status in
  // (client_asset_approved | scheduled | published).
  // Round 3: the schedule open callback now takes the set of
  // available platforms (computed inside the detail panel from
  // loaded variants) so the modal can disable platforms that have
  // no matching variant.
  readonly publishJobsState: PublishJobsLoadState;
  readonly publishInFlight: boolean;
  readonly publishError: string | null;
  readonly canSchedulePublishing: boolean;
  readonly onOpenScheduleModal: (
    contentItemId: string,
    availablePlatforms: ReadonlySet<PublishWorkflowPlatform>,
    plannedScheduledFor: { iso: string; timezone: string | null } | null,
  ) => void;
  readonly onMarkPublishJobPublished: (jobId: string) => Promise<void>;
  readonly onMarkPublishJobFailed: (jobId: string) => Promise<void>;
  readonly onCancelPublishJob: (jobId: string) => Promise<void>;
}

function ContentDetail(props: ContentDetailProps) {
  const { state } = props;
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر قطعة محتوى لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل تفاصيل المحتوى…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="القطعة غير متاحة" description="ربما تم أرشفتها." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل المحتوى" error={state.error} />;

  const { item, variants, approvals } = state;
  // Task 286G — restricted client viewer: render a strictly client-safe detail
  // (client-mode card only; every internal panel below is hidden). Agency
  // viewers (false) keep the full internal detail unchanged.
  const viewerIsClient = props.viewerIsClient;
  // Task 104C round 3 — derive the set of publish-workflow platforms
  // the operator can actually schedule against. The schedule RPC's
  // canonical mapping is one-to-one (instagram→instagram_feed,
  // tiktok→tiktok_video, snapchat→snapchat_story); a variant for a
  // non-canonical instagram_story / instagram_reel does NOT enable
  // 'instagram' until the RPC is widened. The schedule modal uses
  // this set to disable platforms that have no matching variant.
  const availablePublishPlatforms = collectAvailablePublishPlatforms(variants);
  // Task 105A — earliest non-archived calendar_entries.scheduled_for
  // across all variants. The publish schedule modal prefills from
  // this when present so the operator's intended publish time is
  // proposed by default; falls back to tomorrow 10:00 only if no
  // calendar entry exists.
  const plannedScheduledFor = pickEarliestPlannedScheduledFor(variants);
  return (
    <div className="stack-md">
      {/*
        Task 107B — Detail view header redesign.
        Drops the pre-107B `content_items` meta tag (English table
        name leaked to the operator) and the 4-field grid in favour
        of a tighter header: title + inline status pill + secondary
        meta line (المصدر / التخصص / آخر تحديث). The body text
        moves into its own card below the header so the header
        stays scannable for status at a glance.
      */}
      <header className="content-detail-header card">
        <div className="content-detail-header__row">
          <h2 className="content-detail-header__title">{item.title}</h2>
          {/* Task 290B — public group label for clients (fail-closed
              null for unmapped); agency unchanged. */}
          {viewerIsClient ? (
            clientPublicStatusLabel(item.status) ? (
              <span className={`pill pill-${clientPublicStatusVariant(item.status)}`}>
                {clientPublicStatusLabel(item.status)}
              </span>
            ) : null
          ) : (
            <span className={`pill pill-${contentStatusVariant(item.status)}`}>
              {contentStatusLabel(item.status)}
            </span>
          )}
        </div>
        <div className="content-detail-header__meta">
          {props.canSeeOrigin ? (
            <>
              <span className="lbl">المصدر</span>
              <span>{originLabel(item.origin)}</span>
              <span className="content-detail-header__sep" aria-hidden="true">
                ·
              </span>
            </>
          ) : null}
          {/* Task 286G — discipline is internal routing metadata; hidden from clients. */}
          {viewerIsClient ? null : (
            <>
              <span className="lbl">التخصص</span>
              <span>{disciplineLabel(item.discipline)}</span>
              <span className="content-detail-header__sep" aria-hidden="true">
                ·
              </span>
            </>
          )}
          <span className="lbl">آخر تحديث</span>
          <span>{new Date(item.updated_at).toLocaleString('ar-SA')}</span>
        </div>
      </header>

      {/*
        Task 107B — Status timeline. Linear 9-step indicator
        showing the operator where this item sits in the workflow.
        Future steps muted, current highlighted (tangerine), past
        steps marked done (sage). Direct manual handoff items skip
        steps 2 and 3 — the timeline still highlights them as
        "future" rather than "done" so the operator can read it as
        "what's left" without being told false history. The audit
        log card below tells the real history.
      */}
      {/* Task 286G — the workflow status timeline exposes internal review/production
          stages; hidden from clients. */}
      {viewerIsClient ? null : <ContentStatusTimeline status={item.status} />}

      {/* Task 286G — `item.body` is the INTERNAL idea summary ("الفكرة الأساسية",
          not published); never shown to clients. */}
      {!viewerIsClient && item.body ? (
        <div className="card" style={{ padding: 22 }}>
          <div className="sec-title">
            <h2>النص الرئيسي</h2>
          </div>
          <p className="body" style={{ marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {item.body}
          </p>
        </div>
      ) : null}

      {/*
       * Task 101D — production-task panel.
       * Renders ONLY when (a) the displayed item is in_production
       * and (b) a non-cancelled task exists for the item. The panel
       * itself decides whether to show start / upload / submit
       * controls based on the active assignee + permission gate.
       */}
      {/*
       * Codex P2 (round 9) — gate the panel render on
       * `task.source_id === item.id`. The page-level loader effect
       * clears `productionTask` on detail change, but the clear runs
       * AFTER the next render (effects fire post-commit). In the
       * window between the parent re-rendering with a new item and
       * the effect firing, this conditional would otherwise render
       * the OLD panel under the NEW item; clicking Start/Submit in
       * that window would target the wrong task. The id match shrinks
       * the window to zero — mismatched panels never render.
       */}
      {/* Task 286G — the production-task panel (assignments, brief, deliverable
          workflow) is internal; hidden from clients. */}
      {!viewerIsClient && props.productionTask && props.productionTask.source_id === item.id ? (
        <ProductionTaskPanel
          task={props.productionTask}
          inFlight={props.productionTaskInFlight}
          error={props.productionTaskError}
          success={props.productionTaskSuccess}
          currentUserId={props.currentUserId}
          canStartProductionPerm={props.canStartProductionPerm}
          isLeadership={props.isLeadership}
          canManageProductionRouting={props.canManageProductionRouting}
          onProductionRoutingReload={props.onProductionRoutingReload}
          clientIdForRouting={props.clientIdForRouting}
          onStart={props.onStartProductionTask}
          onSubmit={props.onSubmitProductionDeliverable}
          variants={variants}
        />
      ) : null}

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>نسخ المنصات</h2>
          <span className="meta">{variants.length} منصة</span>
        </div>
        {variants.length === 0 ? (
          <EmptyState
            title="لا توجد متغيرات"
            description="ستظهر هنا نسخ خاصة بكل منصة بمجرد إنشائها."
          />
        ) : (
          // Task 286A — render saved variants through the SHARED content-review
          // card (read-only; the same component as the AI draft review), replacing
          // the cramped per-platform grid. The per-variant media + attach workflow
          // is injected via the mediaSlot keyed to the selected variant; the
          // deliverable precedence + slot labels are the EXISTING logic, verbatim.
          <IdeaReviewCard
            key={state.item.id}
            mode={viewerIsClient ? 'client' : 'saved'}
            idx={0}
            item={
              viewerIsClient
                ? savedItemToClientReviewItem(state.item, variants)
                : savedItemToReviewItem(state.item, variants)
            }
            // Task 286J — defense in depth: the internal workflow status label is
            // never even PASSED into the client card's props (the card also gates
            // the pill on mode !== 'client').
            statusPill={
              viewerIsClient
                ? undefined
                : {
                    label: contentStatusLabel(state.item.status),
                    variant: contentStatusVariant(state.item.status),
                  }
            }
            mediaSlot={(rv) => {
              const v = variants.find((cv) => cv.platform === rv.platform);
              if (!v) return null;
              const productionTaskDeliverable =
                props.productionTask?.source_id === state.item.id
                  ? (props.productionTask.latest_production_deliverable ?? null)
                  : null;
              const deliverable: {
                readonly mime_type: string | null;
                readonly signed_url: string;
              } | null = productionTaskDeliverable ?? props.clientFallbackDeliverable ?? null;
              const deliverableState = mediaStateFromDeliverable(deliverable);
              const creativeState = mediaStateFromAsset(
                props.creativeAssetsByTarget.get(`content_variant:${v.id}`),
              );
              const slotState = deliverableState ?? creativeState;
              const reviewLabel = platformCardReviewLabel(
                state.item.status,
                state.item.discipline,
                deliverable !== null,
              );
              const slotLabel =
                reviewLabel ??
                mediaRequiredAssetStatusLabel(
                  v.platform,
                  state.item.discipline,
                  state.item.status,
                  deliverable !== null,
                );
              const isReviewContext = isReviewContextStatus(state.item.status);
              return (
                <>
                  <MediaPreviewSlot
                    platforms={[v.platform]}
                    discipline={state.item.discipline}
                    clientSafe={viewerIsClient}
                    size="md"
                    state={slotState}
                    label={slotLabel}
                  />
                  {/* Task 286G — the asset-hint is internal production guidance
                      (attach / send-to-production / waiting-on-production); hidden
                      from clients. */}
                  {!viewerIsClient && slotLabel === 'أصل مطلوب' ? (
                    <p className="variant-card__asset-hint">
                      {props.canManageCreativeAttachments && !isReviewContext
                        ? 'أرفق التصميم أو الفيديو من زر الإرفاق بالأسفل.'
                        : state.item.status === 'client_content_approved' &&
                            props.canStartProduction
                          ? 'أرسِل العنصر للإنتاج من «الإجراءات المتاحة».'
                          : 'بانتظار إرفاق الأصل من فريق الإنتاج.'}
                    </p>
                  ) : null}
                  {props.canManageCreativeAttachments && !isReviewContext ? (
                    <div className="variant-card__attach-actions">
                      <AttachCreativeAssetButton
                        targetSource="content_variant"
                        targetId={v.id}
                        accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                        label={
                          props.creativeAssetsByTarget.has(`content_variant:${v.id}`)
                            ? 'استبدال التصميم/الفيديو'
                            : 'إرفاق تصميم/فيديو'
                        }
                        onAttach={props.onAttachCreativeAsset}
                      />
                      {props.creativeAssetsByTarget.has(`content_variant:${v.id}`) ? (
                        <RemoveCreativeAssetButton
                          targetSource="content_variant"
                          targetId={v.id}
                          creativeAssetId={
                            props.creativeAssetsByTarget.get(`content_variant:${v.id}`)!.id
                          }
                          onRemove={props.onRemoveCreativeAsset}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </>
              );
            }}
          />
        )}
      </div>

      {/*
        Task 107B — extracted TransitionPanel into a standalone
        "الإجراءات المتاحة" card so it isn't visually buried at
        the bottom of the audit log. Behaviour unchanged: same
        TransitionPanel component, same props, same handlers.
      */}
      {/* Task 286G — internal workflow transition actions are agency-only; hidden
          from clients (client approval happens in the dedicated approval inbox). */}
      {viewerIsClient ? null : (
        <div className="card content-detail-actions" style={{ padding: 22 }}>
          <div className="sec-title">
            <h2>الإجراءات المتاحة</h2>
          </div>
          {/*
           * Task 101A → 101C — honest dead-end note, narrowed.
           *
           * Backend (Task 101B) is now live: an authorised agency caller
           * CAN move `client_content_approved → in_production`. So the
           * banner only fires for callers who lack the production-handoff
           * gate AND are looking at an item that is genuinely stuck for
           * them. Callers who pass `canStartProduction` see the
           * "إرسال للإنتاج" CTA from `transitionsForStatus` instead.
           */}
          {item.status === 'client_content_approved' && !props.canStartProduction ? (
            <div className="banner banner-info" role="note" style={{ marginBottom: 14 }}>
              تم اعتماد النص/الفكرة. لا تملك صلاحية بدء الإنتاج.
            </div>
          ) : null}
          {/*
           * Task 101C — the production-handoff success banner is
           * rendered at PAGE level (above the workspace split), not
           * inside this component. See `runTransition` and the
           * page-level <ProductionHandoffSuccessBanner> render in
           * ContentPage. Codex round 2 P2 fix — an item-scoped banner
           * here would silently drop the message in the very common
           * workflow where the transitioned row leaves the current
           * filter and the auto-selection retargets to a sibling.
           */}
          <TransitionPanel
            item={item}
            isLeadership={props.isLeadership}
            canCreate={props.canCreate}
            canInternalReviewBase={props.canInternalReviewBase}
            canApproveContentDiscipline={props.canApproveContentDiscipline}
            canApproveDesignDiscipline={props.canApproveDesignDiscipline}
            canDecideClientApproval={props.canDecideClientApproval}
            canStartProduction={props.canStartProduction}
            canDirectManualClientHandoff={props.canDirectManualClientHandoff}
            canApproveAndSendToClient={props.canApproveAndSendToClient}
            onTransition={props.onTransition}
            inFlight={props.transitionInFlight}
            error={props.transitionError}
          />
          {/*
           * Task 292C1 — the writing handoff is a SIBLING of TransitionPanel,
           * not one of its actions: it does not change content_items.status,
           * so it cannot be a ContentItemTransitionTarget. It stays inside the
           * `viewerIsClient ? null :` gate above, so clients never see it
           * (Task 286G invariant, pinned by clientPrivacy.286g.test.ts).
           */}
          <SendToWritingPanel
            contentItemId={item.id}
            canSend={props.canSendToWriting}
            itemStatus={item.status}
            itemDiscipline={item.discipline}
          />
        </div>
      )}

      {/*
        Task 104C — publish-workflow panel. Promoted to its own
        card by Task 107B (was previously nested at the bottom of
        the approvals card). Surfaces:
          * `client_asset_approved` → "Schedule publishing" CTA that
            opens the inline schedule modal (platforms / scheduled_for
            / per-platform caption / notes).
          * `scheduled` → per-platform job table with mark-published /
            mark-failed / cancel buttons.
          * `published` → read-only per-platform list (status + URLs).
        All four mutations route through dedicated edge functions;
        the panel never writes to PostgREST directly. The panel is
        page-level state-driven so a mutation triggers exactly one
        publish-jobs reload + one detail reload.
      */}
      {/* Task 286G — publishing/scheduling is agency-only; hidden from clients. */}
      {!viewerIsClient &&
      (item.status === 'client_asset_approved' ||
        item.status === 'scheduled' ||
        item.status === 'published') ? (
        <PublishJobsPanel
          item={item}
          availablePlatforms={availablePublishPlatforms}
          plannedScheduledFor={plannedScheduledFor}
          jobsState={props.publishJobsState}
          inFlight={props.publishInFlight}
          error={props.publishError}
          canSchedule={props.canSchedulePublishing}
          onOpenScheduleModal={props.onOpenScheduleModal}
          onMarkPublished={props.onMarkPublishJobPublished}
          onMarkFailed={props.onMarkPublishJobFailed}
          onCancel={props.onCancelPublishJob}
        />
      ) : null}

      {/* Task 286G — the internal approvals/audit log (review decisions, internal
          reasons, approver IDs) is hidden from clients. */}
      {viewerIsClient ? null : (
        <div className="card" style={{ padding: 22 }}>
          <div className="sec-title">
            <h2>سجلّ الموافقات</h2>
            <span className="meta">{approvals.length} قرار</span>
          </div>
          {approvals.length === 0 ? (
            <EmptyState
              title="لا توجد قرارات مراجعة بعد."
              description="ستظهر هنا قرارات الموافقة وطلبات التعديل من المراجعة الداخلية والعميل."
            />
          ) : (
            // Task 070 — endpoint returns desc; render as-is for newest-
            // first timeline. Reason wraps via `whiteSpace: pre-wrap`.
            <ul className="row-list">
              {approvals.map((a) => (
                <li key={a.id} className="row-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <strong>{approvalStageLabel(a.stage)}</strong>
                    <span className={`pill pill-${approvalDecisionVariant(a.decision)}`}>
                      {approvalDecisionLabel(a.decision)}
                    </span>
                  </div>
                  {a.reason ? (
                    <p
                      className="body"
                      style={{ marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {a.reason}
                    </p>
                  ) : null}
                  <div
                    className="tiny"
                    style={{
                      color: 'var(--muted)',
                      marginTop: 6,
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>{new Date(a.created_at).toLocaleString('ar-SA')}</span>
                    <span>· بواسطة {shortApproverId(a.approver_user_id)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Task 101D: production task panel ────────────────────────────────
//
// Inline panel that surfaces the linked production task on a content
// item that is `in_production`. Action buttons are gated to the
// active assignee with the production.asset.submit permission. All
// state changes route through dedicated edge functions; the panel
// never writes to PostgREST directly.

// Task 108B — Arabic platform label for the production brief read-out.
// Mirrors the inbox-scoped `inboxPlatformLabelAr` (107E) one-for-one
// so the production task panel doesn't fall back to the latin-prefixed
// `variantPlatformLabel` ("Instagram · منشور"). Keeping the helpers
// adjacent to their consumers keeps each task's pin file independent.
function productionTaskPlatformLabelAr(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'إنستجرام · منشور';
    case 'instagram_story':
      return 'إنستجرام · ستوري';
    case 'instagram_reel':
      return 'إنستجرام · ريل';
    case 'facebook_post':
      return 'فيسبوك · منشور';
    case 'facebook_story':
      return 'فيسبوك · ستوري';
    case 'tiktok_video':
      return 'تيك توك · فيديو';
    case 'snapchat_story':
      return 'سناب شات · ستوري';
    default:
      return platform;
  }
}

// Task 108B — ordered field lists for the read-only production brief.
// Each entry pairs the snake_case `platform_specific` key with the
// Arabic label (sourced from the 108A `BRIEF_LABELS_AR` constant so
// the manual-form labels and the production-task labels stay in lock
// step). The arrays are rendered in order; the renderer hides any
// field whose value is missing/empty so designers see only what was
// actually filled in.
const PRODUCTION_BRIEF_STATIC_FIELDS: ReadonlyArray<{
  readonly key: string;
  readonly label: string;
}> = [
  { key: 'on_image_text', label: BRIEF_LABELS_AR.onImageText },
  { key: 'visual_direction', label: BRIEF_LABELS_AR.visualDirection },
  { key: 'creative_brief', label: BRIEF_LABELS_AR.creativeBrief },
  { key: 'cta', label: BRIEF_LABELS_AR.cta },
  { key: 'asset_format', label: BRIEF_LABELS_AR.assetFormat },
];

const PRODUCTION_BRIEF_VIDEO_FIELDS: ReadonlyArray<{
  readonly key: string;
  readonly label: string;
}> = [
  { key: 'hook', label: BRIEF_LABELS_AR.hook },
  { key: 'script', label: BRIEF_LABELS_AR.script },
  { key: 'shot_list', label: BRIEF_LABELS_AR.shotList },
  { key: 'on_screen_text', label: BRIEF_LABELS_AR.onScreenText },
  { key: 'visual_direction', label: BRIEF_LABELS_AR.visualDirection },
  { key: 'audio_direction', label: BRIEF_LABELS_AR.audioDirection },
  { key: 'cta', label: BRIEF_LABELS_AR.cta },
  { key: 'duration_seconds', label: BRIEF_LABELS_AR.durationSeconds },
];

// Task 108B — pull a single brief field out of a variant's
// `platform_specific` jsonb and format it for read-only display.
// Returns `null` when the field is missing, empty, or zero so the
// renderer can skip the row entirely (the spec calls for hiding
// empty blocks instead of rendering blank rows).
//   - `asset_format` is mapped through `ASSET_FORMAT_OPTIONS_AR` so
//     the operator sees `صورة ثابتة` instead of the raw
//     `static_image` enum slug. Unmapped enum values fall through
//     to the raw value rather than being hidden — a future enum
//     addition stays visible (degraded label) instead of silently
//     vanishing.
//   - `duration_seconds` is rendered as `${n} ثانية` so the unit is
//     obvious at a glance; non-integer / out-of-range values fall
//     back to the raw `String(n)`.
//   - All other keys are treated as plain strings: trim, return
//     null if empty.
function readProductionBriefField(
  platformSpecific: Readonly<Record<string, unknown>> | null | undefined,
  key: string,
): string | null {
  if (!platformSpecific) return null;
  const raw = platformSpecific[key];
  if (raw === null || raw === undefined) return null;
  if (key === 'duration_seconds') {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    if (raw <= 0) return null;
    return Number.isInteger(raw) ? `${raw} ثانية` : String(raw);
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (key === 'asset_format') {
    const match = ASSET_FORMAT_OPTIONS_AR.find((opt) => opt.value === trimmed);
    return match ? match.label : trimmed;
  }
  return trimmed;
}

// Task 108B — does this variant have ANY production brief content?
// Used by the renderer to decide between the field list and the
// `لا يوجد موجز إنتاج لهذه النسخة بعد.` empty-state copy.
function variantHasAnyProductionBrief(variant: ContentVariantRow): boolean {
  const ps = variant.platform_specific;
  if (!ps) return false;
  const fields = isVideoPlatform(variant.platform)
    ? PRODUCTION_BRIEF_VIDEO_FIELDS
    : PRODUCTION_BRIEF_STATIC_FIELDS;
  for (const f of fields) {
    if (readProductionBriefField(ps, f.key) !== null) return true;
  }
  return false;
}

// Task 175 PR-A · Issue 3 — owner-aware permission deny copy. Default
// copy assumes the actor is non-leadership and can plausibly ask the
// owner for the missing perm. Leadership (owner / marketing_manager)
// are intentionally NOT seeded with production-execute perms
// (`production.asset.submit` / `task.{design,video,writing}.execute`)
// because "owners coordinate, not deliver" — verified in
// `permissions.ts:46-53` and the role seed at
// `20260423040000_identity_tenant_access.sql:1084-1157`. The default
// copy ("contact the owner to request the permission") is incoherent
// when the actor IS the owner, so render a leadership-specific copy
// that points to the routing affordance instead.
const PRODUCTION_DELIVERY_DENY_NONLEADERSHIP_MESSAGE_AR =
  'لا تملك صلاحية تسليم أصول الإنتاج. تواصل مع مالك الوكالة لطلب الصلاحية.';
// Codex Step A · P2 — role-neutral leadership phrasing. `isLeadership`
// is true for BOTH `owner` and `marketing_manager`; the prior
// `كصاحب الوكالة` ("as the agency owner") was misleading for marketing
// managers who are NOT the owner. `بصفتك من قيادة الوكالة` ("in your
// capacity as agency leadership") covers both roles without claiming
// owner status.
const PRODUCTION_DELIVERY_DENY_LEADERSHIP_MESSAGE_AR =
  'تسليم أصول الإنتاج مخصّص لأعضاء فريق التنفيذ. بصفتك من قيادة الوكالة، يمكنك إعادة تعيين المهمة لمصمم أو محرر بدل تسليمها بنفسك.';

interface ProductionTaskPanelProps {
  readonly task: ProductionTaskRow;
  readonly inFlight: boolean;
  readonly error: string | null;
  readonly success: string | null;
  readonly currentUserId: string | null;
  readonly canStartProductionPerm: boolean;
  // Task 175 PR-A · Issue 3 — branch the deny copy on leadership so
  // owners don't see the incoherent "contact the owner" message.
  readonly isLeadership: boolean;
  // Task 117 / TEAM-2 PR-2 — production-routing caller gate. When
  // true, ProductionRoutingControls is mounted and the manual
  // assign/reassign affordance becomes visible. Server-side
  // `assign_production_task_v1` enforces the same gate.
  readonly canManageProductionRouting: boolean;
  readonly onProductionRoutingReload: () => void;
  readonly clientIdForRouting: string | null;
  readonly onStart: (taskId: string) => Promise<void>;
  readonly onSubmit: (args: {
    readonly taskId: string;
    readonly file: File;
    readonly title: string;
    readonly summary: string;
  }) => Promise<boolean>;
  // Task 108B — read-only access to the item's variants so the panel
  // can render the production brief (`موجز الإنتاج`) per platform.
  // The panel never mutates these; the brief stays read-only inside
  // the production task surface (editing happens earlier in the
  // manual form / AI draft review screen).
  readonly variants: ReadonlyArray<ContentVariantRow>;
}

function productionTaskStatusLabel(status: string): string {
  switch (status) {
    case 'open':
      return 'مفتوحة';
    case 'in_progress':
      return 'قيد التنفيذ';
    case 'blocked':
      return 'متوقفة';
    case 'submitted':
      return 'مُسلَّمة للمراجعة';
    case 'completed':
      return 'مكتملة';
    case 'cancelled':
      return 'ملغاة';
    default:
      return status;
  }
}

function productionTaskStatusVariant(status: string): string {
  switch (status) {
    case 'in_progress':
      return 'review';
    case 'submitted':
      return 'info';
    case 'completed':
      return 'active';
    case 'blocked':
      return 'error';
    case 'cancelled':
      return 'archived';
    default:
      return 'draft';
  }
}

function ProductionTaskPanel(props: ProductionTaskPanelProps) {
  const { task } = props;
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');

  // Active-assignee gate. The backend RPCs re-check this; the panel
  // hides action buttons for non-assignees so they can still SEE the
  // task progress without misleading affordances.
  const isAssignee =
    !!props.currentUserId &&
    !!task.active_assignee_user_id &&
    task.active_assignee_user_id === props.currentUserId;
  // Task 101D Codex P1 (round 5) — unassigned production tasks need
  // a claim affordance. The 101B handoff creates production tasks
  // with `active_assignee_user_id === null` and defers assignment;
  // `start_production_task_v1` is the self-claim point. Without
  // showing the start button to the unassigned case, the panel is
  // dead-on-arrival in the FE even though the backend now supports
  // self-claim. Submit / upload still require IS-assignee because by
  // then the start call has populated the assignment.
  //
  // Codex 101D-2 P2: "unclaimed" means BOTH the human assignee AND
  // the AI worker assignment are null. An AI-assigned task has
  // `active_assignee_user_id === null` (humans only) but
  // `active_ai_worker_id !== null` — the start RPC would reject a
  // human self-claim attempt against it ("task is assigned to
  // another user"). Treating that case as `isUnclaimed=false`
  // hides the claim button and avoids the avoidable 4xx.
  const isAiAssigned = !!task.active_ai_worker_id;
  const isUnclaimed = !task.active_assignee_user_id && !task.active_ai_worker_id;
  const canStartArea = props.canStartProductionPerm && (isAssignee || isUnclaimed);
  const canSubmitArea = props.canStartProductionPerm && isAssignee;
  const canActAtAll = canStartArea || canSubmitArea;
  const canStart =
    canStartArea && !props.inFlight && (task.status === 'open' || task.status === 'blocked');
  const canSubmit =
    canSubmitArea &&
    !props.inFlight &&
    task.status === 'in_progress' &&
    file !== null &&
    title.trim().length > 0;

  const onPickFile = (e: { readonly target: { readonly files: FileList | null } }) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && title.trim().length === 0) {
      // Default the title to the filename so the assignee doesn't
      // have to retype it; they can still edit before submit.
      setTitle(f.name);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    if (title.trim().length === 0) return;
    // Task 101D Codex P2 (round 6) — clear inputs ONLY when this
    // specific submit call returned success. Reading `props.success`
    // here is unsafe because that prop may still hold a STALE
    // banner from a prior Start (the Start success message persists
    // until a new action clears it; it'd false-fire as "this submit
    // succeeded" even when the submit threw). The handler returns
    // `true` iff this exact call landed cleanly.
    const ok = await props.onSubmit({
      taskId: task.id,
      file,
      title: title.trim(),
      summary: summary.trim(),
    });
    if (ok) {
      setFile(null);
      setTitle('');
      setSummary('');
    }
  };

  const shortId = task.id.slice(0, 8);

  return (
    <div className="card" id={PRODUCTION_TASK_PANEL_ANCHOR_ID} style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>مهمة الإنتاج</h2>
        <span className="meta">production_task #{shortId}</span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
          gap: 14,
        }}
      >
        <Field
          label="الحالة"
          value={productionTaskStatusLabel(task.status)}
          pill={`pill pill-${productionTaskStatusVariant(task.status)}`}
        />
        <Field label="العنوان" value={task.title} />
        <Field
          label="آخر تسليم"
          value={
            task.latest_submission_at
              ? new Date(task.latest_submission_at).toLocaleString('ar-SA')
              : '—'
          }
        />
      </div>

      {/*
        Task 117 / TEAM-2 PR-2 — production-routing controls. Renders
        BEFORE the permission gate / action buttons so even non-acting
        viewers (e.g. an account_manager who can see the task but
        can't act on it) still see the assignee chip. The assign /
        reassign button inside the controls only renders for callers
        in the locked set (owner / marketing_manager /
        production_manager-with-CAT); everyone else sees just the chip.
        Backend not deployed yet — the controls lazy-fetch the
        eligible roster only when the user opens the dialog.
      */}
      <ProductionRoutingControls
        task={task}
        clientId={props.clientIdForRouting}
        canManageRouting={props.canManageProductionRouting}
        onAssigned={props.onProductionRoutingReload}
      />

      {/*
        Task 108B — Production brief read-out. Surfaces the 108A
        production brief fields per variant so designers / video
        producers / agency reviewers see all required instructions
        before claiming or uploading the deliverable. Read-only
        here: editing happens earlier in the manual form + AI draft
        review screen. Renders BEFORE the permission gate / action
        buttons so even non-acting viewers can read the brief.
      */}
      {props.variants.length > 0 ? (
        <ProductionBriefReadOnlySection variants={props.variants} />
      ) : null}

      {!canActAtAll ? (
        <p className="tiny" style={{ marginTop: 14, color: 'var(--muted)' }}>
          {!props.canStartProductionPerm
            ? // Task 175 PR-A · Issue 3 — leadership-aware branch.
              props.isLeadership
              ? PRODUCTION_DELIVERY_DENY_LEADERSHIP_MESSAGE_AR
              : PRODUCTION_DELIVERY_DENY_NONLEADERSHIP_MESSAGE_AR
            : isAiAssigned
              ? 'هذه المهمة مخصصة لمساعد ذكي. الإجراءات اليدوية معطلة.'
              : 'هذه المهمة مخصصة لمستخدم آخر. الإجراءات معطلة.'}
        </p>
      ) : null}

      {props.error ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 12 }}>
          {props.error}
        </div>
      ) : null}
      {props.success ? (
        <div className="banner banner-success" role="status" style={{ marginTop: 12 }}>
          {props.success}
        </div>
      ) : null}

      {canStartArea && (task.status === 'open' || task.status === 'blocked') ? (
        <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {/* Task 290A — primary variant + explicit copy: production QA
              found the bare «بدء» easy to miss and ambiguous. */}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canStart}
            onClick={() => props.onStart(task.id)}
          >
            {props.inFlight
              ? isUnclaimed
                ? 'جارٍ المطالبة…'
                : 'جارٍ البدء…'
              : isUnclaimed
                ? 'مطالبة وبدء'
                : 'بدء تنفيذ المهمة'}
          </button>
        </div>
      ) : null}

      {canSubmitArea && task.status === 'in_progress' ? (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">الملف المُسلَّم</span>
            <input type="file" onChange={onPickFile} disabled={props.inFlight} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">عنوان الملف (مطلوب)</span>
            <input
              type="text"
              value={title}
              maxLength={300}
              onChange={(e) => setTitle(e.target.value)}
              disabled={props.inFlight}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">ملاحظات للمراجع (اختياري — حتى 1000 حرف)</span>
            <textarea
              rows={2}
              value={summary}
              maxLength={1000}
              onChange={(e) => setSummary(e.target.value)}
              disabled={props.inFlight}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" disabled={!canSubmit} onClick={handleSubmit}>
              {props.inFlight ? 'جارٍ التسليم…' : 'تسليم للمراجعة'}
            </button>
          </div>
        </div>
      ) : null}

      {task.status === 'submitted' ? (
        <p className="tiny" style={{ marginTop: 14, color: 'var(--muted)' }}>
          تم تسليم هذه المهمة. بانتظار المراجعة الداخلية.
        </p>
      ) : null}
      {task.status === 'completed' ? (
        <p className="tiny" style={{ marginTop: 14, color: 'var(--muted)' }}>
          هذه المهمة مكتملة.
        </p>
      ) : null}
    </div>
  );
}

// ── Task 108B: production brief read-out ────────────────────────────
//
// Read-only display of the 108A production brief fields per variant.
// The producer / designer / agency reviewer reads this BEFORE
// uploading the deliverable so they have all required instructions
// (on-image text, visual direction, hook, script, shot list, audio
// direction, duration, CTA, asset format, etc.). Editing the brief
// happens earlier in the manual form / AI draft review screen — the
// production task surface is intentionally read-only for the brief.
//
// Per-variant rendering rules:
//   - Platform label is fully Arabic (mirrors 107E inbox labels);
//     no latin-prefixed `Instagram · …` strings leak into the
//     production task surface.
//   - Field set is platform-aware (`isVideoPlatform` from 108A).
//   - Empty / missing fields are skipped silently — no blank rows.
//   - When ALL brief fields are empty for a variant the card shows
//     `لا يوجد موجز إنتاج لهذه النسخة بعد.` instead of a blank list.
//
// Wire surface: never mutates `platform_specific`; never calls the
// save / generate / RPC paths. Pure read.
function ProductionBriefReadOnlySection(props: {
  readonly variants: ReadonlyArray<ContentVariantRow>;
}) {
  return (
    <section className="production-task__brief" aria-label="موجز الإنتاج">
      <div className="production-task__brief-header">
        <h3 className="production-task__brief-title">موجز الإنتاج</h3>
        <span className="production-task__brief-hint">
          المعلومات المطلوبة قبل تنفيذ التصميم أو الفيديو
        </span>
      </div>
      <ul className="production-task__brief-grid">
        {props.variants.map((variant) => {
          const fields = isVideoPlatform(variant.platform)
            ? PRODUCTION_BRIEF_VIDEO_FIELDS
            : PRODUCTION_BRIEF_STATIC_FIELDS;
          const hasAny = variantHasAnyProductionBrief(variant);
          return (
            <li key={variant.id} className="production-task__brief-card">
              <div className="production-task__brief-platform">
                {productionTaskPlatformLabelAr(variant.platform)}
              </div>
              {hasAny ? (
                <dl className="production-task__brief-fields">
                  {fields.map((field) => {
                    const value = readProductionBriefField(variant.platform_specific, field.key);
                    if (value === null) return null;
                    return (
                      <div key={field.key} className="production-task__brief-field">
                        <dt className="production-task__brief-field-label">{field.label}</dt>
                        <dd className="production-task__brief-field-value">{value}</dd>
                      </div>
                    );
                  })}
                </dl>
              ) : (
                <p className="production-task__brief-empty">لا يوجد موجز إنتاج لهذه النسخة بعد.</p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── Task 072: media preview slot ────────────────────────────────────
//
// V1 is placeholder-only. The schema has `creative_assets` +
// `creative_asset_matches` (with `match_source = 'content_item' |
// 'content_variant'`) but no edge function writes assets, no upload
// UI exists, and `storage_key` is internal-only — no public URL is
// available even when assets land. So every card today renders the
// "no design yet" empty state. The prop interface is ready for the
// future asset-fetch integration: once a worker or upload flow
// produces a URL, the card just receives a non-null `state.kind`
// and the slot renders the real preview.

type MediaSlotKind = 'image' | 'video' | 'text';

type MediaSlotState =
  | { readonly kind: 'none' }
  // Reserved for future use — the worker/upload signalling that an
  // asset is in flight (rendering job queued, AI image worker
  // running, etc.). Today V1 never emits this; left as a typed
  // option so the call sites don't need a refactor when it lands.
  | { readonly kind: 'awaiting'; readonly note?: string }
  | {
      readonly kind: 'ready';
      readonly mediaKind: MediaSlotKind;
      // V1 has no signed-URL endpoint — left optional and unused.
      readonly url?: string;
    };

const PLATFORM_ASPECT: Readonly<Record<ContentVariantPlatform, '1/1' | '9/16'>> = {
  instagram_feed: '1/1',
  instagram_story: '9/16',
  instagram_reel: '9/16',
  facebook_post: '1/1',
  facebook_story: '9/16',
  tiktok_video: '9/16',
  snapchat_story: '9/16',
};

function disciplineMediaKind(discipline: ContentDiscipline): MediaSlotKind {
  switch (discipline) {
    case 'design':
      return 'image';
    case 'video':
      return 'video';
    case 'content':
    default:
      return 'text';
  }
}

function pickRepresentativePlatform(
  platforms: ReadonlyArray<ContentVariantPlatform>,
): ContentVariantPlatform | null {
  if (platforms.length === 0) return null;
  // Prefer 9:16 vertical when present — that's the dominant V1
  // surface for short-form video and stories. Otherwise first.
  const vertical = platforms.find((p) => PLATFORM_ASPECT[p] === '9/16');
  return vertical ?? platforms[0]!;
}

interface MediaPreviewSlotProps {
  readonly state?: MediaSlotState;
  readonly platforms: ReadonlyArray<ContentVariantPlatform>;
  readonly discipline: ContentDiscipline;
  /** Compact list/calendar cards use 'sm'; detail panel variants use 'md'. */
  readonly size?: 'sm' | 'md';
  /**
   * Task 164 — review-context label override. When defined, replaces
   * the discipline-driven "لا يوجد تصميم بعد" / "محتوى كتابي"
   * placeholder + the default "جاهز · تصميم" / "جاهز · فيديو" ready
   * caption so the slot can show the review-context-appropriate
   * "بانتظار أصل الإنتاج" / "الأصل جاهز للمراجعة" copy when the
   * caller knows the item is at an approval stage that expects a
   * produced deliverable.
   */
  readonly label?: string;
  // Task 286G — when true, the placeholder NEVER reveals the routing discipline
  // (تصميم / فيديو / محتوى كتابي) or production-state hints to a client: the label
  // becomes neutral ("لا توجد معاينة بعد" / "جاهز للمراجعة") and the discipline
  // format-hint is suppressed. The real asset still renders when ready.
  readonly clientSafe?: boolean;
}

function MediaPreviewSlot(props: MediaPreviewSlotProps): JSX.Element {
  const state: MediaSlotState = props.state ?? { kind: 'none' };
  const repPlatform = pickRepresentativePlatform(props.platforms);
  const aspect = repPlatform ? PLATFORM_ASPECT[repPlatform] : '1/1';
  const mediaKind =
    state.kind === 'ready' ? state.mediaKind : disciplineMediaKind(props.discipline);
  const sizeClass = props.size === 'md' ? 'media-slot--md' : 'media-slot--sm';
  const aspectClass = aspect === '9/16' ? 'media-slot--vertical' : 'media-slot--square';

  const [loadError, setLoadError] = useState(false);
  // Reset the error flag whenever the URL changes — a re-signed URL
  // for the same asset may come through and previously have failed
  // (token expired) but now work.
  const readyUrl = state.kind === 'ready' ? state.url : null;
  useEffect(() => {
    setLoadError(false);
  }, [readyUrl]);

  const effectiveState: MediaSlotState =
    state.kind === 'ready' && (state.url === undefined || loadError) ? { kind: 'none' } : state;
  const stateClass =
    effectiveState.kind === 'ready'
      ? 'media-slot--ready'
      : effectiveState.kind === 'awaiting'
        ? 'media-slot--awaiting'
        : 'media-slot--none';
  const className = `media-slot ${sizeClass} ${stateClass} ${aspectClass}`;

  // Task 286J — clientSafe wins FIRST: the placeholder hint (platform · aspect,
  // e.g. «انستغرام · 9:16») is a production-ish placement hint; clients get a
  // neutral empty hint. Previously clientSafe only suppressed the discipline
  // fallback, so a non-empty platforms array still leaked the hint to clients.
  const formatHint = props.clientSafe
    ? ''
    : repPlatform
      ? `${variantPlatformLabel(repPlatform)} · ${aspect.replace('/', ':')}`
      : `${disciplineLabel(props.discipline)}`;

  let label: string;
  if (props.clientSafe) {
    // Task 286G — never reveal the routing discipline (تصميم/فيديو/محتوى كتابي) or
    // production-state hints to a client via the placeholder; the real asset still
    // renders when ready.
    label =
      effectiveState.kind === 'ready'
        ? 'جاهز للمراجعة'
        : loadError
          ? 'تعذر عرض الملف'
          : 'لا توجد معاينة بعد';
  } else if (props.label !== undefined) {
    // Task 164 — review-context override wins so the slot can show
    // "بانتظار أصل الإنتاج" / "الأصل جاهز للمراجعة" copy at approval
    // stages that expect a produced deliverable. Falls through to the
    // discipline-driven defaults otherwise.
    label = props.label;
  } else if (effectiveState.kind === 'ready') {
    label =
      mediaKind === 'video'
        ? 'جاهز · فيديو'
        : mediaKind === 'image'
          ? 'جاهز · تصميم'
          : 'جاهز للمراجعة';
  } else if (effectiveState.kind === 'awaiting') {
    label = effectiveState.note ?? 'بانتظار التصميم';
  } else if (loadError) {
    label = 'تعذر عرض الملف';
  } else if (mediaKind === 'video') {
    label = 'لا يوجد فيديو بعد';
  } else if (mediaKind === 'image') {
    label = 'لا يوجد تصميم بعد';
  } else {
    label = 'محتوى كتابي';
  }

  // Render the actual asset when ready + url present and not yet
  // failed. Image / video tags fall back to the placeholder shell on
  // load error via the `onError` handlers.
  if (effectiveState.kind === 'ready' && state.kind === 'ready' && typeof state.url === 'string') {
    if (mediaKind === 'image') {
      return (
        <div className={className} aria-label={label} role="img">
          <img
            src={state.url}
            alt={label}
            className="media-slot__media"
            onError={() => setLoadError(true)}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        </div>
      );
    }
    if (mediaKind === 'video') {
      return (
        <div className={className} aria-label={label}>
          <video
            src={state.url}
            controls
            preload="metadata"
            className="media-slot__media"
            onError={() => setLoadError(true)}
            playsInline
          />
        </div>
      );
    }
  }

  return (
    <div className={className} aria-label={label} role="img">
      <div className="media-slot__icon" aria-hidden>
        {/* Task 286G — the placeholder icon is discipline-derived (▶/◧/¶); for
            clients use a neutral glyph so the routing discipline never leaks via
            the icon either. The real asset still renders when ready (above). */}
        {props.clientSafe ? '◌' : mediaKind === 'video' ? '▶' : mediaKind === 'image' ? '◧' : '¶'}
      </div>
      <div className="media-slot__label">{label}</div>
      <div className="media-slot__hint">{formatHint}</div>
    </div>
  );
}

function mediaStateFromAsset(asset: CreativeAssetCardRow | undefined): MediaSlotState {
  if (!asset) return { kind: 'none' };
  // V1 asset_type is image|video on the upload path. Defensive
  // fallback to image for unknown types so the slot still renders.
  const kind: MediaSlotKind =
    asset.asset_type === 'video' ? 'video' : asset.asset_type === 'image' ? 'image' : 'image';
  return { kind: 'ready', mediaKind: kind, url: asset.signed_url };
}

/**
 * Task 164 — translate a production-deliverable embed (signed URL +
 * mime_type) into a MediaSlotState. The deliverable lives in
 * `task_files` (designer upload), separate from `creative_assets`
 * (leadership manual attachment). When BOTH are present for the same
 * item, the deliverable wins because it's the actual produced asset
 * the client is being asked to approve. mime_type drives the
 * image/video kind; unknown mime falls back to image so the slot
 * still renders something useful (the FE still provides an
 * open-in-new-tab link via the surrounding chrome).
 *
 * Accepts a minimal structural shape so the same helper works for
 * both `ProductionTaskDeliverableEmbed` (agency content detail) and
 * `PendingClientApprovalDeliverable` (client inbox).
 */
function mediaStateFromDeliverable(
  deliverable:
    | { readonly mime_type: string | null; readonly signed_url: string }
    | null
    | undefined,
): MediaSlotState | null {
  if (!deliverable) return null;
  const mime = (deliverable.mime_type ?? '').toLowerCase();
  const kind: MediaSlotKind = mime.startsWith('video/')
    ? 'video'
    : mime.startsWith('image/')
      ? 'image'
      : // Non-previewable (e.g. application/pdf, zip). The surrounding
        // chrome renders an open/download link via the deliverable's
        // own preview component; the slot itself just shows the
        // "ready for review" label + a generic icon (handled by the
        // existing 'text' branch of the placeholder render path).
        'text';
  return { kind: 'ready', mediaKind: kind, url: deliverable.signed_url };
}

/**
 * Task 164 — statuses where the item is being reviewed (by agency or
 * client) and the platform card preview should surface the production
 * deliverable when one exists, with the review-context labels
 * "بانتظار أصل الإنتاج" / "الأصل جاهز للمراجعة" instead of the
 * discipline-driven placeholders.
 */
const REVIEW_CONTEXT_STATUSES: ReadonlySet<ContentItemStatus> = new Set<ContentItemStatus>([
  'pending_internal_review',
  'internal_approved',
  'pending_client_content_approval',
  'pending_client_asset_approval',
]);

function isReviewContextStatus(status: ContentItemStatus): boolean {
  return REVIEW_CONTEXT_STATUSES.has(status);
}

/**
 * Task 164 — compute the platform-card MediaPreviewSlot label override
 * for an item that may have a production deliverable. Returns
 * undefined when the slot should keep its discipline-driven default
 * label (non-review contexts). Returns one of:
 *   * `'الأصل جاهز للمراجعة'` when a deliverable IS present
 *   * `'بانتظار أصل الإنتاج'` when the item is at a review stage
 *     that expects a deliverable and none has been uploaded yet
 *     (production-required disciplines only — discipline === 'content'
 *     items don't require a produced asset by Task 164's V1 rule
 *     `app.content_item_needs_production`).
 */
function platformCardReviewLabel(
  status: ContentItemStatus,
  discipline: ContentDiscipline,
  hasDeliverable: boolean,
): string | undefined {
  if (!isReviewContextStatus(status)) return undefined;
  if (hasDeliverable) return 'الأصل جاهز للمراجعة';
  if (discipline === 'design' || discipline === 'video') {
    return 'بانتظار أصل الإنتاج';
  }
  return undefined;
}

// Task 175 PR-A · Issue 5 — media-required platforms that physically
// cannot publish text-only (the Task 172 backend set, mirrored here in
// the FE). Variants on these platforms always need a creative asset
// before they reach `scheduled`; `discipline='content'` items routed
// through them go through production. `facebook_post` is intentionally
// EXCLUDED because Facebook's feed accepts text-only posts.
const MEDIA_REQUIRED_PLATFORM_TARGETS: ReadonlySet<ContentVariantPlatform> = new Set([
  'instagram_feed',
  'instagram_story',
  'instagram_reel',
  'tiktok_video',
  'snapchat_story',
  'facebook_story',
]);

/**
 * Task 175 PR-A · Issue 5 — asset-status label for media-required
 * platform cards. The pre-Task-175 fallback was the discipline-driven
 * `'محتوى كتابي'` placeholder, which is misleading on Instagram /
 * TikTok / Snapchat / FB-story cards: those platforms cannot publish
 * text-only, so the slot is communicating the wrong workflow state.
 *
 * Returns an `undefined` (caller falls through to the default label
 * pipeline) when:
 *   - the variant is on a text-friendly platform (currently only
 *     `facebook_post`); the discipline placeholder is honest there.
 *   - the discipline is `design` or `video` — those items have their
 *     own discipline-driven copy via `platformCardReviewLabel`.
 *   - the item has already reached terminal scheduled / published /
 *     cancelled — let the consumer's `ready` branch handle preview.
 *
 * Otherwise returns one of four Arabic copies pinned to the workflow
 * state the user actually cares about:
 *   * `أصل مطلوب`                — no production task yet
 *     (`draft` / internal-review / client-content-approval stages,
 *     no deliverable submitted).
 *   * `قيد الإنتاج`             — `in_production` (task active,
 *     deliverable not yet submitted).
 *   * `أصل جاهز`               — deliverable submitted but still in
 *     internal-review / handoff stages prior to the client's final
 *     approval.
 *   * `بانتظار اعتماد الأصل`    — `pending_client_asset_approval`
 *     (client review of the produced asset).
 */
function mediaRequiredAssetStatusLabel(
  platform: ContentVariantPlatform,
  discipline: ContentDiscipline,
  status: ContentItemStatus,
  hasDeliverable: boolean,
): string | undefined {
  if (!MEDIA_REQUIRED_PLATFORM_TARGETS.has(platform)) return undefined;
  if (discipline !== 'content') return undefined;
  // Codex Step A · P1 — exhaustive switch over every `ContentItemStatus`
  // value so a future enum addition surfaces as a `noImplicitReturns`
  // error (or, at worst, a `default` fall-through that the test pin
  // catches) rather than silently returning the misleading
  // `'أصل مطلوب'` for a state where an asset clearly exists.
  // Specifically, the prior guard-and-fall-through routed
  // `client_asset_approved + hasDeliverable=false` (the inconsistent
  // post-approval-pre-publish state) to `'أصل مطلوب'` even though the
  // client has just approved an asset.
  switch (status) {
    case 'draft':
    case 'pending_internal_review':
    case 'internal_approved':
    case 'pending_client_content_approval':
    case 'client_content_approved':
      // Pre-production stages — `hasDeliverable` is false by
      // invariant (`has_current_production_deliverable` resets to
      // false on every `*_approved → in_production` flip per Task
      // 164). Asset is not yet in production.
      return hasDeliverable ? 'أصل جاهز' : 'أصل مطلوب';
    case 'in_production':
      // Active production cycle. Show "ready" once a deliverable
      // lands (production submitted) — the parent will auto-advance
      // to `pending_internal_review` immediately so this state is
      // very short-lived, but keep the label honest.
      return hasDeliverable ? 'أصل جاهز' : 'قيد الإنتاج';
    case 'pending_client_asset_approval':
      // Client is reviewing the produced asset — always show the
      // review label regardless of `hasDeliverable` (the inbox EF
      // refuses to surface this status without a deliverable, so
      // the false branch is defensive).
      return 'بانتظار اعتماد الأصل';
    case 'client_asset_approved':
      // Client has approved the asset; item is queued for scheduling.
      // The asset IS effectively final — render the "ready" label
      // even when the local `hasDeliverable` view momentarily lags
      // (e.g. inbox row arrived before the production-task loader).
      return 'أصل جاهز';
    case 'scheduled':
    case 'published':
    case 'cancelled':
      // Terminal states — defer to the slot's `ready` branch (the
      // actual image renders), no override label needed.
      return undefined;
  }
}

// Task 073 — probe a file's natural dimensions / duration before
// upload. Required by the `creative_assets` CHECK constraint
// (image needs width+height, video needs all three). All work in
// browser — no network calls, just a transient ObjectURL.
async function probeMediaDimensions(
  file: File,
  kind: CreativeAssetUploadKind,
): Promise<{ width: number; height: number; durationMs: number | null }> {
  const url = URL.createObjectURL(file);
  try {
    if (kind === 'image') {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth <= 0 || img.naturalHeight <= 0) {
            reject(new Error('image has invalid dimensions'));
            return;
          }
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
            durationMs: null,
          });
        };
        img.onerror = () => reject(new Error('image failed to load for probing'));
        img.src = url;
      });
    }
    return await new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        if (video.videoWidth <= 0 || video.videoHeight <= 0) {
          reject(new Error('video has invalid dimensions'));
          return;
        }
        const ms = Number.isFinite(video.duration)
          ? Math.max(1, Math.round(video.duration * 1000))
          : 1;
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          durationMs: ms,
        });
      };
      video.onerror = () => reject(new Error('video failed to load for probing'));
      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface AttachCreativeAssetButtonProps {
  readonly targetSource: CreativeAssetTargetSource;
  readonly targetId: string;
  readonly accept: string;
  readonly label: string;
  readonly onAttach: (args: {
    target_source: CreativeAssetTargetSource;
    target_id: string;
    file: File;
  }) => Promise<void>;
}

function AttachCreativeAssetButton(props: AttachCreativeAssetButtonProps): JSX.Element {
  const inputId = useMemo(
    () =>
      `attach-${props.targetSource}-${props.targetId}-${Math.random().toString(36).slice(2, 8)}`,
    [props.targetSource, props.targetId],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="attach-creative-asset">
      <label htmlFor={inputId} className={busy ? 'btn-text disabled' : 'btn-text'}>
        {busy ? 'جارٍ الرفع…' : props.label}
      </label>
      <input
        id={inputId}
        type="file"
        accept={props.accept}
        disabled={busy}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset the input so the same file can be re-selected after
          // an error or successful re-attach.
          e.target.value = '';
          if (!file) return;
          setError(null);
          setBusy(true);
          props
            .onAttach({
              target_source: props.targetSource,
              target_id: props.targetId,
              file,
            })
            .catch((err: unknown) => {
              setError(err instanceof Error ? err.message : 'فشل الرفع');
            })
            .finally(() => setBusy(false));
        }}
      />
      {error ? <span className="attach-creative-asset__error tiny">{error}</span> : null}
    </div>
  );
}

interface RemoveCreativeAssetButtonProps {
  readonly targetSource: CreativeAssetTargetSource;
  readonly targetId: string;
  readonly creativeAssetId: string;
  readonly onRemove: (args: {
    target_source: CreativeAssetTargetSource;
    target_id: string;
    creative_asset_id: string;
  }) => Promise<void>;
}

// Task 076 — small "remove attachment" action that lives next to
// the attach/replace button on the variant card. Confirms with a
// browser confirm() so a misclick can't quietly archive the asset
// row, and surfaces a short error inline if the server refuses.
function RemoveCreativeAssetButton(props: RemoveCreativeAssetButtonProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="attach-creative-asset attach-creative-asset--remove">
      <button
        type="button"
        className={busy ? 'btn-text disabled' : 'btn-text'}
        disabled={busy}
        onClick={() => {
          // Native confirm keeps this consistent with the rest of
          // the destructive actions on the page (no modal stack
          // exists in this view yet).
          const ok = window.confirm(
            'هل تريد إزالة هذا التصميم/الفيديو من هذا المتغير؟ يمكن إعادة الإرفاق لاحقًا.',
          );
          if (!ok) return;
          setError(null);
          setBusy(true);
          props
            .onRemove({
              target_source: props.targetSource,
              target_id: props.targetId,
              creative_asset_id: props.creativeAssetId,
            })
            .catch((err: unknown) => {
              setError(err instanceof Error ? err.message : 'تعذر الإزالة');
            })
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'جارٍ الإزالة…' : 'إزالة'}
      </button>
      {error ? <span className="attach-creative-asset__error tiny">{error}</span> : null}
    </div>
  );
}

// Task 105A — simplified status labels. The pre-105A labels for
// three statuses were terse to the point of ambiguity; the new
// strings make the workflow stage explicit:
//   * client_content_approved      'موافقة العميل' → 'وافق العميل على المحتوى'
//   * pending_client_asset_approval 'مراجعة أصل'   → 'بانتظار الموافقة النهائية'
//   * client_asset_approved         'أصل معتمد'    → 'جاهز للنشر'
// The other labels match the spec verbatim already.
// ─────────────────────────────────────────────────────────────────────
// Task 107B — content status timeline
//
// Linear 9-step indicator for the content workflow funnel. Each step
// is one of: 'done' | 'current' | 'future'. Steps are deterministic
// per content_item.status; for items that took the direct manual
// handoff (draft → pending_client_content_approval, skipping the
// internal-review pair), steps 2 and 3 stay marked 'future' rather
// than 'done' so the bar doesn't lie about what happened. The audit
// log card below the timeline tells the real history.
//
// The bar is purely presentational — no handlers, no state writes,
// no permission checks. It reads the same `item.status` the rest of
// the detail panel uses, so it can never desync from the source of
// truth.
// ─────────────────────────────────────────────────────────────────────
// Task 120 — workflow phase grouping. The 9 fine-grained steps below
// are grouped into 3 user-facing phases (`content` / `production` /
// `publishing`). The phase strip rendered above the step bar tells the
// user "you have crossed N major milestones"; the existing step bar
// remains underneath as sub-progress. Without the phase strip, when
// the current marker moves from in_production → pending_client_asset_approval
// the user's eye locks on the new tangerine marker and the workflow
// visually "feels restarted". The phase strip prevents that by
// dominating the eye-line with a coarse "2 of 3 phases done" cue.
type ContentTimelinePhase = 'content' | 'production' | 'publishing';

const CONTENT_STATUS_TIMELINE_PHASES: ReadonlyArray<{
  readonly key: ContentTimelinePhase;
  readonly label: string;
}> = [
  { key: 'content', label: 'اعتماد المحتوى' },
  { key: 'production', label: 'الإنتاج' },
  { key: 'publishing', label: 'النشر' },
];

const CONTENT_STATUS_TIMELINE_STEPS: ReadonlyArray<{
  readonly key: string;
  readonly label: string;
  readonly statuses: ReadonlyArray<ContentItemStatus>;
  readonly phase: ContentTimelinePhase;
}> = [
  { key: 'draft', label: 'مسودة', statuses: ['draft'], phase: 'content' },
  {
    key: 'internal_review',
    label: 'مراجعة داخلية',
    statuses: ['pending_internal_review'],
    phase: 'content',
  },
  {
    key: 'internal_approved',
    label: 'معتمد داخلياً',
    statuses: ['internal_approved'],
    phase: 'content',
  },
  {
    key: 'client_content',
    label: 'موافقة العميل',
    statuses: ['pending_client_content_approval', 'client_content_approved'],
    phase: 'content',
  },
  { key: 'in_production', label: 'إنتاج', statuses: ['in_production'], phase: 'production' },
  {
    key: 'asset_review',
    label: 'مراجعة الأصل',
    statuses: ['pending_client_asset_approval'],
    phase: 'publishing',
  },
  {
    key: 'asset_approved',
    label: 'جاهز للنشر',
    statuses: ['client_asset_approved'],
    phase: 'publishing',
  },
  { key: 'scheduled', label: 'مجدول', statuses: ['scheduled'], phase: 'publishing' },
  { key: 'published', label: 'منشور', statuses: ['published'], phase: 'publishing' },
];

function ContentStatusTimeline({ status }: { readonly status: ContentItemStatus }) {
  // Find the step that owns the current status. Statuses outside the
  // active publishing path (e.g. 'cancelled') get an explicit Arabic
  // badge instead of a misleading linear timeline.
  const currentIndex = CONTENT_STATUS_TIMELINE_STEPS.findIndex((s) => s.statuses.includes(status));
  if (currentIndex === -1) {
    return (
      <p className="content-status-timeline-off" role="note">
        خارج مسار النشر النشط
      </p>
    );
  }
  // Task 120 — derive phase-strip state from the same currentIndex the
  // step bar uses. The phase that owns the current step is `active`;
  // any phase whose LAST step index is strictly less than currentIndex
  // is fully `done`; the rest are `upcoming`. This cannot desync from
  // the step bar because both read the same source.
  //
  // Special case: when the LAST step of the entire pipeline is the
  // current step (i.e. status === 'published'), the workflow itself
  // is complete and EVERY phase — including the publishing phase
  // that owns the terminal step — must render as `done`. Without
  // this carve-out, `published` would show "publishing: active"
  // forever, which misreads the workflow as still in-flight.
  const currentPhase = CONTENT_STATUS_TIMELINE_STEPS[currentIndex].phase;
  const isTerminal = currentIndex === CONTENT_STATUS_TIMELINE_STEPS.length - 1;
  const lastIndexByPhase = new Map<ContentTimelinePhase, number>();
  CONTENT_STATUS_TIMELINE_STEPS.forEach((step, i) => {
    lastIndexByPhase.set(step.phase, i);
  });
  const phaseStateLabels: Record<'done' | 'active' | 'upcoming', string> = {
    done: 'مكتمل',
    active: 'نشط',
    upcoming: 'قادم',
  };
  return (
    <div className="content-status-timeline-wrap">
      <ol className="content-status-phases" aria-label="مرحلة المسار العامة">
        {CONTENT_STATUS_TIMELINE_PHASES.map((phase) => {
          const lastIdx = lastIndexByPhase.get(phase.key) ?? -1;
          const state: 'done' | 'active' | 'upcoming' = isTerminal
            ? 'done'
            : phase.key === currentPhase
              ? 'active'
              : lastIdx < currentIndex
                ? 'done'
                : 'upcoming';
          return (
            <li
              key={phase.key}
              className={`content-status-phases__chip is-phase-${state}`}
              aria-current={state === 'active' ? 'step' : undefined}
              aria-label={`${phase.label}: ${phaseStateLabels[state]}`}
            >
              {state === 'done' ? (
                <span className="content-status-phases__check" aria-hidden="true">
                  ✓
                </span>
              ) : (
                <span className="content-status-phases__bullet" aria-hidden="true" />
              )}
              <span className="content-status-phases__label">{phase.label}</span>
            </li>
          );
        })}
      </ol>
      <ol className="content-status-timeline" aria-label="مرحلة سير العمل الحالية">
        {CONTENT_STATUS_TIMELINE_STEPS.map((step, i) => {
          const phase = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'future';
          return (
            <li
              key={step.key}
              className={`content-status-timeline__step is-${phase}`}
              data-phase={step.phase}
              aria-current={phase === 'current' ? 'step' : undefined}
            >
              <span className="content-status-timeline__dot" aria-hidden="true" />
              <span className="content-status-timeline__label">{step.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function contentStatusLabel(status: ContentItemStatus): string {
  switch (status) {
    case 'draft':
      return 'مسودة';
    case 'pending_client_content_approval':
      return 'بانتظار موافقة العميل';
    case 'client_content_approved':
      return 'وافق العميل على المحتوى';
    case 'in_production':
      return 'قيد الإنتاج';
    case 'pending_internal_review':
      return 'مراجعة داخلية';
    case 'internal_approved':
      return 'معتمد داخلياً';
    case 'pending_client_asset_approval':
      return 'بانتظار الموافقة النهائية';
    case 'client_asset_approved':
      return 'جاهز للنشر';
    case 'scheduled':
      return 'مجدول';
    case 'published':
      return 'منشور';
    case 'cancelled':
      return 'ملغي';
    default:
      return status;
  }
}

function contentStatusVariant(status: ContentItemStatus): string {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'cancelled':
      return 'archived';
    case 'published':
      return 'active';
    case 'scheduled':
    case 'client_content_approved':
    case 'client_asset_approved':
    case 'internal_approved':
      return 'info';
    default:
      return 'review';
  }
}

function disciplineLabel(discipline: ContentDiscipline): string {
  switch (discipline) {
    case 'content':
      return 'محتوى كتابي';
    case 'design':
      return 'تصميم';
    case 'video':
      return 'فيديو';
    default:
      return discipline;
  }
}

function originLabel(origin: ContentItemListRow['origin']): string {
  switch (origin) {
    case 'manual':
      return 'يدوي';
    case 'ai_generated':
      return 'AI';
    case 'ai_assisted':
      return 'AI مساعد';
    case 'imported':
      return 'مستورد';
    default:
      // Task 286H — origin is null in client-safe responses; it is only ever
      // rendered behind the agency-only `canSeeContentOrigin` gate, so this is
      // defensive (clients never reach an origin label).
      return origin ?? '';
  }
}

function variantPlatformLabel(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'Instagram · منشور';
    case 'instagram_story':
      return 'Instagram · ستوري';
    case 'instagram_reel':
      return 'Instagram · ريل';
    case 'facebook_post':
      return 'Facebook · منشور';
    case 'facebook_story':
      return 'Facebook · ستوري';
    case 'tiktok_video':
      return 'TikTok · فيديو';
    case 'snapchat_story':
      return 'Snapchat · ستوري';
    default:
      return platform;
  }
}

function variantStatusLabel(status: ContentVariantStatus): string {
  switch (status) {
    case 'draft':
      return 'مسودة';
    case 'approved':
      return 'معتمد';
    case 'scheduled':
      return 'مجدول';
    case 'published':
      return 'منشور';
    case 'failed':
      return 'فشل';
    default:
      return status;
  }
}

function variantStatusVariant(status: ContentVariantStatus): string {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'approved':
      return 'info';
    case 'scheduled':
      return 'review';
    case 'published':
      return 'active';
    case 'failed':
      return 'error';
    default:
      return 'info';
  }
}

function approvalStageLabel(stage: ContentApprovalStage): string {
  switch (stage) {
    case 'client_content_approval':
      return 'موافقة محتوى من العميل';
    case 'client_asset_approval':
      return 'موافقة أصل من العميل';
    case 'internal_review':
      return 'مراجعة داخلية';
    default:
      return stage;
  }
}

function approvalDecisionLabel(decision: ContentApprovalDecision): string {
  switch (decision) {
    case 'approved':
      return 'موافقة';
    case 'rejected':
      return 'رفض';
    case 'changes_requested':
      return 'تعديل مطلوب';
    default:
      return decision;
  }
}

function approvalDecisionVariant(decision: ContentApprovalDecision): string {
  switch (decision) {
    case 'approved':
      return 'active';
    case 'rejected':
      return 'error';
    case 'changes_requested':
      return 'review';
    default:
      return 'info';
  }
}

// ── Task 070 helpers ────────────────────────────────────────────────
//
// `latestClientChangesRequest` returns the most-recent approval row
// when (and only when) it is a `client_content_approval` /
// `changes_requested` decision. The badge derived from this drives
// the agency UI signal that "this draft is sitting here because the
// client asked for changes."
//
// Caller responsibility: gate on `item.status === 'draft'` so the
// badge does not surface on items that have since moved forward into
// a new review cycle.
//
// Why "head row only", no proximity check on `parent.updated_at`:
//   - Agency edits to the returned draft (via `update-content-
//     calendar-draft`) advance `content_items.updated_at` without
//     writing any approval row. A proximity-based freshness check
//     would silently hide the badge after the first edit, exactly
//     when the agency reviewer needs to see WHY the draft was
//     bounced. The simpler "is the head row a client request?"
//     contract holds across draft edits because no new approval
//     row is added to displace the head.
//   - Agency rollback of `pending_client_content_approval → draft`
//     (Branch 2 in the RPC) writes no approval row either, but the
//     prior cycle's `internal_approved` row remains the head — so
//     the badge correctly does not show. The two cases are
//     distinguishable by what's at the head, not by timestamps.
//   - Internal-review bounce inserts an `internal_review /
//     changes_requested` row, displacing any prior client row from
//     the head; the badge correctly drops out.
//
// The endpoint sorts approvals desc, but we still find the head
// defensively so a wire-shape regression can't surface stale data.
type ApprovalSummary = {
  readonly stage: ContentApprovalStage;
  readonly decision: ContentApprovalDecision;
  readonly reason: string | null;
  readonly approver_user_id: string;
  readonly created_at: string;
};

function latestClientChangesRequest<T extends ApprovalSummary>(
  approvals: ReadonlyArray<T> | undefined | null,
): T | null {
  if (!approvals || approvals.length === 0) return null;
  let head: T | null = null;
  for (const a of approvals) {
    if (head === null || a.created_at > head.created_at) {
      head = a;
    }
  }
  if (head === null) return null;
  if (head.stage !== 'client_content_approval' || head.decision !== 'changes_requested') {
    return null;
  }
  return head;
}

// Truncate the reason on cards so a long client paragraph doesn't
// stretch the layout. The detail panel shows the full text.
function truncateReason(reason: string | null, max: number = 140): string | null {
  if (!reason) return null;
  const trimmed = reason.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max - 1).trimEnd() + '…';
}

// Approver identity is exposed as the bare auth_user_id pending a
// follow-up that adds a profile/auth lookup. Keep the display short
// to avoid layout drift.
function shortApproverId(id: string): string {
  if (!id) return '—';
  return id.slice(0, 8);
}

function Field({
  label,
  value,
  pill,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
  readonly pill?: string;
}) {
  if (pill && value) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="lbl">{label}</span>
        <span className={pill} style={{ width: 'fit-content' }}>
          {value}
        </span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Task 067: AI calendar draft + manual add
// ─────────────────────────────────────────────────────────────────────

function aiResponseToDraft(
  data: GenerateContentCalendarDraftData,
  form: AiCalendarFormState,
): DraftState {
  const aiSummary: AiSummary = {
    model: data.model,
    costUsd: data.cost_usd,
    tokensInput: data.tokens_input,
    tokensOutput: data.tokens_output,
    durationMs: data.duration_ms,
  };
  if (data.mode === 'unified') {
    // Task 108A — unified mode now ships a per-platform production
    // brief (`item.platform_briefs`) so production handoff never
    // inherits an empty brief just because the operator chose
    // unified copy. For each form-selected platform we look up the
    // matching `(platform, brief)` entry. A missing match (the
    // worker validator surfaces a `platform_brief_missing` warning
    // when this happens) falls back to `defaultEmptyBrief()` so the
    // draft still renders and the user can fill the gap in the
    // DraftEditor.
    const items: DraftItemState[] = data.draft.items.map((item) => {
      const briefByPlatform = new Map(
        item.platform_briefs.map((pb) => [pb.platform, pb.brief] as const),
      );
      return {
        key: genKey(),
        title: item.title,
        body: item.core_idea,
        scheduledDate: item.scheduled_date,
        scheduledTimeLocal: item.scheduled_time_local,
        variants: form.platforms.map((platform) => ({
          platform,
          headline: '',
          caption: item.caption,
          body: '',
          hashtags: item.hashtags,
          brief: briefStateFromAiResponse(briefByPlatform.get(platform)),
        })),
      };
    });
    return {
      origin: 'ai_assisted',
      mode: 'unified',
      aiRunId: data.ai_run_id,
      month: form.month,
      timezone: form.timezone,
      platforms: form.platforms,
      items,
      aiSummary,
      aiWarnings: data.draft.warnings,
    };
  }
  // platform_adapted — Task 108A: the worker can include a
  // `production_brief` per variant with platform-aware fields
  // (image-format vs video-format). When present, we hydrate
  // `DraftVariantBriefState` from it; when absent, we initialise
  // empty so the operator can fill in the DraftEditor.
  const items: DraftItemState[] = data.draft.items.map((item) => ({
    key: genKey(),
    title: item.title,
    body: item.core_idea,
    scheduledDate: item.scheduled_date,
    scheduledTimeLocal: item.scheduled_time_local,
    variants: item.variants.map((v) => ({
      platform: v.platform,
      headline: v.headline ?? '',
      caption: v.caption,
      body: v.body ?? '',
      hashtags: v.hashtags,
      brief: briefStateFromAiResponse(v.production_brief),
    })),
  }));
  return {
    origin: 'ai_assisted',
    mode: 'platform_adapted',
    aiRunId: data.ai_run_id,
    month: form.month,
    timezone: form.timezone,
    platforms: form.platforms,
    items,
    aiSummary,
    aiWarnings: data.draft.warnings,
  };
}

// Task 105A — manual single-post draft state. Pre-105A used the
// month-grid `DraftState` shape (one item per row, `month` + `platforms`
// at the top level); the new shape is post-by-post: ONE title +
// discipline + date + time + per-platform copy. Save dispatches a
// 1-item array through `save-content-calendar-draft`.
interface ManualSinglePostVariantState {
  readonly platform: ContentVariantPlatform;
  readonly headline: string;
  readonly caption: string;
  readonly body: string;
  // Hashtags entered as a single space- or newline-separated string in
  // the form; serialized to `platform_specific.hashtags: string[]` on
  // submit so the wire shape matches the existing month-grid path.
  readonly hashtagsInput: string;
  // Task 108A — production brief carried alongside the existing
  // copy fields. Same shape + initial-empty rules as
  // `DraftVariantBriefState`, plus a free `cta` string that the AI
  // flow keeps under `platform_specific.cta` (not `brief.cta`) so
  // the manual + AI saves converge on the same wire shape.
  readonly brief: DraftVariantBriefState;
  readonly cta: string;
}

// Discipline is hardcoded to 'content' server-side by
// `save_content_calendar_draft_v1` (see migration
// `20260430110000_content_calendar_draft.sql:521`). Until that RPC
// accepts a discipline parameter, the manual single-post modal
// doesn't expose a picker — wiring one up here would be misleading
// because the server would always overwrite the choice.
interface ManualSinglePostFormState {
  readonly title: string;
  readonly itemBody: string;
  readonly scheduledLocalDate: string;
  readonly scheduledLocalTime: string;
  readonly timezone: string;
  readonly variants: ReadonlyArray<ManualSinglePostVariantState>;
}

function validateDraftBeforeSave(draft: DraftState): string | null {
  if (draft.items.length === 0) return 'يجب أن تحتوي المسودة على عنصر واحد على الأقل.';
  if (draft.items.length > 30) return 'لا يمكن حفظ أكثر من 30 عنصراً في حفظة واحدة.';
  for (const [idx, item] of draft.items.entries()) {
    if (item.title.trim().length === 0) {
      return `العنصر رقم ${idx + 1}: العنوان مطلوب.`;
    }
    if (!/^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(item.scheduledDate)) {
      return `العنصر رقم ${idx + 1}: التاريخ غير صحيح (يلزم YYYY-MM-DD).`;
    }
    if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(item.scheduledTimeLocal)) {
      return `العنصر رقم ${idx + 1}: الوقت غير صحيح (يلزم HH:MM).`;
    }
    if (item.variants.length === 0) {
      return `العنصر رقم ${idx + 1}: يلزم منصة واحدة على الأقل.`;
    }
    if (item.variants.length > 4) {
      return `العنصر رقم ${idx + 1}: لا يمكن أن يحتوي على أكثر من 4 منصات.`;
    }
    const seen = new Set<string>();
    for (const [vIdx, v] of item.variants.entries()) {
      if (seen.has(v.platform)) {
        return `العنصر رقم ${idx + 1}: المنصة «${variantPlatformLabel(v.platform)}» مكررة.`;
      }
      seen.add(v.platform);
      const hasText =
        v.headline.trim().length > 0 || v.caption.trim().length > 0 || v.body.trim().length > 0;
      if (!hasText) {
        return `العنصر رقم ${idx + 1}، المنصة ${vIdx + 1}: أضف عنواناً أو نصاً أو تعليقاً واحداً على الأقل.`;
      }
    }
  }
  return null;
}

function buildSaveItem(item: DraftItemState, timezone: string): SaveContentCalendarDraftItemInput {
  // Combine local date+time with the supplied IANA timezone into a
  // wall-clock ISO string the server interprets via timestamptz.
  // Simpler approach: emit the local-time form and trust the server's
  // tz-naive parse via `(text)::timestamptz` to interpret in the
  // session zone. To stay safe, we emit a UTC ISO instead by
  // reconstructing through Intl. Build a literal `YYYY-MM-DDTHH:MM:00`
  // and let the API server parse it as the supplied tz; we send the
  // tz separately.
  //
  // The server-side parser accepts ISO8601 with timezone, so the
  // safest wire format is the full offset form. We approximate the
  // offset by computing it for the supplied date in the supplied tz
  // via Intl.DateTimeFormat. Browsers ship this in modern
  // environments.
  const offset = computeTzOffsetMinutes(item.scheduledDate, item.scheduledTimeLocal, timezone);
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const offsetH = Math.floor(abs / 60)
    .toString()
    .padStart(2, '0');
  const offsetM = (abs % 60).toString().padStart(2, '0');
  const isoLocal = `${item.scheduledDate}T${item.scheduledTimeLocal}:00${sign}${offsetH}:${offsetM}`;
  const variants: ReadonlyArray<SaveContentCalendarDraftVariantInput> = item.variants.map((v) => {
    // Task 108A — fold the AI / operator-edited production brief
    // into platform_specific. Mirrors the manual flow's
    // `buildPlatformSpecificFromManual` so both save paths converge
    // on identical wire shape. The `cta` lives on the brief jsonb
    // (not on the brief state itself); for AI-supplied drafts the
    // model writes it under `production_brief.cta`-equivalent text
    // — but since the AI worker emits cta separately for V1 and we
    // don't carry a cta string on `DraftVariantState` yet, the
    // brief-side cta defaults to empty here. Operators add it via
    // the DraftEditor's CTA input.
    const ps: Record<string, unknown> = {
      ...briefToPlatformSpecific(v.platform, v.brief, ''),
    };
    if (v.hashtags.length > 0) ps['hashtags'] = v.hashtags;
    return {
      platform: v.platform,
      headline: v.headline.trim().length > 0 ? v.headline.trim() : undefined,
      caption: v.caption.trim().length > 0 ? v.caption.trim() : undefined,
      body: v.body.trim().length > 0 ? v.body.trim() : undefined,
      platform_specific:
        Object.keys(ps).length > 0 ? (ps as SaveContentCalendarDraftPlatformSpecific) : undefined,
    };
  });
  return {
    title: item.title.trim(),
    body: item.body.trim().length > 0 ? item.body.trim() : undefined,
    scheduled_for: isoLocal,
    timezone,
    variants,
  };
}

function computeTzOffsetMinutes(date: string, time: string, timezone: string): number {
  // Best-effort offset computation. We construct the date as if it
  // were UTC, then ask the formatter how the same instant looks in
  // the target timezone, then subtract. If `Intl.DateTimeFormat`
  // can't honor the zone, we fall back to 0 (UTC) which the server
  // still accepts; the user-facing timezone string is the audit
  // anchor.
  try {
    const utc = new Date(`${date}T${time}:00Z`);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(utc);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const tzDate = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second'),
    );
    return Math.round((tzDate - utc.getTime()) / 60_000);
  } catch {
    return 0;
  }
}

// Task 107D — V1 platform allow-list scoped to the AI calendar flow
// (mode chooser + draft review). Mirrors MANUAL_V1_PLATFORM_OPTIONS:
// Facebook is intentionally hidden from the AI generator + draft
// review surfaces. The global PLATFORM_OPTIONS still drives the
// variants display elsewhere on this page.
// Task 107D — Arabic-only platform labels for the AI calendar flow.
// Same approach as manualPlatformLabelAr: keeps brand names in
// Arabic ("إنستجرام · منشور") so operators don't see latin script
// mid-flow. Scoped to this surface so the global
// `variantPlatformLabel` (still latin-prefixed for cross-surface
// readability) stays untouched.
function aiPlatformLabelAr(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'إنستجرام · منشور';
    case 'instagram_story':
      return 'إنستجرام · ستوري';
    case 'instagram_reel':
      return 'إنستجرام · ريل';
    case 'tiktok_video':
      return 'تيك توك · فيديو';
    case 'snapchat_story':
      return 'سناب شات · ستوري';
    default:
      return platform;
  }
}

// Task 107D — Arabic-friendly translation of the AI generator
// warning codes coming from `generate-content-calendar-draft`. The
// raw snake_case codes (e.g. `low_model_confidence`) leak technical
// vocabulary into the operator's flow; this map turns them into
// readable Arabic guidance. Unknown codes fall back to the model's
// natural-language `message` so we never drop information.
function aiWarningLabelAr(code: string): string | null {
  switch (code) {
    case 'low_model_confidence':
      return 'ثقة النموذج منخفضة في بعض العناصر — راجعها قبل الحفظ.';
    case 'incomplete_coverage':
      return 'النموذج لم يولّد العدد المطلوب بالكامل — أضف عناصر يدوياً عند الحاجة.';
    case 'duplicate_topics':
      return 'بعض الأفكار متكررة — راجع المسودة قبل الحفظ.';
    case 'date_clamped':
      return 'تم تعديل بعض التواريخ لتقع داخل الشهر المختار.';
    case 'platform_dropped':
      return 'منصة واحدة على الأقل لم تُولَّد لها نسخة — راجع العناصر المتأثرة.';
    case 'hashtags_truncated':
      return 'تم تقليص قائمة الوسوم في بعض العناصر.';
    case 'instruction_ignored':
      return 'الإرشاد الإبداعي طويل جداً وتم تجاهل جزء منه.';
    default:
      return null;
  }
}

// Task 107D — `ModeChooserCard` is the AI calendar setup screen.
// 107D rewrite: full Arabic labels (no `draft only`, no
// `(YYYY-MM)`), V1 platform allow-list excludes Facebook, timezone
// is a curated `<select>` reusing MANUAL_TIMEZONE_OPTIONS (so the
// raw IANA `Asia/Riyadh` never surfaces), layout uses dedicated
// `.ai-calendar-form*` classes from app.css. Generator payload +
// `generateContentCalendarDraft` call shape are unchanged.
// Task 1D — explain WHY the AI entry-count is the form default (8) instead of a
// package-derived value. Display-only; only shown while the default is still in use.
const AI_ENTRY_COUNT_DEFAULT = 8;
const AI_ENTRY_COUNT_FALLBACK_HINTS: Record<Exclude<EntryCountReason, 'derived'>, string> = {
  no_assignment: 'نستخدم القيمة الافتراضية (8) لأنه لا توجد باقة مفعّلة لهذا العميل.',
  no_included_social:
    'نستخدم القيمة الافتراضية (8) لأن باقة العميل لا تتضمن خدمة سوشال ميديا مفعّلة لتخطيط المحتوى.',
  no_monthly:
    'نستخدم القيمة الافتراضية (8) لأن خدمة السوشال المفعّلة لا تحتوي على كميات شهرية للتصاميم أو الفيديوهات.',
};

function ModeChooserCard({
  form,
  onChange,
  onCancel,
  onGenerate,
  error,
  entryCountReason,
}: {
  readonly form: AiCalendarFormState;
  readonly onChange: (next: AiCalendarFormState) => void;
  readonly onCancel: () => void;
  readonly onGenerate: () => void;
  readonly error: string | null;
  readonly entryCountReason: EntryCountReason | null;
}) {
  // Task 283 — the picker is platform-level and platform-FIRST. Toggling a parent
  // platform or an asset format re-resolves `platforms` (the publish_target_v1[]
  // sent to the generator) as the union of resolveRequestedOutputs. The request
  // shape is unchanged.
  const toggleParentPlatform = (p: SocialPlatform) => {
    const parentPlatforms = form.parentPlatforms.includes(p)
      ? form.parentPlatforms.filter((x) => x !== p)
      : [...form.parentPlatforms, p];
    // Platforms changed → drop any now-incompatible format (formats follow
    // platforms), then re-resolve the targets union.
    const assetFormats = normalizeAssetFormats(form.assetFormats, parentPlatforms);
    onChange({
      ...form,
      parentPlatforms,
      assetFormats,
      // A direct platform toggle is a manual override → stop auto-seeding platforms
      // from the operating setup (toggling a format does NOT set this).
      parentPlatformsManuallyEdited: true,
      platforms: aiResolvedTargets(assetFormats, parentPlatforms),
    });
  };
  const toggleAssetFormat = (f: AssetFormat) => {
    const isOn = form.assetFormats.includes(f);
    // At least one format must stay selected while platforms are chosen, so the
    // resolved union is never silently empty — deselecting the last format is a
    // no-op (the user clears the picker by removing platforms, the primary axis).
    if (isOn && form.assetFormats.length === 1) return;
    // Only compatible formats are rendered, so a toggled-on format always resolves.
    const assetFormats = isOn
      ? form.assetFormats.filter((x) => x !== f)
      : orderedAssetFormats([...form.assetFormats, f]);
    onChange({
      ...form,
      assetFormats,
      platforms: aiResolvedTargets(assetFormats, form.parentPlatforms),
    });
  };
  // Task 283 — the format chips shown for the selected platforms, the resolved
  // outputs grouped by format (for the summary), and the resolved target count
  // (capped at PLATFORMS_MAX by the generate contract — block + explain above it).
  const compatibleFormats = new Set(compatibleAiAssetFormats(form.parentPlatforms));
  const requestedOutputs = resolveRequestedOutputs(
    orderedAssetFormats(form.assetFormats),
    form.parentPlatforms,
  );
  const resolvedCount = form.platforms.length;
  const overCap = resolvedCount > PLATFORMS_MAX;
  // Task 284C — multi-format generation is now supported: a selection with >1
  // resolved-output group submits the additive `requested_outputs` contract and the
  // grouped worker produces one shared asset per format. The Task-283 block is
  // removed; only the no-target and over-cap (1..PLATFORMS_MAX) gates still apply.
  // Only explain the fallback while the default is actually in use (manual
  // override wins: once the user edits the count, the explanation no longer applies).
  const entryCountFallbackHint =
    entryCountReason && entryCountReason !== 'derived' && form.entryCount === AI_ENTRY_COUNT_DEFAULT
      ? AI_ENTRY_COUNT_FALLBACK_HINTS[entryCountReason]
      : null;
  return (
    <div className="card ai-calendar-form">
      <div className="ai-calendar-form__body">
        <header className="ai-calendar-form__header">
          <h2 className="ai-calendar-form__title">إنشاء كالندر بالذكاء الاصطناعي</h2>
          <span className="ai-calendar-form__hint">
            مسودة قابلة للتعديل · لا يُحفظ شيء حتى تضغط حفظ
          </span>
        </header>
        <p className="ai-calendar-form__intro">
          اختر وضع التوليد ثم حدّد إعدادات الشهر والمنصات. سيُنشئ الذكاء الاصطناعي مسودة كاملة
          تستطيع مراجعتها وتعديلها قبل الحفظ.
        </p>

        <fieldset className="ai-calendar-form__section">
          <legend className="ai-calendar-form__section-title">وضع التوليد</legend>
          <div className="ai-calendar-form__mode-grid">
            <ModeOption
              selected={form.mode === 'unified'}
              title="محتوى موحّد لكل المنصات"
              description="فكرة وتعليق واحد يُكرّر على جميع المنصات. تكلفة أقل، تنفيذ أسرع، نفس الرسالة في كل مكان."
              onSelect={() => onChange({ ...form, mode: 'unified' })}
            />
            <ModeOption
              selected={form.mode === 'platform_adapted'}
              title="محتوى مخصص لكل منصة"
              description="كل منصة تحصل على نسخة مكتوبة خصيصاً لها. جودة أعلى، تكلفة أعلى."
              onSelect={() => onChange({ ...form, mode: 'platform_adapted' })}
            />
          </div>
        </fieldset>

        <fieldset className="ai-calendar-form__section">
          <legend className="ai-calendar-form__section-title">إعدادات التوليد</legend>
          <div className="ai-calendar-form__row">
            <div className="ai-calendar-form__field">
              <label className="lbl" htmlFor="ai-calendar-month">
                الشهر <span className="ai-calendar-form__required">*</span>
              </label>
              <input
                id="ai-calendar-month"
                type="month"
                value={form.month}
                onChange={(e) => onChange({ ...form, month: e.target.value })}
              />
            </div>
            <div className="ai-calendar-form__field">
              <label className="lbl" htmlFor="ai-calendar-count">
                عدد الأفكار <span className="ai-calendar-form__required">*</span>
              </label>
              <input
                id="ai-calendar-count"
                type="number"
                min={1}
                max={30}
                value={form.entryCount}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) {
                    onChange({ ...form, entryCount: Math.max(1, Math.min(30, Math.floor(n))) });
                  }
                }}
              />
              {entryCountFallbackHint ? (
                <p
                  className="tiny"
                  style={{ marginTop: 6, color: 'var(--muted)', lineHeight: 1.5 }}
                >
                  {entryCountFallbackHint}
                </p>
              ) : null}
            </div>
            <div className="ai-calendar-form__field">
              <label className="lbl" htmlFor="ai-calendar-tz">
                المنطقة الزمنية
              </label>
              <select
                id="ai-calendar-tz"
                value={form.timezone}
                onChange={(e) => onChange({ ...form, timezone: e.target.value })}
              >
                {MANUAL_TIMEZONE_OPTIONS.some((o) => o.value === form.timezone) ? null : (
                  <option value={form.timezone}>{form.timezone}</option>
                )}
                {MANUAL_TIMEZONE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Task 283 — platform-FIRST: choose parent platforms, THEN the asset
            formats those platforms can carry. Both groups are multi-select. */}
        <fieldset className="ai-calendar-form__section">
          <legend className="ai-calendar-form__section-title">المنصات</legend>
          <div className="ai-calendar-form__chips" role="group" aria-label="المنصات">
            {AI_V1_PARENT_PLATFORMS.map((p) => {
              const on = form.parentPlatforms.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  className={on ? 'chip on' : 'chip'}
                  onClick={() => toggleParentPlatform(p)}
                  aria-pressed={on}
                >
                  {parentPlatformLabelAr(p)}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="ai-calendar-form__section">
          <legend className="ai-calendar-form__section-title">نوع المحتوى</legend>
          {/* Task 283 — render ONLY the formats the selected platforms can carry
              (e.g. TikTok offers carousel + vertical video; Instagram offers all). */}
          {compatibleFormats.size > 0 ? (
            <div className="ai-calendar-form__chips" role="group" aria-label="نوع المحتوى">
              {AI_ASSET_FORMAT_OPTIONS.filter((o) => compatibleFormats.has(o.value)).map((o) => {
                const on = form.assetFormats.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    className={on ? 'chip on' : 'chip'}
                    onClick={() => toggleAssetFormat(o.value)}
                    aria-pressed={on}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="tiny" style={{ color: 'var(--rose)', lineHeight: 1.5 }}>
              اختر منصة واحدة على الأقل لعرض أنواع المحتوى المتاحة.
            </p>
          )}
        </fieldset>

        {/* Task 283 — resolved outputs grouped by asset format: each format lists
            the publish_target_v1[] it fans out to. The generate contract caps the
            total distinct targets at PLATFORMS_MAX (block + explain, never truncate). */}
        <fieldset className="ai-calendar-form__section">
          <legend className="ai-calendar-form__section-title">الوجهات الناتجة</legend>
          {overCap ? (
            <p className="tiny" style={{ color: 'var(--rose)', lineHeight: 1.5 }}>
              الحد الأقصى {PLATFORMS_MAX} وجهات نشر، والمحدد حالياً {resolvedCount}. قلّل المنصات أو
              أنواع المحتوى المختارة.
            </p>
          ) : requestedOutputs.length > 0 ? (
            <div>
              {requestedOutputs.map((g) => (
                <p
                  key={g.format}
                  className="tiny"
                  style={{ marginTop: 6, color: 'var(--muted)', lineHeight: 1.5 }}
                >
                  <strong>{assetFormatLabelAr(g.format)}:</strong>{' '}
                  {g.targets.map(aiPlatformLabelAr).join('، ')}
                </p>
              ))}
            </div>
          ) : (
            <p className="tiny" style={{ color: 'var(--rose)', lineHeight: 1.5 }}>
              لا توجد وجهات نشر — اختر منصة ونوع محتوى متوافقين.
            </p>
          )}
        </fieldset>

        <fieldset className="ai-calendar-form__section">
          <legend className="ai-calendar-form__section-title">إرشاد إبداعي (اختياري)</legend>
          <div className="ai-calendar-form__field">
            <label className="lbl" htmlFor="ai-calendar-instruction">
              نبرة الكتابة، الموضوعات المحورية، أو أي توجيه خاص (حتى 2000 حرف)
            </label>
            <textarea
              id="ai-calendar-instruction"
              rows={3}
              value={form.instruction}
              maxLength={2000}
              onChange={(e) => onChange({ ...form, instruction: e.target.value })}
              placeholder="مثال: ركّز على قصص العملاء وأظهر الجانب الإنساني للعلامة."
            />
          </div>
        </fieldset>

        {/* Task 280J — timing is auto-distributed by the slot planner from the
            client's operating setup; each slot stays editable in the draft. */}
        <p className="tiny" style={{ color: 'var(--muted)', lineHeight: 1.5 }}>
          سيتم توزيع المواعيد تلقائياً حسب إعدادات التشغيل، ويمكن تعديل كل موعد قبل الحفظ.
        </p>

        {error ? (
          <div className="banner banner-error ai-calendar-form__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>

      <div className="ai-calendar-form__actions">
        <button type="button" className="btn-text" onClick={onCancel}>
          إلغاء
        </button>
        <button
          type="button"
          className="btn"
          onClick={onGenerate}
          disabled={resolvedCount < 1 || overCap}
        >
          توليد المسودة
        </button>
      </div>
    </div>
  );
}

function ModeOption({
  selected,
  title,
  description,
  onSelect,
}: {
  readonly selected: boolean;
  readonly title: string;
  readonly description: string;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        selected ? 'ai-calendar-form__mode-option is-selected' : 'ai-calendar-form__mode-option'
      }
      aria-pressed={selected}
    >
      <span className="ai-calendar-form__mode-option-title">{title}</span>
      <span className="ai-calendar-form__mode-option-desc">{description}</span>
    </button>
  );
}

// Task 107C — V1 platform allow-list for the MANUAL single-post form
// only. Operators reported the manual form felt overwhelming with
// every supported platform; for V1 we expose Instagram (feed/story/
// reel), TikTok, and Snapchat. Facebook is intentionally hidden from
// the manual flow. The full `PLATFORM_OPTIONS` list still drives the
// AI calendar generator + the variants display, so this constant is
// scoped to the manual card.
const MANUAL_V1_PLATFORM_OPTIONS: ReadonlyArray<ContentVariantPlatform> = [
  'instagram_feed',
  'instagram_story',
  'instagram_reel',
  'tiktok_video',
  'snapchat_story',
];

// Task 107C — Arabic-only platform labels used inside the manual
// single-post form. The global `variantPlatformLabel` keeps brand
// names in latin script ("Instagram · منشور") for cross-surface
// readability; the manual form prefers full Arabic names so
// operators don't see English vocabulary mid-flow.
function manualPlatformLabelAr(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'إنستجرام · منشور';
    case 'instagram_story':
      return 'إنستجرام · ستوري';
    case 'instagram_reel':
      return 'إنستجرام · ريل';
    case 'tiktok_video':
      return 'تيك توك · فيديو';
    case 'snapchat_story':
      return 'سناب شات · ستوري';
    default:
      return platform;
  }
}

// Task 107C — Curated timezone select for the manual single-post form.
// `value` is the IANA identifier the server expects (the payload
// shape + `DEFAULT_TIMEZONE` are unchanged); `label` is the Arabic
// surface text the operator sees. Pre-107C exposed the raw IANA
// string (`Asia/Riyadh`) as the input value, which leaked English
// into an otherwise all-Arabic flow.
interface ManualTimezoneOption {
  readonly value: string;
  readonly label: string;
}
const MANUAL_TIMEZONE_OPTIONS: ReadonlyArray<ManualTimezoneOption> = [
  { value: 'Asia/Riyadh', label: 'الرياض (UTC+03:00)' },
  { value: 'Asia/Dubai', label: 'دبي (UTC+04:00)' },
  { value: 'Asia/Kuwait', label: 'الكويت (UTC+03:00)' },
  { value: 'Asia/Qatar', label: 'الدوحة (UTC+03:00)' },
  { value: 'Africa/Cairo', label: 'القاهرة (UTC+02:00)' },
  { value: 'Africa/Casablanca', label: 'الدار البيضاء (UTC+01:00)' },
];

// Task 105A — `ManualSinglePostCard` replaces the pre-105A
// `ManualFormCard` (which opened an empty month-grid that mirrored
// the AI calendar batch). Operators reported the month-grid as
// confusing for "I just want to add one post". The new card collects
// every field needed for a single content_item + variants + calendar
// entry, then saves exactly one row through
// `save-content-calendar-draft` (origin='manual', items: [oneItem]).
//
// On success, the parent page reloads + selects the freshly-created
// content_item so the operator lands on the new post's detail panel.
//
// Task 107C — UI redesigned: full Arabic labels (no Headline/Caption/
// Body parentheticals), V1 platform list excludes Facebook, layout
// uses dedicated `.manual-post-form*` classes from `app.css`. Save
// flow + payload shape + onSaved callback are unchanged.
function ManualSinglePostCard({
  clientId,
  onCancel,
  onSaved,
}: {
  readonly clientId: string;
  readonly onCancel: () => void;
  readonly onSaved: (contentItemId: string) => void;
}) {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  // Round 105A — the manual single-post form does NOT default to the
  // first day of next month (the pre-105A `nextMonthYYYYMM()` shape
  // implied a calendar batch). It defaults to TODAY at 10:00 local
  // time so the operator can immediately see "this is a single post
  // for now".
  const [form, setForm] = useState<ManualSinglePostFormState>(() => ({
    title: '',
    itemBody: '',
    scheduledLocalDate: `${yyyy}-${mm}-${dd}`,
    scheduledLocalTime: '10:00',
    timezone: DEFAULT_TIMEZONE,
    variants: [
      {
        platform: 'instagram_feed',
        headline: '',
        caption: '',
        body: '',
        hashtagsInput: '',
        brief: defaultEmptyBrief(),
        cta: '',
      },
    ],
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePlatform = (p: ContentVariantPlatform) => {
    const exists = form.variants.find((v) => v.platform === p);
    if (exists) {
      // Refuse to remove the last platform — every post needs ≥1.
      if (form.variants.length === 1) return;
      setForm({ ...form, variants: form.variants.filter((v) => v.platform !== p) });
    } else if (form.variants.length < 4) {
      setForm({
        ...form,
        variants: [
          ...form.variants,
          {
            platform: p,
            headline: '',
            caption: '',
            body: '',
            hashtagsInput: '',
            brief: defaultEmptyBrief(),
            cta: '',
          },
        ],
      });
    }
  };

  const updateVariant = (idx: number, patch: Partial<ManualSinglePostVariantState>) => {
    const next = form.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    setForm({ ...form, variants: next });
  };

  const handleSave = async () => {
    if (saving) return;
    // FE-side validation mirrors the server's content_calendar_draft
    // gates so the operator gets a fast inline error rather than a
    // round-trip 4xx.
    if (form.title.trim().length === 0) {
      setError('العنوان مطلوب.');
      return;
    }
    if (!/^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(form.scheduledLocalDate)) {
      setError('التاريخ غير صحيح (يلزم YYYY-MM-DD).');
      return;
    }
    if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(form.scheduledLocalTime)) {
      setError('الوقت غير صحيح (يلزم HH:MM).');
      return;
    }
    if (form.variants.length < 1 || form.variants.length > 4) {
      setError('اختر بين 1 و4 منصات.');
      return;
    }
    for (const [i, v] of form.variants.entries()) {
      const storyLike = isStoryLikePlatform(v.platform);
      if (storyLike && v.body.trim().length === 0) {
        setError(`المنصة رقم ${i + 1}: نص الستوري مطلوب.`);
        return;
      }
      const hasText =
        v.headline.trim().length > 0 || v.caption.trim().length > 0 || v.body.trim().length > 0;
      if (!hasText) {
        setError(`المنصة رقم ${i + 1}: أضف عنواناً أو نصاً أو تعليقاً واحداً على الأقل.`);
        return;
      }
    }
    setError(null);
    setSaving(true);
    try {
      const offset = computeTzOffsetMinutes(
        form.scheduledLocalDate,
        form.scheduledLocalTime,
        form.timezone,
      );
      const sign = offset >= 0 ? '+' : '-';
      const abs = Math.abs(offset);
      const offsetH = String(Math.floor(abs / 60)).padStart(2, '0');
      const offsetM = String(abs % 60).padStart(2, '0');
      const isoLocal = `${form.scheduledLocalDate}T${form.scheduledLocalTime}:00${sign}${offsetH}:${offsetM}`;
      const variantsPayload: ReadonlyArray<SaveContentCalendarDraftVariantInput> =
        form.variants.map((v) => {
          const storyLike = isStoryLikePlatform(v.platform);
          const storyText = v.body.trim();
          const hashtags = storyLike
            ? []
            : v.hashtagsInput
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
                .map((s) => (s.startsWith('#') ? s : `#${s}`));
          // Task 294A — story copy has one canonical source (`body`). The
          // production brief mirrors that exact text for the designer, while
          // caption + hashtags are force-cleared even if stale form state is
          // present. Non-story variants keep the pre-294A mapping unchanged.
          const variantForSave = storyLike
            ? { ...v, brief: { ...v.brief, onImageText: storyText } }
            : v;
          // Task 108A — fold the production brief into
          // platform_specific. Empty strings are dropped so the
          // jsonb stays compact and the SQL/edge whitelist treats
          // them as "absent" rather than "must validate".
          const platformSpecific = buildPlatformSpecificFromManual(variantForSave, hashtags);
          return {
            platform: v.platform,
            headline: v.headline.trim().length > 0 ? v.headline.trim() : undefined,
            caption: storyLike || v.caption.trim().length === 0 ? undefined : v.caption.trim(),
            body:
              storyLike && storyText.length > 0
                ? storyText
                : v.body.trim().length > 0
                  ? v.body.trim()
                  : undefined,
            platform_specific:
              Object.keys(platformSpecific).length > 0 ? platformSpecific : undefined,
          };
        });
      const item: SaveContentCalendarDraftItemInput = {
        title: form.title.trim(),
        body: form.itemBody.trim().length > 0 ? form.itemBody.trim() : undefined,
        scheduled_for: isoLocal,
        timezone: form.timezone,
        variants: variantsPayload,
      };
      // Round 105A — we send EXACTLY one item. The server-side
      // `save-content-calendar-draft` accepts 1..30 items; a 1-item
      // array is the single-post path.
      const data = await saveContentCalendarDraft({
        input: {
          client_id: clientId,
          origin: 'manual',
          items: [item],
        },
      });
      const created = data.items[0]?.content_item_id ?? '';
      if (!created) {
        setError('تم الحفظ ولكن تعذر تحديد المنشور الجديد.');
        return;
      }
      onSaved(created);
    } catch (err: unknown) {
      // Task 288A — field-aware Arabic copy for save failures.
      setError(describeSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card manual-post-form">
      <div className="manual-post-form__body">
        <header className="manual-post-form__header">
          <h2 className="manual-post-form__title">إضافة منشور يدوي</h2>
          <span className="manual-post-form__hint">منشور واحد · لا يُحفظ شيء حتى تضغط حفظ</span>
        </header>
        <p className="manual-post-form__intro">
          أنشئ منشوراً واحداً مجدولاً لمنصة أو أكثر. الحفظ ينشئ مسودة جاهزة للمراجعة الداخلية.
        </p>

        <fieldset className="manual-post-form__section" disabled={saving}>
          <legend className="manual-post-form__section-title">معلومات المنشور</legend>

          <div className="manual-post-form__field">
            <label className="lbl" htmlFor="manual-post-title">
              العنوان <span className="manual-post-form__required">*</span>
            </label>
            <input
              id="manual-post-title"
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="مثال: حملة العود الكشميري · يونيو"
            />
          </div>

          <div className="manual-post-form__row">
            <div className="manual-post-form__field">
              <label className="lbl" htmlFor="manual-post-date">
                التاريخ <span className="manual-post-form__required">*</span>
              </label>
              <input
                id="manual-post-date"
                type="date"
                value={form.scheduledLocalDate}
                onChange={(e) => setForm({ ...form, scheduledLocalDate: e.target.value })}
              />
            </div>
            <div className="manual-post-form__field">
              <label className="lbl" htmlFor="manual-post-time">
                الوقت <span className="manual-post-form__required">*</span>
              </label>
              <input
                id="manual-post-time"
                type="time"
                value={form.scheduledLocalTime}
                onChange={(e) => setForm({ ...form, scheduledLocalTime: e.target.value })}
              />
            </div>
            <div className="manual-post-form__field">
              <label className="lbl" htmlFor="manual-post-tz">
                المنطقة الزمنية
              </label>
              <select
                id="manual-post-tz"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              >
                {MANUAL_TIMEZONE_OPTIONS.some((o) => o.value === form.timezone) ? null : (
                  <option value={form.timezone}>{form.timezone}</option>
                )}
                {MANUAL_TIMEZONE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="manual-post-form__field">
            <label className="lbl" htmlFor="manual-post-body">
              نص رئيسي للمنشور (اختياري)
            </label>
            <textarea
              id="manual-post-body"
              value={form.itemBody}
              onChange={(e) => setForm({ ...form, itemBody: e.target.value })}
              rows={3}
              placeholder="ملخص داخلي أو فكرة المنشور — لا يُنشر مع الأصل."
            />
          </div>
        </fieldset>

        <fieldset className="manual-post-form__section" disabled={saving}>
          <legend className="manual-post-form__section-title">
            المنصات <span className="manual-post-form__hint-inline">(اختر من 1 إلى 4)</span>
          </legend>
          <div className="manual-post-form__chips" role="group" aria-label="المنصات">
            {MANUAL_V1_PLATFORM_OPTIONS.map((p) => {
              const on = form.variants.some((v) => v.platform === p);
              return (
                <button
                  key={p}
                  type="button"
                  className={on ? 'chip on' : 'chip'}
                  onClick={() => togglePlatform(p)}
                  disabled={!on && form.variants.length >= 4}
                  aria-pressed={on}
                >
                  {manualPlatformLabelAr(p)}
                </button>
              );
            })}
          </div>
        </fieldset>

        {form.variants.map((v, idx) => (
          <fieldset key={`v-${v.platform}`} className="manual-post-form__variant" disabled={saving}>
            <legend className="manual-post-form__variant-title">
              <span className="manual-post-form__variant-dot" aria-hidden="true" />
              {manualPlatformLabelAr(v.platform)}
            </legend>
            <span className="manual-post-form__variant-badge" aria-hidden="true">
              نسخة المنصة
            </span>

            {/* Task 273 — headline kept internal (form-level); never published,
                never shown in the post preview. */}
            <div className="manual-post-form__field post-req-headline">
              <label className="lbl" htmlFor={`manual-post-headline-${idx}`}>
                العنوان <span className="post-req-headline__note">داخلي — لا يظهر في المنشور</span>
              </label>
              <input
                id={`manual-post-headline-${idx}`}
                type="text"
                value={v.headline}
                onChange={(e) => updateVariant(idx, { headline: e.target.value })}
                placeholder="عنوان قصير للتنظيم الداخلي"
              />
            </div>

            {/* Task 273/294A — two cards per variant. Feed-like variants keep the
                canonical caption + hashtag editor. Story-like variants expose one
                «نص الستوري» field in the design card and remain captionless. */}
            <div className="post-req-cards">
              <section className="post-req-card post-req-card--design">
                <div className="post-req-card__head">
                  <span className="post-req-card__head-ic" aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <path d="M12 3l2.2 4.8L19 9l-3.5 3.4.8 4.9L12 15l-4.3 2.3.8-4.9L5 9l4.8-1.2z" />
                    </svg>
                  </span>
                  طلب التصميم
                </div>
                {/* Task 108A — platform-aware production brief (نص التصميم /
                    التوجه البصري / موجز التصميم / نوع الأصل for image, the video
                    set for reels). The shared الدعوة للإجراء stays here. */}
                <ProductionBriefManualSection
                  variantIdx={idx}
                  variant={v}
                  onChangeBrief={(patch) => updateVariant(idx, { brief: { ...v.brief, ...patch } })}
                  onChangeCta={(cta) => updateVariant(idx, { cta })}
                  onChangeStoryText={(body) => updateVariant(idx, { body })}
                />
                <CaptionReference caption={v.caption} storyLike={isStoryLikePlatform(v.platform)} />
              </section>

              <section className="post-req-card post-req-card--preview">
                <div className="post-req-card__head">
                  <span className="post-req-card__head-ic" aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </span>
                  معاينة المنشور
                </div>
                <PostAppearancePreview
                  platform={v.platform}
                  caption={isStoryLikePlatform(v.platform) ? '' : v.caption}
                  hashtags={
                    isStoryLikePlatform(v.platform) ? [] : parseHashtagsInput(v.hashtagsInput)
                  }
                />
                {!isStoryLikePlatform(v.platform) ? (
                  <div className="post-req-card__copy">
                    {/* Task 273 — the single canonical caption, bound to the
                      publish-safe `caption` column (Option A). `body` is no
                      longer surfaced here; payload shape is unchanged. */}
                    <div className="manual-post-form__field">
                      <label className="lbl" htmlFor={`manual-post-caption-${idx}`}>
                        نص الكابشن الكامل
                      </label>
                      <textarea
                        id={`manual-post-caption-${idx}`}
                        value={v.caption}
                        onChange={(e) => updateVariant(idx, { caption: e.target.value })}
                        rows={4}
                        placeholder="النص الذي يظهر تحت المنشور على المنصة"
                      />
                    </div>
                    <div className="manual-post-form__field">
                      <label className="lbl" htmlFor={`manual-post-tags-${idx}`}>
                        الوسوم
                      </label>
                      <input
                        id={`manual-post-tags-${idx}`}
                        type="text"
                        value={v.hashtagsInput}
                        placeholder="كلمات مفصولة بمسافة أو فاصلة — تُضاف لها # تلقائياً"
                        onChange={(e) => updateVariant(idx, { hashtagsInput: e.target.value })}
                      />
                    </div>
                  </div>
                ) : null}
              </section>
            </div>
          </fieldset>
        ))}

        {error ? (
          <div className="banner banner-error manual-post-form__error" role="alert">
            {error}
          </div>
        ) : null}
      </div>

      <div className="manual-post-form__actions">
        <button type="button" className="btn-text" onClick={onCancel} disabled={saving}>
          إلغاء
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            void handleSave();
          }}
          disabled={saving}
        >
          {saving ? 'جارٍ الحفظ…' : 'حفظ منشور واحد'}
        </button>
      </div>
    </div>
  );
}

// Task 108A — production brief section reused inside
// `ManualSinglePostCard`. Pure presentational; consumes brief +
// cta and emits patches the parent merges into form state. The
// platform-aware field set is driven by `isVideoPlatform`.
function ProductionBriefManualSection({
  variantIdx,
  variant,
  onChangeBrief,
  onChangeCta,
  onChangeStoryText,
}: {
  readonly variantIdx: number;
  readonly variant: ManualSinglePostVariantState;
  readonly onChangeBrief: (patch: Partial<DraftVariantBriefState>) => void;
  readonly onChangeCta: (cta: string) => void;
  readonly onChangeStoryText: (body: string) => void;
}) {
  const isVideo = isVideoPlatform(variant.platform);
  const storyLike = isStoryLikePlatform(variant.platform);
  const idPrefix = `manual-brief-${variantIdx}`;
  return (
    <div className="manual-post-form__brief">
      {isVideo ? (
        <>
          <BriefField id={`${idPrefix}-hook`} label={BRIEF_LABELS_AR.hook} multiline>
            <textarea
              id={`${idPrefix}-hook`}
              rows={2}
              value={variant.brief.hook}
              onChange={(e) => onChangeBrief({ hook: e.target.value })}
              placeholder="أول 3 ثوانٍ — جملة الافتتاح أو الهوك."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-script`} label={BRIEF_LABELS_AR.script} multiline>
            <textarea
              id={`${idPrefix}-script`}
              rows={4}
              value={variant.brief.script}
              onChange={(e) => onChangeBrief({ script: e.target.value })}
              placeholder="السكربت المنطوق أو الممثَّل."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-shotlist`} label={BRIEF_LABELS_AR.shotList} multiline>
            <textarea
              id={`${idPrefix}-shotlist`}
              rows={3}
              value={variant.brief.shotList}
              onChange={(e) => onChangeBrief({ shotList: e.target.value })}
              placeholder="قائمة المشاهد بالترتيب — كل سطر مشهد."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-onscreen`} label={BRIEF_LABELS_AR.onScreenText} multiline>
            <textarea
              id={`${idPrefix}-onscreen`}
              rows={2}
              value={variant.brief.onScreenText}
              onChange={(e) => onChangeBrief({ onScreenText: e.target.value })}
              placeholder="النصوص التي تظهر على الشاشة لكل مشهد."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-visual`} label={BRIEF_LABELS_AR.visualDirection} multiline>
            <textarea
              id={`${idPrefix}-visual`}
              rows={2}
              value={variant.brief.visualDirection}
              onChange={(e) => onChangeBrief({ visualDirection: e.target.value })}
              placeholder="الستايل، الإضاءة، تكوين الكاميرا."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-audio`} label={BRIEF_LABELS_AR.audioDirection} multiline>
            <textarea
              id={`${idPrefix}-audio`}
              rows={2}
              value={variant.brief.audioDirection}
              onChange={(e) => onChangeBrief({ audioDirection: e.target.value })}
              placeholder="الموسيقى، التعليق الصوتي، المؤثرات."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-cta`} label={BRIEF_LABELS_AR.cta}>
            <input
              id={`${idPrefix}-cta`}
              type="text"
              value={variant.cta}
              onChange={(e) => onChangeCta(e.target.value)}
              placeholder="ماذا تريد المشاهد أن يفعل بعد المشاهدة؟"
            />
          </BriefField>
          <BriefField id={`${idPrefix}-duration`} label={BRIEF_LABELS_AR.durationSeconds}>
            <input
              id={`${idPrefix}-duration`}
              type="number"
              min={1}
              max={3600}
              value={variant.brief.durationSeconds}
              onChange={(e) => onChangeBrief({ durationSeconds: e.target.value })}
              placeholder="مثال: 15"
            />
          </BriefField>
        </>
      ) : (
        <>
          <BriefField
            id={`${idPrefix}-onimage`}
            label={storyLike ? 'نص الستوري' : BRIEF_LABELS_AR.onImageText}
            multiline
          >
            <textarea
              id={`${idPrefix}-onimage`}
              rows={2}
              value={storyLike ? variant.body : variant.brief.onImageText}
              onChange={(e) => {
                if (storyLike) onChangeStoryText(e.target.value);
                else onChangeBrief({ onImageText: e.target.value });
              }}
              placeholder={
                storyLike
                  ? 'النص الكامل الذي سيظهر داخل الستوري.'
                  : 'النص الذي سيُكتب على التصميم بالضبط.'
              }
            />
          </BriefField>
          <BriefField id={`${idPrefix}-visual`} label={BRIEF_LABELS_AR.visualDirection} multiline>
            <textarea
              id={`${idPrefix}-visual`}
              rows={3}
              value={variant.brief.visualDirection}
              onChange={(e) => onChangeBrief({ visualDirection: e.target.value })}
              placeholder="الستايل، الألوان، التكوين، الجو العام."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-creative`} label={BRIEF_LABELS_AR.creativeBrief} multiline>
            <textarea
              id={`${idPrefix}-creative`}
              rows={4}
              value={variant.brief.creativeBrief}
              onChange={(e) => onChangeBrief({ creativeBrief: e.target.value })}
              placeholder="موجز كامل جاهز للمصمم أو لتوليد الصور بالذكاء الاصطناعي."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-cta`} label={BRIEF_LABELS_AR.cta}>
            <input
              id={`${idPrefix}-cta`}
              type="text"
              value={variant.cta}
              onChange={(e) => onChangeCta(e.target.value)}
              placeholder="ماذا تريد المتابع أن يفعل؟"
            />
          </BriefField>
          <BriefField id={`${idPrefix}-format`} label={BRIEF_LABELS_AR.assetFormat}>
            <select
              id={`${idPrefix}-format`}
              value={variant.brief.assetFormat}
              onChange={(e) => onChangeBrief({ assetFormat: e.target.value })}
            >
              <option value="">— اختر النوع —</option>
              {ASSET_FORMAT_OPTIONS_AR.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </BriefField>
        </>
      )}
    </div>
  );
}

// Task 108A — small wrapper that puts the Arabic label above each
// brief field with consistent spacing. The `multiline` prop is a
// pure CSS hint via the data attribute (the textarea height is set
// by the consumer via `rows`).
function BriefField({
  id,
  label,
  multiline,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly multiline?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className="manual-post-form__field" data-multiline={multiline ? 'true' : undefined}>
      <label className="lbl" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

// Task 272 — split a free-text hashtag input into bare tags for the
// read-only preview only. The save path keeps its own serialization
// (`hashtagsInput → platform_specific.hashtags`); this helper never
// feeds a payload, so the two cannot drift the wire shape.
function parseHashtagsInput(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((h) => h.trim().replace(/^#+/, ''))
    .filter((h) => h.length > 0);
}

// Task 272/273 — read-only, approximate post-appearance preview rendered inside
// the "معاينة المنشور" card. Mirrors the EXISTING publish `caption` + hashtags
// into a lightweight Instagram-style card. The media slot is always blank — no
// produced asset exists at request time (Task 273: no asset-exists state). It is
// presentational only: no inputs, no onChange, no endpoint calls — `caption` +
// the save payload remain the single source of truth (stated in the disclaimer).
// Instagram-first; non-IG platforms show a short note. See
// `tasks/273-post-request-two-card-ui.md`.
function PostAppearancePreview({
  platform,
  caption,
  hashtags,
}: {
  readonly platform: ContentVariantPlatform;
  readonly caption: string;
  readonly hashtags: ReadonlyArray<string>;
}) {
  if (!isInstagramPlatform(platform)) {
    return <p className="post-preview__na">معاينة شكل المنشور متاحة لإنستاجرام حالياً.</p>;
  }
  const captionText = caption.trim();
  const tags = hashtags.map((h) => h.trim()).filter((h) => h.length > 0);
  // Approximate framing only: 1/1 feed vs 9/16 story+reel.
  const aspect = platform === 'instagram_feed' ? '1 / 1' : '9 / 16';
  const ratioLabel = platform === 'instagram_feed' ? '١:١' : '٩:١٦';
  return (
    <div className="post-preview" aria-label="معاينة شكل المنشور">
      <div className="post-preview__card" aria-hidden="true">
        <div className="post-preview__head">
          <span className="post-preview__avatar" />
          <span className="post-preview__handle">حساب العميل</span>
        </div>
        <div className="post-preview__media" style={{ aspectRatio: aspect }}>
          <span className="post-preview__media-ic" aria-hidden="true">
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="8.5" cy="10" r="1.5" />
              <path d="M21 16l-5-5L5 19" />
            </svg>
          </span>
          <span className="post-preview__media-line1">بانتظار تنفيذ التصميم</span>
          <span className="post-preview__media-line2">لم يُرفع التصميم بعد</span>
          <span className="post-preview__ratio">نسبة العرض {ratioLabel}</span>
        </div>
        <div className="post-preview__copy">
          {/* Task 285A — stories publish without a feed caption, so the preview
              shows the captionless cue instead of rendering caption as post copy. */}
          {isStoryLikePlatform(platform) ? (
            <p className="post-preview__caption" data-empty="true">
              الستوري يُنشر بدون كابشن — النص على التصميم نفسه.
            </p>
          ) : captionText.length > 0 ? (
            <p className="post-preview__caption">{captionText}</p>
          ) : (
            <p className="post-preview__caption" data-empty="true">
              اكتب «نص الكابشن الكامل» لرؤية المعاينة.
            </p>
          )}
          {tags.length > 0 ? (
            <p className="post-preview__tags">{tags.map((t) => `#${t}`).join(' ')}</p>
          ) : null}
        </div>
      </div>
      <p className="post-preview__disclaimer">
        {isStoryLikePlatform(platform)
          ? 'معاينة تقريبية لإطار الستوري — لا يُنشر كابشن مع الستوري؛ النص يكون على التصميم.'
          : 'معاينة تقريبية لشكل المنشور — النص النهائي يُعتمد من «نص الكابشن الكامل»، وليست هذه المعاينة مصدر الحقيقة.'}
      </p>
    </div>
  );
}

// Task 273 — read-only reference to the publish caption, shown inside the
// "طلب التصميم" card so the designer sees the caption as context. It is NOT an
// editable field and NOT on-design text (stated in the hint). Reads `caption`
// only; renders nothing editable and calls no endpoint.
// Task 285A — `storyLike` swaps the head + hint so a story/Snapchat variant
// reads as captionless (copy on the design) instead of contradicting the
// generic "this caption is published with the post" copy. Display-only.
function CaptionReference({
  caption,
  storyLike = false,
}: {
  readonly caption: string;
  readonly storyLike?: boolean;
}) {
  const text = caption.trim();
  return (
    <div className="post-req-ref">
      <div className="post-req-ref__head">
        <svg
          aria-hidden="true"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        >
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        {storyLike ? 'نص الستوري — لا يُنشر ككابشن' : 'نص الكابشن المرافق — للاطلاع فقط'}
      </div>
      <blockquote
        className="post-req-ref__quote"
        data-empty={text.length === 0 ? 'true' : undefined}
      >
        {text.length > 0 ? text : 'لا يوجد نص كابشن لهذه النسخة.'}
      </blockquote>
      <p className="post-req-ref__hint">
        {storyLike
          ? 'الستوري يُنشر بدون كابشن — النص الكامل موجود في «نص الستوري» أعلاه.'
          : 'هذا هو نص الكابشن الذي يُنشر مع المنشور، معروض للسياق فقط — وليس نصاً يُكتب على التصميم. النص الذي يظهر على التصميم هو «نص التصميم» أعلاه.'}
      </p>
    </div>
  );
}

// Task 108A — production brief section reused inside the AI
// `DraftEditor` per variant. Same field set as the manual form but
// scoped to the `.ai-calendar-form*` namespace so styling stays
// consistent with the rest of the AI review screen. The cta lives
// inside `brief` for the AI flow because the model writes it under
// `production_brief.cta` (see worker schema below).
// Task 286A — read-only rendering of the shared brief, used when the shared card
// is reused on the saved-content detail (no editor). Shows only the populated
// fields (label + value), following the same platform-based video/static field
// family as the editor; empty fields are skipped.
function ProductionBriefReadOnly({
  brief,
  isVideo,
}: {
  readonly brief: DraftVariantBriefState;
  readonly isVideo: boolean;
}) {
  const rows: Array<{ readonly label: string; readonly value: string }> = [];
  const push = (label: string, raw: string) => {
    const t = raw.trim();
    if (t.length > 0) rows.push({ label, value: t });
  };
  if (isVideo) {
    push(BRIEF_LABELS_AR.hook, brief.hook);
    push(BRIEF_LABELS_AR.script, brief.script);
    push(BRIEF_LABELS_AR.shotList, brief.shotList);
    push(BRIEF_LABELS_AR.onScreenText, brief.onScreenText);
    push(BRIEF_LABELS_AR.visualDirection, brief.visualDirection);
    push(BRIEF_LABELS_AR.audioDirection, brief.audioDirection);
    push(BRIEF_LABELS_AR.durationSeconds, brief.durationSeconds);
  } else {
    push(BRIEF_LABELS_AR.onImageText, brief.onImageText);
    push(BRIEF_LABELS_AR.visualDirection, brief.visualDirection);
    push(BRIEF_LABELS_AR.creativeBrief, brief.creativeBrief);
    const fmt = brief.assetFormat.trim();
    if (fmt) {
      push(
        BRIEF_LABELS_AR.assetFormat,
        ASSET_FORMAT_OPTIONS_AR.find((o) => o.value === fmt)?.label ?? fmt,
      );
    }
  }
  return (
    <div className="ai-draft-review__brief">
      {rows.length === 0 ? (
        <p className="ai-draft-review__empty-inline">لا يوجد موجز تصميم بعد.</p>
      ) : (
        rows.map((r) => (
          <div className="ai-draft-review__readrow" key={r.label}>
            <div className="ai-draft-review__field-label">{r.label}</div>
            <div className="ai-draft-review__readtext">{r.value}</div>
          </div>
        ))
      )}
    </div>
  );
}

function ProductionBriefDraftSection({
  itemIdx,
  variantIdx,
  variant,
  onChangeBrief,
}: {
  readonly itemIdx: number;
  readonly variantIdx: number;
  readonly variant: DraftVariantState;
  // Task 286A — optional: when absent the brief renders READ-ONLY (the shared
  // card reused on the saved-content detail has no inline brief editor).
  readonly onChangeBrief?: (patch: Partial<DraftVariantBriefState>) => void;
}) {
  if (!onChangeBrief) {
    return (
      <ProductionBriefReadOnly brief={variant.brief} isVideo={isVideoPlatform(variant.platform)} />
    );
  }
  // Task 285B — the brief field set follows the variant's PLATFORM, NOT the
  // idea's asset format, because the save serializer (briefToPlatformSpecific)
  // is platform-based: it persists the video field family for instagram_reel /
  // tiktok_video and the static family for everything else. Driving the editor
  // from the format instead would expose fields the serializer silently drops
  // (e.g. hook/script on a snapchat_story variant), losing edits. The shared
  // brief is bound to the representative variant (item.variants[0]), which is
  // the first resolved target — instagram_reel for a vertical_video idea, so the
  // common case still shows the video field set; `visual_direction` is shared by
  // both families and propagates to every variant.
  const isVideo = isVideoPlatform(variant.platform);
  const idPrefix = `ai-brief-${itemIdx}-${variantIdx}`;
  return (
    <div className="ai-draft-review__brief">
      {isVideo ? (
        <>
          <BriefField id={`${idPrefix}-hook`} label={BRIEF_LABELS_AR.hook} multiline>
            <textarea
              id={`${idPrefix}-hook`}
              rows={2}
              value={variant.brief.hook}
              onChange={(e) => onChangeBrief({ hook: e.target.value })}
              placeholder="أول 3 ثوانٍ — جملة الافتتاح."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-script`} label={BRIEF_LABELS_AR.script} multiline>
            <textarea
              id={`${idPrefix}-script`}
              rows={3}
              value={variant.brief.script}
              onChange={(e) => onChangeBrief({ script: e.target.value })}
              placeholder="السكربت الكامل المنطوق."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-shotlist`} label={BRIEF_LABELS_AR.shotList} multiline>
            <textarea
              id={`${idPrefix}-shotlist`}
              rows={3}
              value={variant.brief.shotList}
              onChange={(e) => onChangeBrief({ shotList: e.target.value })}
              placeholder="قائمة المشاهد بالترتيب."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-onscreen`} label={BRIEF_LABELS_AR.onScreenText} multiline>
            <textarea
              id={`${idPrefix}-onscreen`}
              rows={2}
              value={variant.brief.onScreenText}
              onChange={(e) => onChangeBrief({ onScreenText: e.target.value })}
              placeholder="النصوص التي تظهر على الشاشة."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-visual`} label={BRIEF_LABELS_AR.visualDirection} multiline>
            <textarea
              id={`${idPrefix}-visual`}
              rows={2}
              value={variant.brief.visualDirection}
              onChange={(e) => onChangeBrief({ visualDirection: e.target.value })}
              placeholder="الستايل، الإضاءة، التكوين."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-audio`} label={BRIEF_LABELS_AR.audioDirection} multiline>
            <textarea
              id={`${idPrefix}-audio`}
              rows={2}
              value={variant.brief.audioDirection}
              onChange={(e) => onChangeBrief({ audioDirection: e.target.value })}
              placeholder="الموسيقى، التعليق الصوتي، المؤثرات."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-duration`} label={BRIEF_LABELS_AR.durationSeconds}>
            <input
              id={`${idPrefix}-duration`}
              type="number"
              min={1}
              max={3600}
              value={variant.brief.durationSeconds}
              onChange={(e) => onChangeBrief({ durationSeconds: e.target.value })}
              placeholder="مثال: 15"
            />
          </BriefField>
        </>
      ) : (
        <>
          {/* Task 285A — surface the chosen asset format (e.g. كاروسيل) so a
              carousel brief reads as a populated, intentional carousel and not
              an empty static card. Display-only; the <select> below still owns it. */}
          {variant.brief.assetFormat ? (
            <div className="ai-draft-review__format-badge">
              {BRIEF_LABELS_AR.assetFormat}:{' '}
              {ASSET_FORMAT_OPTIONS_AR.find((o) => o.value === variant.brief.assetFormat)?.label ??
                variant.brief.assetFormat}
            </div>
          ) : null}
          <BriefField id={`${idPrefix}-onimage`} label={BRIEF_LABELS_AR.onImageText} multiline>
            <textarea
              id={`${idPrefix}-onimage`}
              rows={2}
              value={variant.brief.onImageText}
              onChange={(e) => onChangeBrief({ onImageText: e.target.value })}
              placeholder="النص الذي سيُكتب على التصميم."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-visual`} label={BRIEF_LABELS_AR.visualDirection} multiline>
            <textarea
              id={`${idPrefix}-visual`}
              rows={3}
              value={variant.brief.visualDirection}
              onChange={(e) => onChangeBrief({ visualDirection: e.target.value })}
              placeholder="الستايل، الألوان، التكوين."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-creative`} label={BRIEF_LABELS_AR.creativeBrief} multiline>
            <textarea
              id={`${idPrefix}-creative`}
              rows={3}
              value={variant.brief.creativeBrief}
              onChange={(e) => onChangeBrief({ creativeBrief: e.target.value })}
              placeholder="موجز كامل جاهز للمصمم."
            />
          </BriefField>
          <BriefField id={`${idPrefix}-format`} label={BRIEF_LABELS_AR.assetFormat}>
            <select
              id={`${idPrefix}-format`}
              value={variant.brief.assetFormat}
              onChange={(e) => onChangeBrief({ assetFormat: e.target.value })}
            >
              <option value="">— اختر النوع —</option>
              {ASSET_FORMAT_OPTIONS_AR.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </BriefField>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Task 285B — generated-content review redesign. One generated idea
// (DraftItemState) is ONE shared asset/brief (flattenGroupedToPlatformAdapted
// emits one item per asset_format; every variant carries the identical
// shared_brief + asset_format). So we render ONE idea card → a shared
// asset/brief panel → platform variants as chips → one selected-variant
// detail panel, instead of one full card per platform. FE/display only;
// per-platform copy still edits its own variant and the save payload shape
// is unchanged (see IdeaReviewCard / SharedAssetPanel).
// ─────────────────────────────────────────────────────────────────────

/** Two-letter mono badge for a parent platform (IG / TT / SC). */
function aiPlatformMono(parent: SocialPlatform): string {
  switch (parent) {
    case 'instagram':
      return 'IG';
    case 'tiktok':
      return 'TT';
    case 'snapchat':
      return 'SC';
    default:
      return parent.slice(0, 2).toUpperCase();
  }
}

/** The shared asset format for a generated idea. All variants of one item carry
 *  the same `asset_format` (shared_brief), so we read the first that has one,
 *  then fall back to inferring from the variant platforms (unified / legacy). */
function draftItemAssetFormat(item: DraftItemState): AssetFormat | null {
  for (const v of item.variants) {
    const f = v.brief.assetFormat;
    if (f === 'static_image' || f === 'carousel' || f === 'vertical_video' || f === 'story') {
      return f;
    }
  }
  // Only instagram_reel is unambiguously vertical_video. tiktok_video is also a
  // carousel target, so we never infer the format from it alone (e.g. an empty
  // item appended to a carousel draft) — fall through to the neutral label.
  if (item.variants.some((v) => v.platform === 'instagram_reel')) return 'vertical_video';
  if (item.variants.length > 0 && item.variants.every((v) => isStoryLikePlatform(v.platform))) {
    return 'story';
  }
  return null;
}

/** Shared-asset label / sub-label / aspect ratio for the asset panel. */
function assetKindMeta(format: AssetFormat | null): {
  readonly label: string;
  readonly sub: string;
  readonly ratio: string;
} {
  switch (format) {
    case 'vertical_video':
      return {
        label: 'فيديو واحد مشترك · 9:16',
        sub: 'تُستخدم له نسخ نصية حسب المنصة',
        ratio: '9:16',
      };
    case 'carousel':
      return { label: 'كاروسيل / عرض شرائح', sub: 'تصميم واحد مشترك لكل المنصات', ratio: '4:5' };
    case 'story':
      return {
        label: 'ستوري · محتوى داخل التصميم',
        sub: 'النص جزء من التصميم — لا يوجد كابشن منشور',
        ratio: '9:16',
      };
    case 'static_image':
      return { label: 'تصميم ثابت مشترك', sub: 'تصميم واحد لكل المنصات', ratio: '4:5' };
    default:
      return { label: 'أصل مشترك', sub: 'أصل واحد لكل المنصات', ratio: '4:5' };
  }
}

// Task 285B — designer-facing recommended canvas size for a (format, target).
// The size depends on BOTH the asset format and the placement, NOT the target
// alone (e.g. tiktok_video is 1080×1920 for vertical_video but 1080×1080 for a
// carousel / photo slideshow):
//   - vertical video, every story, and any story/Snapchat placement → 1080×1920 (9:16);
//   - TikTok carousel / photo slideshow → 1080×1080 (1:1);
//   - Instagram feed / carousel portrait (and other static placements) → 1080×1350 (4:5).
function recommendedCanvasSize(
  format: AssetFormat | null,
  platform: ContentVariantPlatform,
): string {
  if (format === 'vertical_video' || format === 'story' || isStoryLikePlatform(platform)) {
    return '1080×1920';
  }
  if (platform === 'tiktok_video') return '1080×1080';
  return '1080×1350';
}

// Task 285B — recommended canvas size(s) on the shared-asset panel. One shared
// line when every variant needs the same size; otherwise a compact per-platform
// list (e.g. carousel → Instagram 1080×1350 + TikTok 1080×1080). Display-only.
function SharedCanvasSizes({
  item,
  format,
}: {
  readonly item: DraftItemState;
  readonly format: AssetFormat | null;
}) {
  const rows = item.variants.map((v) => ({
    parent: targetToPlatform(v.platform),
    size: recommendedCanvasSize(format, v.platform),
  }));
  if (rows.length === 0) return null;
  const uniqueSizes = Array.from(new Set(rows.map((r) => r.size)));
  if (uniqueSizes.length === 1) {
    const descriptor = format ? assetFormatLabelAr(format) : null;
    return (
      <div className="ai-draft-review__canvas">
        <span className="ai-draft-review__canvas-label">المقاس المطلوب:</span>{' '}
        <span className="ai-draft-review__canvas-size">{uniqueSizes[0]}</span>
        {descriptor ? <span className="ai-draft-review__canvas-desc"> · {descriptor}</span> : null}
      </div>
    );
  }
  // Different sizes per placement — one row per parent platform (1 target each).
  const byParent: Array<{ readonly parent: SocialPlatform; readonly size: string }> = [];
  const seen = new Set<SocialPlatform>();
  for (const r of rows) {
    if (!seen.has(r.parent)) {
      seen.add(r.parent);
      byParent.push(r);
    }
  }
  return (
    <div className="ai-draft-review__canvas">
      <div className="ai-draft-review__canvas-label">المقاسات المطلوبة</div>
      <ul className="ai-draft-review__canvas-list">
        {byParent.map((r) => (
          <li key={r.parent}>
            <span className="ai-draft-review__canvas-plat">{parentPlatformLabelAr(r.parent)}</span>
            <span className="ai-draft-review__canvas-size">{r.size}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Task 285B — pending-asset placeholder: a deliberate "أصل مطلوب" state with the
// shared aspect ratio, NOT a dead blank. The next-step hint lives in SharedAssetPanel.
function AssetPendingPreview({ ratio }: { readonly ratio: string }) {
  const ratioCls =
    ratio === '9:16'
      ? 'ai-draft-review__asset-preview--9-16'
      : 'ai-draft-review__asset-preview--4-5';
  return (
    <div className={`ai-draft-review__asset-preview ${ratioCls}`}>
      <div className="ai-draft-review__asset-stripes" aria-hidden="true" />
      <div className="ai-draft-review__asset-body">
        <span className="ai-draft-review__asset-pending">أصل مطلوب</span>
        <span className="ai-draft-review__asset-ratio">{ratio}</span>
      </div>
    </div>
  );
}

// Task 285B — concise shared-concept band: core idea (editable `item.body`),
// hook/objective + shared visual direction (read-only, from the shared brief),
// and the asset requirement (derived). Missing values show an empty state.
// Task 286I — client-aware: `isClient` renders the LIMITED client concept band
// (نوع المحتوى / فكرة المحتوى / الافتتاحية) — always read-only, simple labels,
// NO visual direction, NO required-asset/canvas production framing. The internal
// 4-cell band below is unchanged for draft/saved.
function IdeaConceptSummary({
  item,
  idx,
  format,
  onUpdateItem,
  isClient,
}: {
  readonly item: DraftItemState;
  readonly idx: number;
  readonly format: AssetFormat | null;
  // Task 286A — optional: absent ⇒ the core idea renders read-only (saved/client).
  readonly onUpdateItem?: (idx: number, patch: Partial<DraftItemState>) => void;
  readonly isClient?: boolean;
}) {
  const brief = item.variants[0]?.brief;
  const meta = assetKindMeta(format);
  const readCell = (value: string | undefined) =>
    value && value.trim().length > 0 ? (
      <div className="ai-draft-review__concept-val">{value}</div>
    ) : (
      <div className="ai-draft-review__concept-val ai-draft-review__empty">—</div>
    );
  if (isClient) {
    // Task 286I — the client-safe concept summary. Data holds ONLY what the 286I
    // loader projection delivers to clients (item body + brief hook/assetFormat via
    // clientConceptBriefFromPlatformSpecific); labels are the simple client wording.
    // `assetFormatLabelAr` (صورة ثابتة / كاروسيل / فيديو عمودي / ستوري) — never
    // `assetKindMeta`, whose labels carry ratio/canvas production framing. Read-only
    // regardless of callbacks.
    return (
      <div className="ai-draft-review__concept" data-client-concept="true">
        <div className="ai-draft-review__concept-cell">
          <div className="ai-draft-review__concept-label">نوع المحتوى</div>
          {readCell(format ? assetFormatLabelAr(format) : undefined)}
        </div>
        <div className="ai-draft-review__concept-cell">
          <div className="ai-draft-review__concept-label">فكرة المحتوى</div>
          {readCell(item.body)}
        </div>
        <div className="ai-draft-review__concept-cell">
          <div className="ai-draft-review__concept-label">الافتتاحية</div>
          {readCell(brief?.hook)}
        </div>
      </div>
    );
  }
  return (
    <div className="ai-draft-review__concept">
      <div className="ai-draft-review__concept-cell">
        <div className="ai-draft-review__concept-label">الفكرة الأساسية</div>
        {onUpdateItem ? (
          <textarea
            className="ai-draft-review__concept-input"
            rows={2}
            value={item.body}
            onChange={(e) => onUpdateItem(idx, { body: e.target.value })}
            placeholder="ملخص داخلي للفكرة — لا يُنشر مع الأصل."
          />
        ) : (
          readCell(item.body)
        )}
      </div>
      <div className="ai-draft-review__concept-cell">
        <div className="ai-draft-review__concept-label">الهدف / الهوك</div>
        {readCell(brief?.hook)}
      </div>
      <div className="ai-draft-review__concept-cell">
        <div className="ai-draft-review__concept-label">الاتجاه البصري المشترك</div>
        {readCell(brief?.visualDirection)}
      </div>
      <div className="ai-draft-review__concept-cell">
        <div className="ai-draft-review__concept-label">الأصل المطلوب</div>
        <div className="ai-draft-review__concept-val">{meta.label}</div>
      </div>
    </div>
  );
}

// Task 285B — compact platform selector (one chip per variant). Selecting a
// chip swaps the single detail panel; it does NOT duplicate the content card.
function VariantChips({
  variants,
  selectedIdx,
  onSelect,
}: {
  readonly variants: ReadonlyArray<DraftVariantState>;
  readonly selectedIdx: number;
  readonly onSelect: (vIdx: number) => void;
}) {
  return (
    <section className="ai-draft-review__panel">
      <div className="ai-draft-review__eyebrow">نسخ المنصات</div>
      <div className="ai-draft-review__chips">
        {variants.map((v, vIdx) => {
          const parent = targetToPlatform(v.platform);
          const hasCaption = !isStoryLikePlatform(v.platform);
          const active = vIdx === selectedIdx;
          return (
            <button
              key={`${v.platform}-${vIdx}`}
              type="button"
              className={`ai-draft-review__chip${active ? ' ai-draft-review__chip--active' : ''}`}
              aria-pressed={active}
              onClick={() => onSelect(vIdx)}
            >
              <span className="ai-draft-review__chip-mono">{aiPlatformMono(parent)}</span>
              <span className="ai-draft-review__chip-main">
                <span className="ai-draft-review__chip-name">{parentPlatformLabelAr(parent)}</span>
                <span className="ai-draft-review__chip-target">
                  {aiPlatformLabelAr(v.platform)}
                </span>
              </span>
              <span className="ai-draft-review__chip-meta">
                <span
                  className={`ai-draft-review__chip-dot${hasCaption ? ' ai-draft-review__chip-dot--on' : ''}`}
                  aria-hidden="true"
                />
                {hasCaption ? 'كابشن' : 'بدون كابشن'}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// Task 285B — the shared asset / design-brief panel: ONE per idea. Shows the
// shared asset kind + ratio + pending state + next-step hint, then the shared
// brief (ProductionBriefDraftSection bound to the representative variant; edits
// write to ALL variants so the shared asset stays in sync — payload unchanged).
function SharedAssetPanel({
  item,
  idx,
  format,
  mode,
  selectedVariant,
  mediaSlot,
  onUpdateSharedBrief,
}: {
  readonly item: DraftItemState;
  readonly idx: number;
  readonly format: AssetFormat | null;
  // Task 286B — client mode shows ONLY the produced-asset media preview; the
  // internal asset-kind header, required canvas sizes, and the design brief are
  // production-only and hidden.
  readonly mode: ReviewCardMode;
  readonly selectedVariant: DraftVariantState | undefined;
  // Task 286A — the asset/media region is a SLOT: draft injects AssetPendingPreview,
  // saved injects MediaPreviewSlot + attach actions for the selected variant.
  readonly mediaSlot: (v: DraftVariantState) => ReactNode;
  // Task 286A — optional: absent ⇒ the shared brief renders read-only (saved).
  readonly onUpdateSharedBrief?: (idx: number, patch: Partial<DraftVariantBriefState>) => void;
}) {
  const isClient = mode === 'client';
  const meta = assetKindMeta(format);
  const rep = item.variants[0];
  // Task 286A — the EDITABLE shared brief (draft) binds to the representative
  // variant because edits write to ALL variants (the shared asset stays in sync).
  // The READ-ONLY brief (saved) instead follows the SELECTED variant, so each
  // platform shows its own saved platform_specific brief.
  const briefVariant = onUpdateSharedBrief ? rep : (selectedVariant ?? rep);
  return (
    <section className="ai-draft-review__panel ai-draft-review__asset-panel">
      {/* Task 286B — client mode shows only the produced-asset preview. The
          internal asset-kind header, required canvas sizes, and the design brief
          are production-only and hidden from clients. */}
      <div className="ai-draft-review__eyebrow">{isClient ? 'المعاينة' : 'الأصل المشترك'}</div>
      {isClient ? null : (
        <div className="ai-draft-review__asset-head">
          <span className="ai-draft-review__asset-icon" aria-hidden="true">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <path d="M3 15l5-5 4 4 3-3 6 6" />
            </svg>
          </span>
          <div>
            <div className="ai-draft-review__asset-label">{meta.label}</div>
            <div className="ai-draft-review__asset-sub">{meta.sub}</div>
          </div>
        </div>
      )}
      {selectedVariant ? mediaSlot(selectedVariant) : null}
      {isClient ? null : <SharedCanvasSizes item={item} format={format} />}
      {!isClient && briefVariant ? (
        <div className="ai-draft-review__shared-brief">
          <div className="ai-draft-review__eyebrow">موجز التصميم المشترك</div>
          <ProductionBriefDraftSection
            itemIdx={idx}
            variantIdx={0}
            variant={briefVariant}
            onChangeBrief={
              onUpdateSharedBrief ? (patch) => onUpdateSharedBrief(idx, patch) : undefined
            }
          />
        </div>
      ) : null}
    </section>
  );
}

// Task 285B — the selected platform's detail panel. Per-platform copy edits its
// OWN variant (onUpdateVariant). Story/Snapchat: no published-caption field —
// a deliberate captionless state + the design/story copy (body). CTA + platform
// notes are not in the AI data contract, so they show intentional empty states
// (making them editable would change the save payload — out of scope).
function SelectedVariantDetail({
  item,
  idx,
  vIdx,
  mode,
  onUpdateVariant,
}: {
  readonly item: DraftItemState;
  readonly idx: number;
  readonly vIdx: number;
  // Task 286B — client mode hides internal-only fields (the internal headline,
  // the CTA/platform-notes workflow row, and the internal variant-status pill).
  readonly mode: ReviewCardMode;
  // Task 286A — optional: absent ⇒ all copy renders READ-ONLY (saved/client).
  readonly onUpdateVariant?: (idx: number, vIdx: number, patch: Partial<DraftVariantState>) => void;
}) {
  const isClient = mode === 'client';
  const v = item.variants[vIdx];
  if (!v) return null;
  const story = isStoryLikePlatform(v.platform);
  const parent = targetToPlatform(v.platform);
  const readText = (value: string, emptyLabel: string) => {
    const t = value.trim();
    return t.length > 0 ? (
      <div className="ai-draft-review__readtext">{t}</div>
    ) : (
      <div className="ai-draft-review__empty-inline">{emptyLabel}</div>
    );
  };
  const headlineField = isClient ? null : onUpdateVariant ? (
    <div className="ai-draft-review__field post-req-headline">
      <label className="ai-draft-review__field-label" htmlFor={`ai-v-headline-${idx}-${vIdx}`}>
        العنوان <span className="post-req-headline__note">داخلي — لا يظهر في المنشور</span>
      </label>
      <input
        id={`ai-v-headline-${idx}-${vIdx}`}
        className="ai-draft-review__inp"
        type="text"
        value={v.headline}
        onChange={(e) => onUpdateVariant(idx, vIdx, { headline: e.target.value })}
        placeholder="عنوان قصير للتنظيم الداخلي"
      />
    </div>
  ) : v.headline.trim().length > 0 ? (
    <div className="ai-draft-review__field post-req-headline">
      <div className="ai-draft-review__field-label">
        العنوان <span className="post-req-headline__note">داخلي — لا يظهر في المنشور</span>
      </div>
      {readText(v.headline, '')}
    </div>
  ) : null;
  // Task 286B — CTA + platform-notes are internal; hidden from clients.
  // Task 286F — the saved / staff card surfaces the real production CTA
  // (`platform_specific.cta`) when present; otherwise the placeholder. Rendered for
  // BOTH story and non-story variants (the old static brief showed CTA for stories
  // too) via this shared const, mirroring `headlineField`. The AI draft + the
  // client-safe adapter leave `cta` undefined, so it stays a placeholder there.
  const ctaNotesField = isClient ? null : (
    <div className="ai-draft-review__field-row">
      <div className="ai-draft-review__field">
        <div className="ai-draft-review__field-label">دعوة لاتخاذ إجراء (CTA)</div>
        {readText(v.cta ?? '', 'يُضاف ضمن الكابشن أعلاه')}
      </div>
      <div className="ai-draft-review__field">
        <div className="ai-draft-review__field-label">الرابط</div>
        {readText(v.link ?? '', 'لا يوجد رابط')}
      </div>
      <div className="ai-draft-review__field">
        <div className="ai-draft-review__field-label">ملاحظات المنصة</div>
        <div className="ai-draft-review__empty-inline">لا توجد ملاحظات</div>
      </div>
    </div>
  );
  return (
    <section className="ai-draft-review__panel ai-draft-review__detail">
      <div className="ai-draft-review__detail-head">
        <div>
          <div className="ai-draft-review__detail-platform">{parentPlatformLabelAr(parent)}</div>
          <div className="ai-draft-review__detail-target">{aiPlatformLabelAr(v.platform)}</div>
        </div>
        {v.status && !isClient ? (
          <div className="ai-draft-review__detail-badges">
            <span className={`pill pill-${variantStatusVariant(v.status)}`}>
              {variantStatusLabel(v.status)}
            </span>
            <span
              className={`ai-draft-review__badge${story ? ' ai-draft-review__badge--muted' : ' ai-draft-review__badge--ok'}`}
            >
              {story ? 'بدون كابشن منشور' : 'كابشن منشور'}
            </span>
          </div>
        ) : (
          <span
            className={`ai-draft-review__badge${story ? ' ai-draft-review__badge--muted' : ' ai-draft-review__badge--ok'}`}
          >
            {story ? 'بدون كابشن منشور' : 'كابشن منشور'}
          </span>
        )}
      </div>

      {story ? (
        <>
          <div className="ai-draft-review__captionless">
            <div className="ai-draft-review__captionless-title">لا يوجد كابشن منشور لهذا النوع</div>
            <div className="ai-draft-review__captionless-sub">
              النص جزء من التصميم نفسه — لا يُنشر كنص منفصل.
            </div>
          </div>
          {isClient ? (
            // Task 286K — the story's in-design text IS what the client approves
            // (stories publish captionless). Public label, read-only; hidden
            // entirely when empty instead of a confusing internal empty state.
            v.body.trim().length > 0 ? (
              <div className="ai-draft-review__field">
                <div className="ai-draft-review__field-label">نص الستوري</div>
                {readText(v.body, '')}
              </div>
            ) : null
          ) : (
            <div className="ai-draft-review__field">
              <label className="ai-draft-review__field-label" htmlFor={`ai-v-story-${idx}-${vIdx}`}>
                نص داخل التصميم / الستوري
              </label>
              {onUpdateVariant ? (
                <textarea
                  id={`ai-v-story-${idx}-${vIdx}`}
                  className="ai-draft-review__ta"
                  rows={5}
                  value={v.body}
                  onChange={(e) => onUpdateVariant(idx, vIdx, { body: e.target.value })}
                  placeholder="النص الذي يظهر داخل التصميم / الستوري."
                />
              ) : (
                readText(v.body, 'لا يوجد نص داخل التصميم.')
              )}
            </div>
          )}
          {ctaNotesField}
          {headlineField}
        </>
      ) : (
        <>
          <div className="ai-draft-review__field">
            <label className="ai-draft-review__field-label" htmlFor={`ai-v-caption-${idx}-${vIdx}`}>
              الكابشن المنشور
            </label>
            {onUpdateVariant ? (
              <textarea
                id={`ai-v-caption-${idx}-${vIdx}`}
                className="ai-draft-review__ta"
                rows={5}
                value={v.caption}
                onChange={(e) => onUpdateVariant(idx, vIdx, { caption: e.target.value })}
                placeholder="النص الذي يظهر تحت المنشور على المنصة."
              />
            ) : (
              readText(v.caption, 'لا يوجد نص كابشن.')
            )}
          </div>
          <div className="ai-draft-review__field">
            <label className="ai-draft-review__field-label" htmlFor={`ai-v-tags-${idx}-${vIdx}`}>
              الهاشتاقات
            </label>
            {onUpdateVariant ? (
              <input
                id={`ai-v-tags-${idx}-${vIdx}`}
                className="ai-draft-review__inp"
                type="text"
                value={v.hashtags.join(', ')}
                placeholder="كلمات مفصولة بفاصلة — تُضاف لها # تلقائياً"
                onChange={(e) =>
                  onUpdateVariant(idx, vIdx, {
                    hashtags: e.target.value
                      .split(',')
                      .map((h) => h.trim().replace(/^#/, ''))
                      .filter((h) => h.length > 0),
                  })
                }
              />
            ) : (
              readText(v.hashtags.map((h) => `#${h}`).join('  '), 'لا توجد هاشتاقات')
            )}
          </div>
          {ctaNotesField}
          {headlineField}
        </>
      )}
    </section>
  );
}

// Task 285B — one parent idea card: header (index, editable title, format
// badge, draft status, editable date/time), the concept band, then a body grid
// — selected-variant detail (primary, right in RTL) + platform chips & shared
// asset/brief (secondary, left). One card per idea, not one per platform.
// Task 286A — the canonical content-review card is mode-aware. Editing is driven
// by callback PRESENCE (absent ⇒ read-only), not by `mode`; `mode` only governs
// the index badge, the draft "مسودة" tag, and the saved/client status pill. The
// asset/media region is a render-prop slot so each surface injects its own
// (draft: AssetPendingPreview; saved: MediaPreviewSlot + attach).
type ReviewCardMode = 'draft' | 'saved' | 'client';

// Task 286F — EXPORTED so the staff production-task workspaces (designer / video
// producer) reuse this ONE canonical card. The definition stays here (its many
// sub-components + helpers live in this module); other pages import it by name.
export function IdeaReviewCard({
  item,
  idx,
  mode,
  saving,
  canRemove,
  statusPill,
  mediaSlot,
  onUpdateItem,
  onUpdateVariant,
  onUpdateSharedBrief,
  onRemoveItem,
}: {
  readonly item: DraftItemState;
  readonly idx: number;
  readonly mode: ReviewCardMode;
  readonly saving?: boolean;
  readonly canRemove?: boolean;
  readonly statusPill?: { readonly label: string; readonly variant: string };
  readonly mediaSlot: (v: DraftVariantState) => ReactNode;
  readonly onUpdateItem?: (idx: number, patch: Partial<DraftItemState>) => void;
  readonly onUpdateVariant?: (idx: number, vIdx: number, patch: Partial<DraftVariantState>) => void;
  readonly onUpdateSharedBrief?: (idx: number, patch: Partial<DraftVariantBriefState>) => void;
  readonly onRemoveItem?: (idx: number) => void;
}) {
  const [selVIdx, setSelVIdx] = useState(0);
  const safeSel = Math.min(selVIdx, Math.max(0, item.variants.length - 1));
  const format = draftItemAssetFormat(item);
  return (
    <fieldset className="ai-draft-review__idea" disabled={saving ?? false}>
      <header className="ai-draft-review__idea-head">
        {mode === 'draft' ? (
          <span className="ai-draft-review__idea-index" aria-hidden="true">
            {String(idx + 1).padStart(2, '0')}
          </span>
        ) : null}
        <div className="ai-draft-review__idea-headmain">
          {onUpdateItem ? (
            <input
              className="ai-draft-review__idea-title"
              type="text"
              value={item.title}
              onChange={(e) => onUpdateItem(idx, { title: e.target.value })}
              placeholder="عنوان الفكرة"
              aria-label="عنوان الفكرة"
            />
          ) : (
            <h2 className="ai-draft-review__idea-title-static">{item.title || '—'}</h2>
          )}
          <div className="ai-draft-review__idea-meta">
            {/* Task 286B — the asset-format tag is asset-kind metadata (inferred
                from the variants even when the brief is empty); hidden from clients
                to match SharedAssetPanel's hidden asset-kind header. */}
            {format && mode !== 'client' ? (
              <span className="ai-draft-review__tag ai-draft-review__tag--format">
                {assetFormatLabelAr(format)}
              </span>
            ) : null}
            {/* Task 286J — the item-level status pill is INTERNAL workflow metadata
                (in_production / pending_internal_review …); never render it in
                client mode (mirrors the format-tag gate above). */}
            {statusPill && mode !== 'client' ? (
              <span className={`pill pill-${statusPill.variant}`}>{statusPill.label}</span>
            ) : mode === 'draft' ? (
              <span className="ai-draft-review__tag ai-draft-review__tag--draft">مسودة</span>
            ) : null}
            {onUpdateItem ? (
              <>
                <label className="ai-draft-review__idea-when">
                  <span className="ai-draft-review__sr">التاريخ</span>
                  <input
                    type="date"
                    value={item.scheduledDate}
                    onChange={(e) => onUpdateItem(idx, { scheduledDate: e.target.value })}
                  />
                </label>
                <label className="ai-draft-review__idea-when">
                  <span className="ai-draft-review__sr">الوقت</span>
                  <input
                    type="time"
                    value={item.scheduledTimeLocal}
                    onChange={(e) => onUpdateItem(idx, { scheduledTimeLocal: e.target.value })}
                  />
                </label>
              </>
            ) : null}
          </div>
        </div>
        {onRemoveItem ? (
          <button
            type="button"
            className="btn-text ai-draft-review__idea-remove"
            onClick={() => onRemoveItem(idx)}
            disabled={!canRemove}
          >
            حذف
          </button>
        ) : null}
      </header>

      {/* Task 286B hid the INTERNAL concept band (core idea, hook, visual direction)
          from clients entirely. Task 286I — clients now render the LIMITED client
          concept band instead (نوع المحتوى / فكرة المحتوى / الافتتاحية, read-only);
          the internal band is unchanged for draft/saved. */}
      <IdeaConceptSummary
        item={item}
        idx={idx}
        format={format}
        onUpdateItem={onUpdateItem}
        isClient={mode === 'client'}
      />

      {/* Task 286E — data-mode lets the saved surface REORDER the review flow with
          CSS only (the DOM order — detail/col-primary then support/col-secondary —
          is identical for both modes). Saved collapses the body to one column and
          uses `order` so the reviewer reads the design brief / asset / canvas first,
          then the platform selector, and approves the published caption LAST. Draft
          keeps its two-column grid. */}
      <div className="ai-draft-review__idea-body" data-mode={mode}>
        <div className="ai-draft-review__col-primary">
          <SelectedVariantDetail
            item={item}
            idx={idx}
            vIdx={safeSel}
            mode={mode}
            onUpdateVariant={onUpdateVariant}
          />
        </div>
        <aside className="ai-draft-review__col-secondary">
          <VariantChips variants={item.variants} selectedIdx={safeSel} onSelect={setSelVIdx} />
          <SharedAssetPanel
            item={item}
            idx={idx}
            format={format}
            mode={mode}
            selectedVariant={item.variants[safeSel]}
            mediaSlot={mediaSlot}
            onUpdateSharedBrief={onUpdateSharedBrief}
          />
        </aside>
      </div>
    </fieldset>
  );
}

// Task 286A — read the per-variant production brief from the SAVED jsonb
// (`platform_specific`, snake_case) into the camelCase DraftVariantBriefState the
// shared review card consumes. Raw values; ProductionBriefReadOnly formats them
// (asset_format → Arabic label, etc.) for read-only display.
function briefFromVariantRow(v: ContentVariantRow): DraftVariantBriefState {
  const ps = v.platform_specific;
  const str = (key: string): string => {
    const raw = ps[key];
    return typeof raw === 'string' ? raw : '';
  };
  const durationRaw = ps['duration_seconds'];
  return {
    onImageText: str('on_image_text'),
    visualDirection: str('visual_direction'),
    creativeBrief: str('creative_brief'),
    assetFormat: str('asset_format'),
    hook: str('hook'),
    script: str('script'),
    shotList: str('shot_list'),
    onScreenText: str('on_screen_text'),
    audioDirection: str('audio_direction'),
    durationSeconds:
      typeof durationRaw === 'number' && Number.isFinite(durationRaw) ? String(durationRaw) : '',
  };
}

function hashtagsFromPlatformSpecific(ps: Readonly<Record<string, unknown>>): string[] {
  const raw = ps['hashtags'];
  if (!Array.isArray(raw)) return [];
  // Manual posts persist hashtags WITH a leading '#'; AI drafts store them
  // bare. Normalize to bare here so the read-only render's '#' prefix can't
  // double up ('##tag').
  return raw
    .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
    .map((t) => t.trim().replace(/^#+/, ''))
    .filter((t) => t.length > 0);
}

function hashtagsFromVariantRow(v: ContentVariantRow): string[] {
  return hashtagsFromPlatformSpecific(v.platform_specific);
}

// Task 286F — the production CTA lives on `platform_specific.cta` (a separate field
// from the caption; both the AI flow and the manual editor write it there via
// `briefToPlatformSpecific`). Read-only string for the saved / staff card.
function ctaFromVariantRow(v: ContentVariantRow): string {
  const raw = v.platform_specific['cta'];
  return typeof raw === 'string' ? raw : '';
}

// Task 286F — the production destination link (`platform_specific.link`); same
// source + read-only handling as the CTA (the old designer brief showed both).
function linkFromVariantRow(v: ContentVariantRow): string {
  const raw = v.platform_specific['link'];
  return typeof raw === 'string' ? raw : '';
}

// Task 286B — a fully-empty production brief. The client review card NEVER
// receives real brief data: the design/production brief is internal, so the
// client adapter synthesizes this empty brief (defense in depth — the card also
// hides the brief UI in client mode).
const EMPTY_CLIENT_BRIEF: DraftVariantBriefState = {
  onImageText: '',
  visualDirection: '',
  creativeBrief: '',
  assetFormat: '',
  hook: '',
  script: '',
  shotList: '',
  onScreenText: '',
  audioDirection: '',
  durationSeconds: '',
};

// Task 286I — the CLIENT concept brief: EMPTY_CLIENT_BRIEF plus ONLY the two
// client-approved concept fields — `hook` (الافتتاحية) and `asset_format`
// (نوع المحتوى, enum-validated so dirty data can't ride through). Read directly
// (NOT via briefFromVariantRow — the full brief adapter stays agency-only), so
// every production field (visual direction, creative brief, script, shot list,
// on-image/on-screen text, audio, duration) remains '' for clients regardless of
// what the server sends. The 286I loader projection is the real boundary; this is
// the FE mirror of its allowlist.
function clientConceptBriefFromPlatformSpecific(
  ps: Readonly<Record<string, unknown>>,
): DraftVariantBriefState {
  const hook = typeof ps['hook'] === 'string' ? ps['hook'] : '';
  const rawFormat = ps['asset_format'];
  const assetFormat =
    rawFormat === 'static_image' ||
    rawFormat === 'carousel' ||
    rawFormat === 'vertical_video' ||
    rawFormat === 'story'
      ? rawFormat
      : '';
  return { ...EMPTY_CLIENT_BRIEF, hook, assetFormat };
}

// Task 286F — build the canonical review-card shape (DraftItemState) from a content
// item's identity + its variants. EXPORTED so the staff production-task workspaces
// (designer / video producer) render the SAME shared IdeaReviewCard without
// duplicating the adapter. Pure FE, read-only (no copy/brief edit callbacks); the
// per-variant `status` drives the saved status pill; the card derives canvas sizes
// from the variants' assetFormat via the SAME `draftItemAssetFormat` the draft uses.
export function reviewItemFromContent(args: {
  readonly key: string;
  readonly title: string;
  readonly body: string | null;
  readonly variants: ReadonlyArray<ContentVariantRow>;
}): DraftItemState {
  return {
    key: args.key,
    title: args.title,
    body: args.body ?? '',
    scheduledDate: '',
    scheduledTimeLocal: '',
    variants: args.variants.map((v) => {
      // Saved/production rows may store copy in the alternate column (e.g. a manual
      // story with text in `caption`, or a legacy feed row with text only in `body`).
      // The shared card reads `body` for story-like platforms and `caption`
      // otherwise, so mirror the old grid's `caption ?? body` forgiveness here —
      // mapped to the field the card actually renders for that platform.
      const caption = v.caption ?? '';
      const body = v.body ?? '';
      const story = isStoryLikePlatform(v.platform);
      return {
        platform: v.platform,
        headline: v.headline ?? '',
        caption: story ? caption : caption.trim().length > 0 ? caption : body,
        body: story ? (body.trim().length > 0 ? body : caption) : body,
        hashtags: hashtagsFromVariantRow(v),
        brief: briefFromVariantRow(v),
        status: v.status,
        cta: ctaFromVariantRow(v),
        link: linkFromVariantRow(v),
      };
    }),
  };
}

// Task 286A — adapt a SAVED content item + its variants into the canonical review
// card shape; delegates to the shared `reviewItemFromContent` builder (286F).
function savedItemToReviewItem(
  item: ContentItemDetailRow,
  variants: ReadonlyArray<ContentVariantRow>,
): DraftItemState {
  return reviewItemFromContent({ key: item.id, title: item.title, body: item.body, variants });
}

// Task 286G — the CLIENT-SAFE saved adapter. The internal saved detail loads the
// FULL ContentItemDetailRow + variants; this maps them into the card shape WITHOUT
// any internal field — blanks the per-variant headline, drops the production brief
// + internal variant status — so a client viewer's card never carries internal data
// into its props (matches the inbox's approvalRowToReviewItem).
// All non-archived variants are mapped; caption/body/hashtags are the published copy.
// Task 286I — the item body (core idea) + the concept brief (hook + asset_format)
// are now client-visible: the 286I loader projection delivers exactly those to
// client callers, and this adapter carries them into the card's client concept band.
function savedItemToClientReviewItem(
  item: ContentItemDetailRow,
  variants: ReadonlyArray<ContentVariantRow>,
): DraftItemState {
  return {
    key: item.id,
    title: item.title,
    body: item.body ?? '',
    scheduledDate: '',
    scheduledTimeLocal: '',
    variants: variants.map((v) => {
      const caption = v.caption ?? '';
      const body = v.body ?? '';
      const story = isStoryLikePlatform(v.platform);
      return {
        platform: v.platform,
        headline: '',
        caption: story ? caption : caption.trim().length > 0 ? caption : body,
        body: story ? (body.trim().length > 0 ? body : caption) : body,
        hashtags: hashtagsFromVariantRow(v),
        brief: clientConceptBriefFromPlatformSpecific(v.platform_specific),
      };
    }),
  };
}

// Task 286B — adapt a CLIENT pending-approval row into the canonical review-card
// shape (DraftItemState), CLIENT-SAFE. Unlike the saved adapter this NEVER copies
// internal data into the card: the production brief is replaced with the client
// concept brief (hook + asset_format ONLY — Task 286I), the internal headline is
// blanked, and the internal variant status is omitted — so even though the client
// card also HIDES the internal fields, no internal value is ever placed in its data
// (defense in depth). ALL non-archived variants are mapped so the client can switch
// platforms; hashtags + caption/body are the client-facing published copy. Pure FE.
// Task 286I — the item body (core idea) is client-visible: the inbox loader now
// delivers it to client callers for the concept band (فكرة المحتوى).
function approvalRowToReviewItem(item: PendingClientApprovalRow): DraftItemState {
  return {
    key: item.id,
    title: item.title,
    body: item.body ?? '',
    scheduledDate: '',
    scheduledTimeLocal: '',
    // Defense in depth — the inbox EF already drops archived variants, but never
    // surface an archived variant as a selectable client platform chip.
    variants: item.content_variants
      .filter((v) => v.archived_at === null)
      .map((v) => {
        const caption = v.caption ?? '';
        const body = v.body ?? '';
        const story = isStoryLikePlatform(v.platform);
        return {
          platform: v.platform,
          headline: '',
          caption: story ? caption : caption.trim().length > 0 ? caption : body,
          body: story ? (body.trim().length > 0 ? body : caption) : body,
          hashtags: hashtagsFromPlatformSpecific(v.platform_specific),
          brief: clientConceptBriefFromPlatformSpecific(v.platform_specific),
        };
      }),
  };
}

// Task 107D — `DraftEditor` is the AI calendar review screen. Since
// 105A the manual flow uses `ManualSinglePostCard` directly, so this
// component is only reached after AI generation completes (origin =
// 'ai_assisted'). The 107D rewrite drops the raw debug summary
// ("AI run: gpt-4 · $0.02 · 1234 tokens · 567 ms"), translates the
// snake_case warning codes via `aiWarningLabelAr`, hides the raw
// `v.platform` enum next to each variant card, swaps the global
// `variantPlatformLabel` for the Arabic-only `aiPlatformLabelAr`,
// and renders each draft item as a polished card with grouped
// fields. Save flow + payload + onSave callback are unchanged.
function DraftEditor({
  state,
  onChange,
  onCancel,
  onSave,
  saveError,
  saving,
}: {
  readonly state: DraftState;
  readonly onChange: (next: DraftState) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly saveError: string | null;
  readonly saving: boolean;
}) {
  const updateItem = (idx: number, patch: Partial<DraftItemState>) => {
    const next = state.items.map((item, i) => (i === idx ? { ...item, ...patch } : item));
    onChange({ ...state, items: next });
  };
  const updateVariant = (idx: number, vIdx: number, patch: Partial<DraftVariantState>) => {
    const target = state.items[idx];
    if (!target) return;
    const newVariants = target.variants.map((v, i) => (i === vIdx ? { ...v, ...patch } : v));
    updateItem(idx, { variants: newVariants });
  };
  // Task 285B — the design brief belongs to the ONE shared asset, so a brief
  // edit applies to every variant of the idea (they carry the identical
  // shared_brief by construction). Per-platform copy still edits its own variant.
  const updateAllVariantsBrief = (idx: number, patch: Partial<DraftVariantBriefState>) => {
    const target = state.items[idx];
    if (!target) return;
    const newVariants = target.variants.map((v) => ({ ...v, brief: { ...v.brief, ...patch } }));
    updateItem(idx, { variants: newVariants });
  };
  const removeItem = (idx: number) => {
    const next = state.items.filter((_, i) => i !== idx);
    onChange({ ...state, items: next });
  };
  const appendItem = () => {
    if (state.items.length >= 30) return;
    onChange({
      ...state,
      items: [...state.items, defaultEmptyDraftItem(state.platforms, state.month)],
    });
  };

  // Task 107D — friendly Arabic label for the timezone shown in the
  // header. Falls back to the raw IANA only if the curated map
  // doesn't cover it (defensive — in practice the AI flow always
  // writes one of the curated values).
  const tzLabel =
    MANUAL_TIMEZONE_OPTIONS.find((o) => o.value === state.timezone)?.label ?? state.timezone;
  const modeLabel =
    state.mode === 'unified'
      ? 'وضع موحّد'
      : state.mode === 'platform_adapted'
        ? 'وضع مخصص لكل منصة'
        : 'مسودة يدوية';

  return (
    <div className="card ai-calendar-form ai-draft-review">
      <div className="ai-calendar-form__body">
        <header className="ai-calendar-form__header">
          <h2 className="ai-calendar-form__title">مراجعة مسودة الكالندر</h2>
          <span className="ai-calendar-form__hint">
            {state.month} · {tzLabel} · {modeLabel}
          </span>
        </header>
        <p className="ai-calendar-form__intro">
          راجع كل عنصر وعدّله إن لزم. لن تُحفظ أي بيانات حتى تضغط «حفظ كمسودات». الإلغاء يُفقد
          التعديلات.
        </p>

        {state.aiWarnings.length > 0 ? (
          <div className="ai-draft-review__warnings" role="status">
            <span className="ai-draft-review__warnings-title">ملاحظات على المسودة</span>
            <ul className="ai-draft-review__warnings-list">
              {state.aiWarnings.map((w, i) => (
                <li key={`${w.code}-${i}`}>{aiWarningLabelAr(w.code) ?? w.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="ai-draft-review__feed">
          {state.items.map((item, idx) => (
            <IdeaReviewCard
              key={item.key}
              item={item}
              idx={idx}
              mode="draft"
              saving={saving}
              canRemove={state.items.length > 1}
              mediaSlot={() => (
                <>
                  <AssetPendingPreview ratio={assetKindMeta(draftItemAssetFormat(item)).ratio} />
                  <p className="ai-draft-review__asset-hint">
                    الأصل لم يُرفع بعد — يُنشأ في الإنتاج بعد حفظ المسودة، ويُربط تلقائيًا بكل
                    المنصات.
                  </p>
                </>
              )}
              onUpdateItem={updateItem}
              onUpdateVariant={updateVariant}
              onUpdateSharedBrief={updateAllVariantsBrief}
              onRemoveItem={removeItem}
            />
          ))}
        </div>

        {saveError ? (
          <div className="banner banner-error ai-calendar-form__error" role="alert">
            {saveError}
          </div>
        ) : null}
      </div>

      <div className="ai-calendar-form__actions ai-draft-review__actions">
        <button
          type="button"
          className="btn-text"
          onClick={appendItem}
          disabled={state.items.length >= 30 || saving}
        >
          إضافة عنصر فارغ
        </button>
        <button type="button" className="btn-text" onClick={onCancel} disabled={saving}>
          إلغاء
        </button>
        <button type="button" className="btn" onClick={onSave} disabled={saving}>
          {saving ? 'جارٍ الحفظ…' : 'حفظ كمسودات'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Task 068: review-workflow transition panel + calendar view + edit modal
// ─────────────────────────────────────────────────────────────────────

interface TransitionAction {
  readonly target: ContentItemTransitionTarget;
  readonly label: string;
  readonly variant: 'btn' | 'btn-text';
  readonly confirm?: string;
}

function transitionsForStatus(
  item: ContentItemDetailRow,
  perms: {
    isLeadership: boolean;
    canCreate: boolean;
    canInternalReviewBase: boolean;
    canApproveContentDiscipline: boolean;
    canApproveDesignDiscipline: boolean;
    canDecideClientApproval: boolean;
    // Task 101C — production-handoff CTA gate.
    canStartProduction: boolean;
    // Task 105A (+ Task 160 AM tier) — trusted manual content handoff.
    // Mirrors the RPC gate: agency role + (leadership OR
    // senior_content_writer with active client_assignment OR
    // account_manager with active client_assignment). The FE flag is
    // computed in ContentPage from `me.roles` + the item's client_id;
    // this helper just gates the button render.
    canDirectManualClientHandoff: boolean;
    // Task 160 — one-click "approve internal + send to client" gate.
    canApproveAndSendToClient: boolean;
  },
): ReadonlyArray<TransitionAction> {
  const out: TransitionAction[] = [];
  const canApproveDiscipline =
    item.discipline === 'content'
      ? perms.canApproveContentDiscipline
      : perms.canApproveDesignDiscipline;
  switch (item.status) {
    case 'draft': {
      if (perms.canCreate) {
        out.push({
          target: 'pending_internal_review',
          label: 'إرسال للمراجعة الداخلية',
          variant: 'btn',
        });
      }
      // Task 105A — direct trusted-content handoff. Owner /
      // marketing_manager / senior_content_writer can skip internal
      // review for `manual` and `imported` content (both are
      // operator-authored / curated; AI-generated and AI-assisted
      // items still go through the internal-review path because the
      // RPC raises 23514 for those origins). Button label simplified
      // to "إرسال لموافقة العميل" per the new label spec; the
      // confirmation copy spells out that internal review is skipped.
      if (
        perms.canDirectManualClientHandoff &&
        (item.origin === 'manual' || item.origin === 'imported')
      ) {
        out.push({
          target: 'pending_client_content_approval',
          label: 'إرسال لموافقة العميل',
          variant: 'btn',
          confirm: 'سيُرسَل المحتوى مباشرةً للعميل لاعتماده، دون مراجعة داخلية. هل تؤكد؟',
        });
      }
      break;
    }
    case 'pending_internal_review': {
      // Task 160 — one-click decision-maker handoff (leadership OR AM+assigned).
      if (perms.canApproveAndSendToClient) {
        if (item.has_current_production_deliverable === true) {
          out.push({
            target: 'pending_client_asset_approval',
            label: 'اعتماد وإرسال الأصل للعميل',
            variant: 'btn',
          });
        } else if (item.has_current_production_deliverable === false) {
          out.push({
            target: 'pending_client_content_approval',
            label: 'اعتماد وإرسال للعميل',
            variant: 'btn',
          });
        }
      }
      if (perms.canInternalReviewBase && canApproveDiscipline) {
        out.push({
          target: 'internal_approved',
          label: 'اعتماد داخلي',
          variant: 'btn',
          confirm: 'هل تؤكد الاعتماد الداخلي؟ سيُسجَّل قراراً ثابتاً في سجلّ الموافقات.',
        });
      }
      if (perms.canCreate) {
        out.push({
          target: 'draft',
          label: 'إرجاع لمسودة',
          variant: 'btn-text',
        });
      }
      break;
    }
    case 'internal_approved': {
      if (perms.canCreate) {
        // Task 104B Round 4 (strategic rewrite) — `internal_approved`
        // has TWO mutually exclusive client handoff edges in the v2
        // RPC, discriminated by the persisted column
        // `content_items.has_current_production_deliverable`:
        //   * `pending_client_content_approval` for fresh items that
        //     have NOT yet been through production
        //     (`has_current_production_deliverable === false`);
        //   * `pending_client_asset_approval` for items that DO have
        //     a current production deliverable on file
        //     (`has_current_production_deliverable === true`).
        // The backend rejects the wrong target with 23514, so the
        // FE gates by the boolean to keep the UI honest. If the flag
        // is missing on the wire (legacy / typing fallback) we FAIL
        // CLOSED and surface NO handoff button — the safer default
        // since either target would risk the wrong RPC error.
        if (item.has_current_production_deliverable === true) {
          out.push({
            target: 'pending_client_asset_approval',
            label: 'إرسال لموافقة العميل النهائية على الأصل',
            variant: 'btn',
            confirm: 'سيُرسَل الأصل النهائي للعميل لاعتماده. هل تؤكد؟',
          });
        } else if (item.has_current_production_deliverable === false) {
          out.push({
            target: 'pending_client_content_approval',
            label: 'إرسال لموافقة العميل على المحتوى',
            variant: 'btn',
            confirm: 'سيُرسَل المحتوى للعميل لاعتماده قبل الإنتاج. هل تؤكد؟',
          });
        }
        // else: flag undefined → fail closed (no handoff button rendered)
      }
      if (perms.isLeadership) {
        out.push({
          target: 'draft',
          label: 'إرجاع لمسودة',
          variant: 'btn-text',
          confirm: 'سيُلغى الاعتماد الداخلي. هل تؤكد؟',
        });
      }
      break;
    }
    case 'pending_client_asset_approval': {
      // Task 104B — client final asset approval. Approve gates on
      // `canDecideClientApproval` (client-category role +
      // approval.client.decide); the RPC enforces it server-side too.
      // Request-changes is the non-decision return-to-draft path —
      // FE-side gated to client-category + decide so the inbox
      // surface UX matches the agency permission boundary; the RPC's
      // `pending_client_asset_approval → draft` branch admits both
      // client-with-decide AND agency-with-content.create.
      if (perms.canDecideClientApproval) {
        out.push({
          target: 'client_asset_approved',
          label: 'اعتماد العميل النهائي للأصل',
          variant: 'btn',
          confirm:
            'هل تؤكد اعتماد الأصل النهائي نيابةً عن العميل؟ سيُسجَّل قراراً ثابتاً في سجلّ الموافقات.',
        });
      }
      if (perms.canCreate) {
        out.push({
          target: 'draft',
          label: 'طلب تعديل / إرجاع لمسودة',
          variant: 'btn-text',
        });
      }
      break;
    }
    case 'client_asset_approved': {
      // Task 104B — leadership-only revert (mirrors
      // `client_content_approved → draft`). The next step
      // (scheduled / published) is deliberately deferred to a
      // separate task; no forward-action button is exposed here yet.
      if (perms.isLeadership) {
        out.push({
          target: 'draft',
          label: 'إرجاع لمسودة',
          variant: 'btn-text',
          confirm: 'سيُلغى اعتماد العميل النهائي للأصل. هذا الإجراء يحتاج تأكيداً واضحاً.',
        });
      }
      break;
    }
    case 'pending_client_content_approval': {
      if (perms.canDecideClientApproval) {
        out.push({
          target: 'client_content_approved',
          label: 'اعتماد العميل',
          variant: 'btn',
          confirm: 'هل تؤكد اعتماد المحتوى نيابةً عن العميل؟',
        });
      }
      if (perms.canCreate) {
        out.push({
          target: 'draft',
          label: 'إرجاع لمسودة',
          variant: 'btn-text',
        });
      }
      break;
    }
    case 'client_content_approved': {
      // Task 101C — production-handoff CTA. Backend RPC v2 enforces
      // agency role + (leadership OR client_assignment) +
      // `production.queue.manage`; the FE flag mirrors that gate.
      // Keep the leadership-only rollback as the secondary action.
      if (perms.canStartProduction) {
        out.push({
          target: 'in_production',
          label: 'إرسال للإنتاج',
          variant: 'btn',
          confirm: 'سيتم إنشاء مهمة إنتاج وتحويل العنصر إلى قيد الإنتاج. هل تؤكد؟',
        });
      }
      if (perms.isLeadership) {
        out.push({
          target: 'draft',
          label: 'إرجاع لمسودة',
          variant: 'btn-text',
          confirm: 'سيُلغى اعتماد العميل المسجَّل. هذا الإجراء يحتاج تأكيداً واضحاً.',
        });
      }
      break;
    }
    default:
      break;
  }
  return out;
}

interface TransitionPanelProps {
  readonly item: ContentItemDetailRow;
  readonly isLeadership: boolean;
  readonly canCreate: boolean;
  readonly canInternalReviewBase: boolean;
  readonly canApproveContentDiscipline: boolean;
  readonly canApproveDesignDiscipline: boolean;
  readonly canDecideClientApproval: boolean;
  // Task 101C — production-handoff CTA gate.
  readonly canStartProduction: boolean;
  // Task 105A — direct manual handoff CTA gate.
  readonly canDirectManualClientHandoff: boolean;
  // Task 160 — one-click "approve internal + send to client" gate.
  readonly canApproveAndSendToClient: boolean;
  readonly onTransition: (target: ContentItemTransitionTarget, reason: string) => Promise<void>;
  readonly inFlight: boolean;
  readonly error: string | null;
}

function TransitionPanel(props: TransitionPanelProps) {
  // Task 112B — inline confirmation replaces the native `window.confirm`
  // for transitions that require an "are you sure?" gate. The panel
  // renders inside the same "الإجراءات المتاحة" card so the action
  // stays on-page (no browser-modal interruption) and screen-reader
  // users get a real `role="alertdialog"` instead of a synchronous
  // browser primitive. The per-action `confirm` copy from
  // `transitionsForStatus` is preserved verbatim.
  const [pendingConfirm, setPendingConfirm] = useState<TransitionAction | null>(null);
  const actions = transitionsForStatus(props.item, {
    isLeadership: props.isLeadership,
    canCreate: props.canCreate,
    canInternalReviewBase: props.canInternalReviewBase,
    canApproveContentDiscipline: props.canApproveContentDiscipline,
    canApproveDesignDiscipline: props.canApproveDesignDiscipline,
    canDecideClientApproval: props.canDecideClientApproval,
    canStartProduction: props.canStartProduction,
    canDirectManualClientHandoff: props.canDirectManualClientHandoff,
    canApproveAndSendToClient: props.canApproveAndSendToClient,
  });
  // Drop a stale pending confirmation if the action set no longer
  // exposes that target (status changed under us, perms revoked,
  // sibling row selected). Without this guard the inline panel would
  // ask the operator to confirm an action that is no longer reachable.
  useEffect(() => {
    if (pendingConfirm && !actions.some((a) => a.target === pendingConfirm.target)) {
      setPendingConfirm(null);
    }
  }, [actions, pendingConfirm]);
  if (actions.length === 0) {
    return (
      <div style={{ marginTop: 14 }}>
        <p className="tiny" style={{ color: 'var(--muted)' }}>
          لا توجد إجراءات متاحة في الحالة الحالية.
        </p>
        {props.error ? (
          <div className="banner banner-error" role="alert" style={{ marginTop: 8 }}>
            {props.error}
          </div>
        ) : null}
      </div>
    );
  }
  // Task 105A — generic `window.prompt('سبب الإجراء')` removed. The
  // reason field is required by the v2 RPC for audit but for routine
  // transitions (internal-review send, client content/asset handoff,
  // approve, return-to-draft from review) the action's own label is
  // a more honest record than asking the operator to type a tag.
  // Specific reason flows that genuinely need narrative copy
  // (mark-failed, cancel, request-changes) keep their own scoped
  // prompts at the call sites that own the action.
  const dispatch = async (action: TransitionAction) => {
    await props.onTransition(action.target, action.label);
  };
  const onActionClick = (action: TransitionAction) => {
    if (props.inFlight || pendingConfirm) return;
    if (action.confirm) {
      setPendingConfirm(action);
      return;
    }
    void dispatch(action);
  };
  const onConfirmYes = async () => {
    if (!pendingConfirm || props.inFlight) return;
    const action = pendingConfirm;
    setPendingConfirm(null);
    await dispatch(action);
  };
  const onConfirmNo = () => {
    if (props.inFlight) return;
    setPendingConfirm(null);
  };
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {actions.map((a) => (
          <button
            key={a.target}
            type="button"
            className={a.variant}
            disabled={props.inFlight || pendingConfirm !== null}
            onClick={() => onActionClick(a)}
          >
            {props.inFlight ? 'جارٍ التحديث…' : a.label}
          </button>
        ))}
      </div>
      {pendingConfirm ? (
        <div
          className="card content-transition-confirm"
          role="alertdialog"
          aria-label="تأكيد الإجراء"
          style={{ marginTop: 12, padding: 14 }}
        >
          <p style={{ margin: '0 0 10px' }}>{pendingConfirm.confirm}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn"
              disabled={props.inFlight}
              onClick={() => void onConfirmYes()}
            >
              {props.inFlight ? 'جارٍ التحديث…' : 'تأكيد'}
            </button>
            <button
              type="button"
              className="btn-text"
              disabled={props.inFlight}
              onClick={onConfirmNo}
            >
              إلغاء
            </button>
          </div>
        </div>
      ) : null}
      {props.error ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 8 }}>
          {props.error}
        </div>
      ) : null}
    </div>
  );
}

interface CalendarViewProps {
  readonly state: CalendarLoadState;
  readonly month: string;
  readonly timezone: string;
  readonly canEdit: boolean;
  // Task 277 — show the AI-origin label only to agency-category viewers.
  readonly canSeeOrigin: boolean;
  // Task 286G — a restricted client viewer; hide internal variant status + the
  // internal headline on calendar cards.
  readonly viewerIsClient: boolean;
  readonly onMonthChange: (month: string) => void;
  readonly onTimezoneChange: (tz: string) => void;
  readonly onReload: () => void;
  readonly onSelect: (contentItemId: string) => void;
  readonly onEdit: (entry: CalendarEntryRow) => void;
  readonly creativeAssetsByTarget: Map<string, CreativeAssetCardRow>;
}

function CalendarView(props: CalendarViewProps) {
  const entriesByDay = useMemo(() => {
    if (props.state.status !== 'ready') return new Map<string, CalendarEntryRow[]>();
    const map = new Map<string, CalendarEntryRow[]>();
    for (const e of props.state.entries) {
      const localDate = formatLocalDate(e.scheduled_for, props.timezone);
      const arr = map.get(localDate) ?? [];
      arr.push(e);
      map.set(localDate, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
    }
    return map;
  }, [props.state, props.timezone]);
  const orderedDays = useMemo(() => Array.from(entriesByDay.keys()).sort(), [entriesByDay]);

  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">الشهر</span>
            <input
              type="month"
              value={props.month}
              onChange={(e) => props.onMonthChange(e.target.value)}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">المنطقة الزمنية</span>
            <input
              type="text"
              value={props.timezone}
              onChange={(e) => props.onTimezoneChange(e.target.value)}
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="button" className="btn-text" onClick={props.onReload}>
              تحديث
            </button>
          </div>
        </div>
      </div>

      {props.state.status === 'loading' ? <LoadingState title="جاري تحميل التقويم…" /> : null}
      {props.state.status === 'not-found' ? (
        <NotFoundState
          title="لا يمكن الوصول إلى تقويم هذا العميل"
          description="إما أن العميل غير موجود، أو لا تملك صلاحية فتحه."
        />
      ) : null}
      {props.state.status === 'error' ? (
        <ErrorState title="تعذر تحميل التقويم" error={props.state.error} onRetry={props.onReload} />
      ) : null}
      {props.state.status === 'ready' && props.state.entries.length === 0 ? (
        <EmptyState
          title="لا توجد إدخالات تقويم لهذا الشهر"
          description={
            // Task 290B — the agency instruction names actions clients
            // cannot take; give client viewers a neutral line instead.
            props.viewerIsClient
              ? 'سيظهر التقويم هنا عندما تجدول الوكالة محتوى لهذا الشهر.'
              : 'استخدم «إنشاء كالندر بالذكاء الاصطناعي» أو «إضافة يدوية» لبدء التخطيط.'
          }
        />
      ) : null}
      {props.state.status === 'ready' && props.state.entries.length > 0 ? (
        <div className="stack-md">
          {orderedDays.map((day) => (
            <div key={day} className="card" style={{ padding: 16 }}>
              <div className="sec-title">
                <h3 style={{ margin: 0 }}>{formatLongLocalDate(day)}</h3>
                <span className="meta">{(entriesByDay.get(day) ?? []).length} إدخال</span>
              </div>
              <ul className="row-list">
                {(entriesByDay.get(day) ?? []).map((e) => {
                  const variant = e.content_variants;
                  const item = variant?.content_items ?? null;
                  if (!variant || !item) return null;
                  const local = formatLocalTime(e.scheduled_for, props.timezone);
                  const isEditable = item.status === 'draft' && props.canEdit;
                  // Task 070 — same draft-only request-changes badge
                  // logic as the list view, sourced from the calendar
                  // endpoint's three-deep approvals embed.
                  const latestRequestChanges =
                    item.status === 'draft'
                      ? latestClientChangesRequest(item.content_approvals)
                      : null;
                  const reasonShort = truncateReason(latestRequestChanges?.reason ?? null);
                  return (
                    <li key={e.id} className="row-card calendar-entry-card">
                      <div className="calendar-entry-card__media">
                        <MediaPreviewSlot
                          platforms={[variant.platform]}
                          discipline={item.discipline}
                          clientSafe={props.viewerIsClient}
                          size="sm"
                          state={mediaStateFromAsset(
                            props.creativeAssetsByTarget.get(`content_variant:${variant.id}`) ??
                              props.creativeAssetsByTarget.get(`content_item:${item.id}`),
                          )}
                        />
                      </div>
                      <div className="calendar-entry-card__body">
                        <div className="calendar-entry-card__head">
                          <strong>{item.title}</strong>
                          {/* Task 290B — public group label for clients
                              (fail-closed null); agency unchanged. */}
                          {props.viewerIsClient ? (
                            clientPublicStatusLabel(item.status) ? (
                              <span
                                className={`pill pill-${clientPublicStatusVariant(item.status)}`}
                              >
                                {clientPublicStatusLabel(item.status)}
                              </span>
                            ) : null
                          ) : (
                            <span className={`pill pill-${contentStatusVariant(item.status)}`}>
                              {contentStatusLabel(item.status)}
                            </span>
                          )}
                        </div>
                        <div className="calendar-entry-card__chips">
                          <span className="chip chip-soft">{local}</span>
                          <span className="chip chip-ghost">
                            {variantPlatformLabel(variant.platform)}
                          </span>
                          {props.canSeeOrigin ? (
                            <span className="chip chip-ghost">{originLabel(item.origin)}</span>
                          ) : null}
                          {/* Task 286G — internal variant workflow status; hidden
                              from clients. */}
                          {props.viewerIsClient ? null : (
                            <span className="chip chip-ghost">
                              {variantStatusLabel(variant.status)}
                            </span>
                          )}
                        </div>
                        {latestRequestChanges ? (
                          <div className="calendar-entry-card__alert">
                            <span className="pill pill-review">طلب العميل تعديلات</span>
                            {reasonShort ? (
                              <p className="calendar-entry-card__reason">{reasonShort}</p>
                            ) : null}
                          </div>
                        ) : null}
                        {/* Task 286G — the internal headline is never published; hidden
                            from clients (caption below is the public copy). */}
                        {!props.viewerIsClient && variant.headline ? (
                          <p className="calendar-entry-card__headline">{variant.headline}</p>
                        ) : null}
                        {variant.caption ? (
                          <p className="calendar-entry-card__caption">{variant.caption}</p>
                        ) : null}
                        <div className="calendar-entry-card__actions">
                          <button
                            type="button"
                            className="btn-text"
                            onClick={() => props.onSelect(item.id)}
                          >
                            فتح التفاصيل
                          </button>
                          {/* Task 290B — clients can never edit (no
                              content.create) and the disabled tooltip
                              spoke internal workflow language; hide
                              the control for client viewers entirely.
                              Agency rendering unchanged. */}
                          {props.viewerIsClient ? null : (
                            <button
                              type="button"
                              className="btn-text"
                              onClick={() => props.onEdit(e)}
                              disabled={!isEditable}
                              title={
                                isEditable
                                  ? undefined
                                  : item.status === 'draft'
                                    ? 'لا تملك صلاحية التعديل.'
                                    : 'الإدخال خارج حالة المسودة. أعِده لمسودة أولاً.'
                              }
                            >
                              تعديل
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface EditDraftModalProps {
  readonly state: EditModalState;
  readonly onChange: (next: EditModalState) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}

function EditDraftModal({ state, onChange, onCancel, onSave }: EditDraftModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 24,
        zIndex: 50,
        overflowY: 'auto',
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 720,
          width: '100%',
          padding: 22,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div className="sec-title">
          <h2>تعديل إدخال التقويم</h2>
          <span className="meta">draft only</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 8,
            marginTop: 8,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">العنوان</span>
            <input
              type="text"
              value={state.title}
              onChange={(e) => onChange({ ...state, title: e.target.value })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">التاريخ المحلي</span>
            <input
              type="date"
              value={state.scheduledLocalDate}
              onChange={(e) => onChange({ ...state, scheduledLocalDate: e.target.value })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">الوقت المحلي (24H)</span>
            <input
              type="time"
              value={state.scheduledLocalTime}
              onChange={(e) => onChange({ ...state, scheduledLocalTime: e.target.value })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">المنطقة الزمنية</span>
            <input
              type="text"
              value={state.timezone}
              onChange={(e) => onChange({ ...state, timezone: e.target.value })}
            />
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          <span className="lbl">الفكرة الأساسية / مذكرة (اختياري)</span>
          <textarea
            rows={2}
            value={state.body}
            onChange={(e) => onChange({ ...state, body: e.target.value })}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          <span className="lbl">عنوان المنصة (اختياري)</span>
          <input
            type="text"
            value={state.headline}
            onChange={(e) => onChange({ ...state, headline: e.target.value })}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          <span className="lbl">التعليق</span>
          <textarea
            rows={3}
            value={state.caption}
            onChange={(e) => onChange({ ...state, caption: e.target.value })}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          <span className="lbl">جسم النص (اختياري)</span>
          <textarea
            rows={2}
            value={state.variantBody}
            onChange={(e) => onChange({ ...state, variantBody: e.target.value })}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          <span className="lbl">الهاشتاغات (افصلها بفواصل)</span>
          <input
            type="text"
            value={state.hashtags}
            onChange={(e) => onChange({ ...state, hashtags: e.target.value })}
          />
        </label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 8,
            marginTop: 8,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">CTA (اختياري)</span>
            <input
              type="text"
              value={state.cta}
              onChange={(e) => onChange({ ...state, cta: e.target.value })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="lbl">رابط (اختياري)</span>
            <input
              type="text"
              value={state.link}
              onChange={(e) => onChange({ ...state, link: e.target.value })}
            />
          </label>
        </div>
        {state.error ? (
          <div className="banner banner-error" role="alert" style={{ marginTop: 12 }}>
            {state.error}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-text" onClick={onCancel} disabled={state.saving}>
            إلغاء
          </button>
          <button type="button" className="btn" onClick={onSave} disabled={state.saving}>
            {state.saving ? 'جارٍ الحفظ…' : 'حفظ التعديلات'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers — local-time / IANA conversion + edit validation
// ─────────────────────────────────────────────────────────────────────

function splitUtcIntoLocalParts(utcIso: string, timezone: string): { date: string; time: string } {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(new Date(utcIso));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const yyyy = get('year').padStart(4, '0');
    const mm = get('month').padStart(2, '0');
    const dd = get('day').padStart(2, '0');
    const hh = get('hour').padStart(2, '0');
    const mi = get('minute').padStart(2, '0');
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
  } catch {
    const d = new Date(utcIso);
    return {
      date: d.toISOString().slice(0, 10),
      time: d.toISOString().slice(11, 16),
    };
  }
}

function composeIsoFromLocalParts(date: string, time: string, timezone: string): string {
  const offset = computeTzOffsetMinutes(date, time, timezone);
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const offsetH = Math.floor(abs / 60)
    .toString()
    .padStart(2, '0');
  const offsetM = (abs % 60).toString().padStart(2, '0');
  return `${date}T${time}:00${sign}${offsetH}:${offsetM}`;
}

function formatLocalDate(utcIso: string, timezone: string): string {
  return splitUtcIntoLocalParts(utcIso, timezone).date;
}

function formatLocalTime(utcIso: string, timezone: string): string {
  return splitUtcIntoLocalParts(utcIso, timezone).time;
}

function formatLongLocalDate(yyyymmdd: string): string {
  // yyyymmdd already in local-tz space; format for ar-SA without
  // re-applying a tz (we treat it as a calendar-day label).
  const [yyyyStr, mmStr, ddStr] = yyyymmdd.split('-');
  if (!yyyyStr || !mmStr || !ddStr) return yyyymmdd;
  const yyyy = Number(yyyyStr);
  const mm = Number(mmStr);
  const dd = Number(ddStr);
  const d = new Date(Date.UTC(yyyy, mm - 1, dd));
  try {
    return d.toLocaleDateString('ar-SA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return yyyymmdd;
  }
}

function validateEditModalBeforeSave(state: EditModalState): string | null {
  if (state.title.trim().length === 0) return 'العنوان مطلوب.';
  if (state.title.trim().length > 200) return 'العنوان طويل جداً (الحد الأقصى 200 حرف).';
  if (!/^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(state.scheduledLocalDate)) {
    return 'التاريخ غير صحيح.';
  }
  if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(state.scheduledLocalTime)) {
    return 'الوقت غير صحيح.';
  }
  if (state.timezone.trim().length === 0) return 'المنطقة الزمنية مطلوبة.';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: state.timezone });
  } catch {
    return 'المنطقة الزمنية غير صالحة.';
  }
  const hasText =
    state.headline.trim().length > 0 ||
    state.caption.trim().length > 0 ||
    state.variantBody.trim().length > 0;
  if (!hasText) return 'أضف عنواناً أو نصاً أو تعليقاً واحداً على الأقل.';
  return null;
}

// ─────────────────────────────────────────────────────────────────────
// Task 069: Client approval inbox
// ─────────────────────────────────────────────────────────────────────

// Task 107E — Arabic-only platform labels for the client approval
// inbox. Covers all 7 supported platforms (V1 + the legacy Facebook
// pair) so legacy variants render cleanly without leaking the raw
// `facebook_post` enum to client users. Scoped to this surface so
// the AI / manual flow allow-lists stay tight.
function inboxPlatformLabelAr(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'إنستجرام · منشور';
    case 'instagram_story':
      return 'إنستجرام · ستوري';
    case 'instagram_reel':
      return 'إنستجرام · ريل';
    case 'facebook_post':
      return 'فيسبوك · منشور';
    case 'facebook_story':
      return 'فيسبوك · ستوري';
    case 'tiktok_video':
      return 'تيك توك · فيديو';
    case 'snapchat_story':
      return 'سناب شات · ستوري';
    default:
      return platform;
  }
}

// Task 107E — friendly Arabic label for the production deliverable's
// MIME type. Pre-107E the row leaked the raw `image/jpeg` /
// `application/pdf` token next to the file link; client users don't
// need that vocabulary. Returns `null` for unmapped types so the
// caller can hide the chip rather than show garbage.
function inboxMimeLabelAr(mime: string | null | undefined): string | null {
  if (!mime) return null;
  const m = mime.toLowerCase();
  if (m.startsWith('image/')) return 'صورة';
  if (m.startsWith('video/')) return 'فيديو';
  if (m.startsWith('audio/')) return 'صوت';
  if (m === 'application/pdf') return 'مستند PDF';
  if (m.startsWith('text/')) return 'نص';
  if (m.startsWith('application/')) return 'ملف';
  return null;
}

// Task 107E — short Arabic label for the inbox stage. Drives the
// per-row stage badge so client users immediately see whether they
// are approving content or the final deliverable.
function inboxStageLabelAr(stage: PendingClientApprovalStage): string {
  return stage === 'asset' ? 'موافقة الأصل النهائي' : 'موافقة المحتوى';
}

interface ClientApprovalInboxProps {
  readonly state: InboxLoadState;
  readonly canDecide: boolean;
  readonly inFlight: boolean;
  readonly error: string | null;
  // Task 104B — `stage` selects the queue label/copy. Approve target
  // resolution lives in the parent's `onInboxApprove` handler (which
  // reads `item.status` defensively); the prop is purely a UX label
  // helper here.
  readonly stage: PendingClientApprovalStage;
  readonly onApprove: (item: PendingClientApprovalRow) => Promise<void>;
  // Task 105A — request-changes opens the in-app structured-feedback
  // modal at page level. The handler returns void synchronously
  // because the actual RPC dispatch happens inside the modal's save
  // path (`handleSubmitRequestChangesModal`), not inline.
  readonly onRequestChanges: (item: PendingClientApprovalRow) => void;
  readonly onReload: () => void;
  readonly creativeAssetsByTarget: Map<string, CreativeAssetCardRow>;
  // Task 290B — the inbox renders for every client-CATEGORY holder,
  // including dual-hat agency actors; only true client viewers get the
  // public status vocabulary on the row pill.
  readonly rowViewerIsClient: boolean;
}

// Task 107E — Client approval inbox redesign. Header card stacks
// title + Arabic guidance. The list state hands off to LoadingState
// / NotFoundState / ErrorState / EmptyState (existing helpers) for
// non-ready branches, then renders polished `client-inbox__row`
// cards for the ready+non-empty case. All RPC + permission +
// transition behavior is preserved from 104B / 105A.
function ClientApprovalInbox(props: ClientApprovalInboxProps) {
  const isAssetStage = props.stage === 'asset';
  const headerTitle = isAssetStage ? 'موافقة الأصل النهائي' : 'موافقة المحتوى';
  const headerSub = isAssetStage
    ? 'الأصل النهائي بعد إنتاج الوكالة ومراجعتها الداخلية'
    : 'المحتوى الذي ينتظر قرارك قبل بدء الإنتاج';
  const headerBody = isAssetStage
    ? 'هنا يظهر الأصل النهائي بعد أن أنتجته الوكالة واعتمدته داخلياً. اعتمد الأصل أو اطلب تعديل مع ذكر السبب.'
    : 'هنا يظهر المحتوى الذي أرسلته الوكالة لمراجعتك. اعتمد المحتوى أو اطلب تعديل مع ذكر السبب.';
  const emptyTitle = isAssetStage
    ? 'لا يوجد أصل نهائي بانتظار موافقتك حالياً.'
    : 'لا يوجد محتوى بانتظار موافقتك حالياً.';
  const emptyBody = isAssetStage
    ? 'حالما تُكمل الوكالة الإنتاج وتعتمده داخلياً، سيظهر الأصل هنا للموافقة النهائية.'
    : 'حالما ترسل الوكالة محتوى للمراجعة، سيظهر هنا.';
  return (
    <>
      <header className="card client-inbox__header">
        <span className="client-inbox__header-eyebrow">صندوق موافقات العميل</span>
        <h2 className="client-inbox__header-title">{headerTitle}</h2>
        <p className="client-inbox__header-sub">{headerSub}</p>
        <p className="client-inbox__header-body">{headerBody}</p>
        {props.error ? (
          <div className="banner banner-error client-inbox__header-error" role="alert">
            {props.error}
          </div>
        ) : null}
      </header>

      {props.state.status === 'loading' ? (
        <div className="client-inbox__state">
          <LoadingState title="جاري تحميل صندوق الموافقات…" />
        </div>
      ) : null}
      {props.state.status === 'not-found' ? (
        <div className="client-inbox__state">
          <NotFoundState
            title="لا يمكن الوصول إلى صندوق الموافقات"
            description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح هذا الصندوق."
          />
        </div>
      ) : null}
      {props.state.status === 'error' ? (
        <div className="client-inbox__state">
          <ErrorState
            title="تعذر تحميل صندوق الموافقات"
            error={props.state.error}
            onRetry={props.onReload}
          />
        </div>
      ) : null}
      {props.state.status === 'ready' && props.state.items.length === 0 ? (
        <div className="client-inbox__state">
          <EmptyState title={emptyTitle} description={emptyBody} />
        </div>
      ) : null}
      {props.state.status === 'ready' && props.state.items.length > 0 ? (
        <ul className="client-inbox__list">
          {props.state.items.map((item) => (
            <ClientApprovalInboxRow
              key={item.id}
              item={item}
              canDecide={props.canDecide}
              inFlight={props.inFlight}
              onApprove={() => props.onApprove(item)}
              onRequestChanges={() => props.onRequestChanges(item)}
              creativeAssetsByTarget={props.creativeAssetsByTarget}
              rowViewerIsClient={props.rowViewerIsClient}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}

function ClientApprovalInboxRow({
  item,
  canDecide,
  inFlight,
  onApprove,
  onRequestChanges,
  creativeAssetsByTarget,
  rowViewerIsClient,
}: {
  readonly item: PendingClientApprovalRow;
  readonly canDecide: boolean;
  readonly inFlight: boolean;
  readonly onApprove: () => void;
  readonly onRequestChanges: () => void;
  readonly creativeAssetsByTarget: Map<string, CreativeAssetCardRow>;
  readonly rowViewerIsClient: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const calendar =
    item.content_variants[0]?.calendar_entries.find((c) => c.archived_at === null) ?? null;
  const tz = calendar?.timezone ?? DEFAULT_TIMEZONE;
  const scheduledLabel = calendar
    ? formatLocalDateTime(calendar.scheduled_for, tz)
    : 'بدون موعد محدد';
  // Read off `item.status` directly so a row that arrives stale (e.g. a sub-tab
  // refresh while a prior approval is in flight) still dispatches the correct
  // target rather than trusting the inbox tab selection.
  const isAssetStage = item.status === 'pending_client_asset_approval';
  const deliverable = item.latest_production_deliverable;
  const stageLabel = inboxStageLabelAr(isAssetStage ? 'asset' : 'content');
  const platformsLabel =
    item.content_variants.length > 0
      ? item.content_variants.map((v) => inboxPlatformLabelAr(v.platform)).join('، ')
      : '—';
  // Task 286B — the full review renders through the ONE shared client-mode
  // IdeaReviewCard. The adapter is client-safe (no internal brief/headline/idea
  // body/variant status). Media loads ONLY when the row is expanded (the slot
  // mounts on demand) and only for the SELECTED variant — so the inbox list never
  // eagerly loads heavy media per row.
  const reviewItem = approvalRowToReviewItem(item);
  const renderClientMedia = (rv: DraftVariantState): ReactNode => {
    const cv = item.content_variants.find((c) => c.platform === rv.platform) ?? null;
    // Prefer the produced deliverable (the actual asset to approve at asset stage);
    // otherwise fall back to the attached creative asset for this variant/item.
    const slotState =
      mediaStateFromDeliverable(deliverable) ??
      mediaStateFromAsset(
        (cv ? creativeAssetsByTarget.get(`content_variant:${cv.id}`) : undefined) ??
          creativeAssetsByTarget.get(`content_item:${item.id}`),
      );
    const label = isAssetStage
      ? deliverable
        ? 'الأصل جاهز للمراجعة'
        : 'بانتظار أصل الإنتاج'
      : undefined;
    return (
      <>
        <MediaPreviewSlot
          platforms={[rv.platform]}
          discipline={item.discipline}
          clientSafe
          size="md"
          state={slotState}
          label={label}
        />
        {isAssetStage && deliverable ? (
          <ProductionDeliverablePreview deliverable={deliverable} />
        ) : null}
      </>
    );
  };
  return (
    <li className="client-inbox__row">
      <span
        className={
          isAssetStage
            ? 'client-inbox__row-stage client-inbox__row-stage--asset'
            : 'client-inbox__row-stage client-inbox__row-stage--content'
        }
      >
        {stageLabel}
      </span>
      <div className="client-inbox__row-body">
        <div className="client-inbox__row-head">
          <h3 className="client-inbox__row-title">{item.title}</h3>
          {/* Task 290B — public group label for client viewers
              (fail-closed null); dual-hat agency actors keep the
              internal vocabulary. */}
          {rowViewerIsClient ? (
            clientPublicStatusLabel(item.status) ? (
              <span className={`pill pill-${clientPublicStatusVariant(item.status)}`}>
                {clientPublicStatusLabel(item.status)}
              </span>
            ) : null
          ) : (
            <span className={`pill pill-${contentStatusVariant(item.status)}`}>
              {contentStatusLabel(item.status)}
            </span>
          )}
        </div>
        <dl className="client-inbox__row-meta">
          <div className="client-inbox__row-meta-item">
            <dt>الموعد المقترح</dt>
            <dd>{scheduledLabel}</dd>
          </div>
          <div className="client-inbox__row-meta-item">
            <dt>المنصات</dt>
            <dd>{platformsLabel}</dd>
          </div>
          {/* Task 277 — the client approval inbox is a client-only surface
              (gated by hasClientCategoryRole), so the AI-origin (المصدر) row is
              intentionally omitted: clients must never see AI provenance. */}
        </dl>
        {/* Task 286B — lightweight row: expand to review the full content through
            the shared client-mode card. The card's media + per-platform copy mount
            only when expanded (no list-wide eager media), and the client can switch
            platform variants inside the card. */}
        <div className="client-inbox__row-review">
          <button
            type="button"
            className="btn-text client-inbox__row-review-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'إخفاء المحتوى' : 'مراجعة المحتوى'}
          </button>
          {expanded ? (
            <div className="client-inbox__row-card">
              <IdeaReviewCard
                item={reviewItem}
                idx={0}
                mode="client"
                mediaSlot={renderClientMedia}
              />
            </div>
          ) : null}
        </div>
        {/* Task 104B Round 2 — asset-stage blocker when no produced deliverable is
            attached yet. The deliverable itself is shown inside the expanded card. */}
        {isAssetStage && !deliverable ? (
          <div className="banner banner-warn client-inbox__row-blocker" role="alert">
            لا يوجد أصل نهائي مرفوع لهذا العنصر بعد. لا يمكن اعتماد ما لم تَستلِم الأصل من الوكالة.
          </div>
        ) : null}
        <div className="client-inbox__row-actions">
          {canDecide ? (
            <>
              <button
                type="button"
                className="btn"
                disabled={inFlight || (isAssetStage && !deliverable)}
                onClick={() => onApprove()}
              >
                {inFlight ? 'جارٍ المعالجة…' : 'اعتماد'}
              </button>
              <button
                type="button"
                className="btn-text"
                disabled={inFlight}
                onClick={() => onRequestChanges()}
              >
                {inFlight ? '…' : 'طلب تعديل'}
              </button>
            </>
          ) : (
            // Codex round 2 — gate BOTH actions on canDecide. The
            // request-changes path is gated by the same RPC predicate
            // (client-category role + assignment + approval.client.decide)
            // as the approve path; rendering it without the perm would
            // produce a confusing 4xx if a non-deciding viewer ever
            // somehow reached the inbox.
            //
            // Task 104B Round 2 — copy is now stage-aware so a non-
            // deciding viewer gets the right hint at both stages.
            // Task 107E — the previous copy leaked the raw permission
            // key `approval.client.decide` to client users. The new
            // copy keeps the user-facing message Arabic-only.
            <p className="client-inbox__row-denied" role="status">
              {isAssetStage
                ? 'لا تملك صلاحية اعتماد الأصل النهائي أو طلب التعديل. تواصل مع مالك حساب العميل لطلب الصلاحية.'
                : 'لا تملك صلاحية اعتماد المحتوى أو طلب التعديل. تواصل مع مالك حساب العميل لطلب الصلاحية.'}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

// Task 104B Round 2 — embed renderer for the produced deliverable on
// asset-stage rows. Renders a download/preview link backed by the
// short-lived signed_url (the FE never sees the raw storage_key).
// Task 107E — promoted to a labeled section with a clear "الأصل
// النهائي للمراجعة" header, file-type chip in Arabic, and explicit
// open/download action so the deliverable doesn't hide inside a
// weak text row anymore.
function ProductionDeliverablePreview(props: {
  readonly deliverable: PendingClientApprovalDeliverable;
}) {
  const { deliverable } = props;
  const isImage = (deliverable.mime_type ?? '').toLowerCase().startsWith('image/');
  const mimeLabel = inboxMimeLabelAr(deliverable.mime_type);
  return (
    // Task 286B — internal task UUIDs were previously emitted as data-* attributes
    // here; they have no consumer and this section renders inside the client review,
    // so they are dropped (no internal IDs in client DOM).
    <section className="client-inbox__deliverable">
      <header className="client-inbox__deliverable-head">
        <span className="client-inbox__deliverable-title">الأصل النهائي للمراجعة</span>
        {mimeLabel ? <span className="client-inbox__deliverable-kind">{mimeLabel}</span> : null}
      </header>
      {isImage ? (
        <a
          href={deliverable.signed_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`فتح الأصل النهائي: ${deliverable.title}`}
          className="client-inbox__deliverable-thumb"
        >
          {/* Task 286B — lazy + no-referrer so the deliverable thumbnail never
              eagerly loads in list/inbox surfaces. */}
          <img
            src={deliverable.signed_url}
            alt={deliverable.title}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        </a>
      ) : null}
      <div className="client-inbox__deliverable-actions">
        <a
          href={deliverable.signed_url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-text client-inbox__deliverable-link"
        >
          {isImage ? 'فتح الأصل في تبويب جديد' : `تحميل الأصل: ${deliverable.title}`}
        </a>
      </div>
      {deliverable.summary ? (
        <div className="client-inbox__deliverable-notes">
          <span className="client-inbox__row-text-label">ملاحظات الإنتاج</span>
          <p>{deliverable.summary}</p>
        </div>
      ) : null}
    </section>
  );
}

function formatLocalDateTime(utcIso: string, timezone: string): string {
  try {
    const fmt = new Intl.DateTimeFormat('ar-SA', {
      timeZone: timezone,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return fmt.format(new Date(utcIso));
  } catch {
    return utcIso;
  }
}

// =====================================================================
// Task 104C — Publishing workflow V1 components
// =====================================================================

interface PublishJobsPanelProps {
  readonly item: ContentItemDetailRow;
  // Task 104C round 3 — set of platforms that have a matching
  // content_variant on the parent item. Drives both the schedule
  // CTA enable state and the modal's per-platform checkbox enable
  // state. Empty set ⇒ scheduling impossible (the schedule RPC
  // would 23514 for any platform passed in).
  readonly availablePlatforms: ReadonlySet<PublishWorkflowPlatform>;
  // Task 105A — earliest non-archived calendar_entries.scheduled_for
  // across the variants. The schedule modal prefills from this when
  // present, fallback to tomorrow 10:00 only if null.
  readonly plannedScheduledFor: { iso: string; timezone: string | null } | null;
  readonly jobsState: PublishJobsLoadState;
  readonly inFlight: boolean;
  readonly error: string | null;
  readonly canSchedule: boolean;
  readonly onOpenScheduleModal: (
    contentItemId: string,
    availablePlatforms: ReadonlySet<PublishWorkflowPlatform>,
    plannedScheduledFor: { iso: string; timezone: string | null } | null,
  ) => void;
  readonly onMarkPublished: (jobId: string) => Promise<void>;
  readonly onMarkFailed: (jobId: string) => Promise<void>;
  readonly onCancel: (jobId: string) => Promise<void>;
}

function PublishJobsPanel(props: PublishJobsPanelProps) {
  const { item, jobsState, inFlight, error, canSchedule, availablePlatforms, plannedScheduledFor } =
    props;
  const isApproved = item.status === 'client_asset_approved';
  const isScheduled = item.status === 'scheduled';
  const isPublished = item.status === 'published';

  const headerLabel = isApproved
    ? 'جاهز للجدولة'
    : isScheduled
      ? 'مهام النشر المجدولة'
      : 'مهام النشر المنشورة';

  // Task 104C round 3 — schedule CTA is disabled when no platform has
  // a matching variant. The schedule RPC would otherwise raise 23514
  // for every platform the operator selected.
  const hasAnyAvailable = availablePlatforms.size > 0;
  const availableLabels = PUBLISH_WORKFLOW_PLATFORMS.filter((p) => availablePlatforms.has(p))
    .map((p) => publishWorkflowPlatformLabel(p))
    .join(' / ');

  return (
    <div className="card" style={{ padding: 18, marginTop: 18 }}>
      <div className="sec-title">
        <h3>{headerLabel}</h3>
        <span className="meta">publish_jobs</span>
      </div>

      {error ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      ) : null}

      {isApproved ? (
        <div style={{ marginTop: 10 }}>
          <p className="body" style={{ color: 'var(--muted)' }}>
            {hasAnyAvailable
              ? `تم اعتماد الأصل النهائي. المنصات المتاحة للجدولة: ${availableLabels}.`
              : 'تم اعتماد الأصل النهائي، لكن لا توجد متغيرات (variants) مطابقة لأي من منصات النشر V1 (Instagram / TikTok / Snapchat). أنشئ المتغيّر المناسب قبل الجدولة.'}
          </p>
          {canSchedule ? (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn"
                disabled={inFlight || !hasAnyAvailable}
                onClick={() =>
                  props.onOpenScheduleModal(item.id, availablePlatforms, plannedScheduledFor)
                }
              >
                {inFlight ? 'جارٍ الفتح…' : 'جدولة النشر'}
              </button>
              {!hasAnyAvailable ? (
                <p className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                  زر الجدولة معطل: لا يوجد متغيّر لأي منصة V1 على هذه القطعة.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
              لا تملك صلاحية جدولة النشر (publish.schedule).
            </p>
          )}
        </div>
      ) : null}

      {(isScheduled || isPublished) && jobsState.status === 'loading' ? (
        <LoadingState title="جاري تحميل مهام النشر…" />
      ) : null}
      {(isScheduled || isPublished) && jobsState.status === 'error' ? (
        <ErrorState title="تعذر تحميل مهام النشر" error={(jobsState as { error: unknown }).error} />
      ) : null}
      {(isScheduled || isPublished) && jobsState.status === 'ready' ? (
        jobsState.jobs.length === 0 ? (
          <EmptyState
            title="لا توجد مهام نشر"
            description="المهام المرتبطة قد تكون أُلغيت كلها أو مؤرشفة."
          />
        ) : (
          <ul className="stack-sm" style={{ listStyle: 'none', padding: 0, marginTop: 10 }}>
            {jobsState.jobs.map((job) => (
              <PublishJobRowItem
                key={job.id}
                job={job}
                inFlight={inFlight}
                canSchedule={canSchedule}
                showMutationButtons={isScheduled}
                onMarkPublished={props.onMarkPublished}
                onMarkFailed={props.onMarkFailed}
                onCancel={props.onCancel}
              />
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

interface PublishJobRowItemProps {
  readonly job: PublishJobRow;
  readonly inFlight: boolean;
  readonly canSchedule: boolean;
  readonly showMutationButtons: boolean;
  readonly onMarkPublished: (jobId: string) => Promise<void>;
  readonly onMarkFailed: (jobId: string) => Promise<void>;
  readonly onCancel: (jobId: string) => Promise<void>;
}

function PublishJobRowItem(props: PublishJobRowItemProps) {
  const { job } = props;
  const target = job.publish_targets?.target ?? null;
  const platformLabel = target ? variantPlatformLabel(target) : '—';
  const statusVariant = publishJobStatusVariant(job.status);
  const statusLabel = publishJobStatusLabel(job.status);
  const scheduledLabel = formatLocalDateTime(job.scheduled_for, DEFAULT_TIMEZONE);
  const canMutate =
    props.canSchedule &&
    props.showMutationButtons &&
    (job.status === 'scheduled' || job.status === 'failed');
  const canCancel =
    props.canSchedule &&
    props.showMutationButtons &&
    (job.status === 'scheduled' || job.status === 'failed' || job.status === 'processing');

  return (
    <li className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span className="pill pill-info">{platformLabel}</span>
        <span className={`pill pill-${statusVariant}`}>{statusLabel}</span>
      </div>
      <div className="tiny" style={{ color: 'var(--muted)' }}>
        مجدول لـ: {scheduledLabel}
      </div>
      {job.completed_at ? (
        <div className="tiny" style={{ color: 'var(--muted)' }}>
          نُشر في: {formatLocalDateTime(job.completed_at, DEFAULT_TIMEZONE)}
        </div>
      ) : null}
      {job.failed_at ? (
        <div className="tiny" style={{ color: 'var(--muted)' }}>
          فشل في: {formatLocalDateTime(job.failed_at, DEFAULT_TIMEZONE)}
        </div>
      ) : null}
      {job.cancelled_at ? (
        <div className="tiny" style={{ color: 'var(--muted)' }}>
          أُلغي في: {formatLocalDateTime(job.cancelled_at, DEFAULT_TIMEZONE)}
        </div>
      ) : null}
      {job.external_post_url ? (
        <div className="tiny">
          <a href={job.external_post_url} target="_blank" rel="noopener noreferrer">
            رابط المنشور ↗
          </a>
        </div>
      ) : null}
      {job.external_post_id ? (
        <div className="tiny" style={{ color: 'var(--muted)' }}>
          معرّف المنشور: {job.external_post_id}
        </div>
      ) : null}
      {job.failure_message ? (
        <div className="tiny" style={{ color: 'var(--muted)' }}>
          سبب الفشل: {job.failure_message}
        </div>
      ) : null}
      {canMutate || canCancel ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
          {canMutate ? (
            <>
              <button
                type="button"
                className="btn btn-sm"
                disabled={props.inFlight}
                onClick={() => props.onMarkPublished(job.id)}
              >
                وسم كمنشور
              </button>
              <button
                type="button"
                className="btn-text btn-sm"
                disabled={props.inFlight}
                onClick={() => props.onMarkFailed(job.id)}
              >
                وسم كفاشل
              </button>
            </>
          ) : null}
          {canCancel ? (
            <button
              type="button"
              className="btn-text btn-sm"
              disabled={props.inFlight}
              onClick={() => props.onCancel(job.id)}
            >
              إلغاء المهمة
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function publishJobStatusLabel(status: PublishJobRow['status']): string {
  switch (status) {
    case 'scheduled':
      return 'مجدول';
    case 'processing':
      return 'قيد التنفيذ';
    case 'succeeded':
      return 'منشور';
    case 'failed':
      return 'فشل';
    case 'cancelled':
      return 'ملغى';
    default:
      return status;
  }
}

function publishJobStatusVariant(status: PublishJobRow['status']): string {
  switch (status) {
    case 'scheduled':
      return 'review';
    case 'processing':
      return 'info';
    case 'succeeded':
      return 'active';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'archived';
    default:
      return 'draft';
  }
}

interface SchedulePublishingModalProps {
  readonly state: ScheduleModalState;
  readonly onChange: (next: ScheduleModalState) => void;
  readonly onCancel: () => void;
  readonly onSave: () => Promise<void>;
}

function SchedulePublishingModal({
  state,
  onChange,
  onCancel,
  onSave,
}: SchedulePublishingModalProps) {
  const usesApprovedScheduleTime = state.plannedScheduledForIso !== null;
  const togglePlatform = (p: PublishWorkflowPlatform) => {
    // Round 3 fix: never let an unavailable platform enter the
    // selection set. The schedule RPC would 23514 immediately on
    // submit; surfacing the gate at click-time is friendlier.
    if (!state.availablePlatforms.has(p)) return;
    const next = new Set(state.selectedPlatforms);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    onChange({ ...state, selectedPlatforms: next });
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="جدولة النشر"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !state.saving) onCancel();
      }}
    >
      <div
        className="card"
        style={{ padding: 22, width: 'min(560px, 92vw)', maxHeight: '92vh', overflow: 'auto' }}
      >
        <h2 className="h-section">جدولة النشر</h2>
        <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 12 }}>
          اختر المنصات وموعد النشر. سيتم إنشاء مهمة نشر لكل منصة. التنفيذ يدوي في V1 — تنشر يدوياً
          ثم تضغط «وسم كمنشور».
        </p>

        <div style={{ marginTop: 14 }}>
          <label className="tiny" style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
            المنصات
          </label>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {PUBLISH_WORKFLOW_PLATFORMS.map((p) => {
              const available = state.availablePlatforms.has(p);
              return (
                <label
                  key={p}
                  title={available ? '' : 'لا يوجد متغيّر لهذه المنصة على هذه القطعة.'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: available ? 1 : 0.45,
                    cursor: available ? 'pointer' : 'not-allowed',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={state.selectedPlatforms.has(p)}
                    onChange={() => togglePlatform(p)}
                    disabled={state.saving || !available}
                  />
                  <span>{publishWorkflowPlatformLabel(p)}</span>
                  {!available ? (
                    <span className="tiny" style={{ color: 'var(--muted)' }}>
                      (لا يوجد متغيّر)
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
        </div>

        {usesApprovedScheduleTime ? (
          <div className="banner banner-info" role="note" style={{ marginTop: 14 }}>
            سيتم استخدام موعد النشر المعتمد لهذا المحتوى. لا يمكن تعديل التاريخ أو الوقت من نافذة
            الجدولة.
          </div>
        ) : null}

        <div style={{ marginTop: 14, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label className="tiny" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
              التاريخ
            </label>
            <input
              type="date"
              className="input"
              value={state.scheduledLocalDate}
              onChange={(e) => onChange({ ...state, scheduledLocalDate: e.target.value })}
              disabled={state.saving || usesApprovedScheduleTime}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label className="tiny" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
              الوقت
            </label>
            <input
              type="time"
              className="input"
              value={state.scheduledLocalTime}
              onChange={(e) => onChange({ ...state, scheduledLocalTime: e.target.value })}
              disabled={state.saving || usesApprovedScheduleTime}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            المنطقة الزمنية: {state.timezone}
          </span>
        </div>

        {/*
         * Task 104C round 2 — per-platform caption overrides removed.
         * Captions are customer-approved at `client_asset_approved`
         * and the schedule modal cannot rewrite them. To change wording
         * for a platform, return the item to draft and re-run content
         * approval for that platform.
         */}
        <div style={{ marginTop: 14 }}>
          <p className="tiny" style={{ color: 'var(--muted)' }}>
            النص المعتمد لكل منصة يُستخدم كما هو من النسخة المعتمدة. لتعديل النص ارجع إلى المسودة
            وأعد دورة موافقة المحتوى لتلك المنصة.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <label className="tiny" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            ملاحظات للوكالة (اختياري — لا تُرسل للعميل)
          </label>
          <textarea
            className="input"
            value={state.notes}
            onChange={(e) => onChange({ ...state, notes: e.target.value })}
            disabled={state.saving}
            rows={2}
            style={{ width: '100%' }}
          />
        </div>

        {state.error ? (
          <div className="banner banner-error" role="alert" style={{ marginTop: 12 }}>
            {state.error}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-text" onClick={onCancel} disabled={state.saving}>
            إلغاء
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void onSave();
            }}
            disabled={state.saving || state.selectedPlatforms.size === 0}
          >
            {state.saving ? 'جارٍ الجدولة…' : 'جدولة'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Task 105A — translate the structured request-changes feedback
// (notes + optional category + optional edited version) into the
// plain-text `reason` field carried by the transition RPC. The text
// is formatted so it's both human-readable in the agency's audit
// view AND machine-greppable later (when a follow-up task lifts the
// fields to dedicated columns / metadata jsonb keys).
function rejectionCategoryLabel(c: ClientRejectionCategory): string {
  switch (c) {
    case 'content_quality':
      return 'جودة المحتوى';
    case 'tone':
      return 'النبرة';
    case 'timing':
      return 'التوقيت';
    case 'imagery':
      return 'الصور / التصميم';
    case 'other':
      return 'أخرى';
    default:
      return '';
  }
}

function buildRequestChangesReason(s: RequestChangesModalState): string {
  const parts: string[] = ['طلب تعديل من العميل.'];
  const cat = rejectionCategoryLabel(s.rejectionCategory);
  if (cat.length > 0) parts.push(`الفئة: ${cat}`);
  parts.push(`الملاحظات:\n${s.feedbackNotes.trim()}`);
  const edited = s.editedVersion.trim();
  if (edited.length > 0) parts.push(`النسخة المعدّلة المقترحة:\n${edited}`);
  return parts.join('\n\n');
}

interface RequestChangesModalProps {
  readonly state: RequestChangesModalState;
  readonly onChange: (next: RequestChangesModalState) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}

function RequestChangesModal({ state, onChange, onCancel, onSave }: RequestChangesModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="طلب تعديل"
      className="request-changes-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !state.saving) onCancel();
      }}
    >
      <div className="card request-changes-modal">
        <div className="request-changes-modal__body">
          <header className="request-changes-modal__header">
            <span className="request-changes-modal__eyebrow">رد العميل</span>
            <h2 className="request-changes-modal__title">طلب تعديل من العميل</h2>
            <p className="request-changes-modal__subject">
              <span className="request-changes-modal__subject-label">المنشور:</span>{' '}
              {state.contentItemTitle}
            </p>
          </header>

          <fieldset className="request-changes-modal__section" disabled={state.saving}>
            <div className="request-changes-modal__field">
              <label className="lbl" htmlFor="request-changes-notes">
                الملاحظات <span className="request-changes-modal__required">*</span>
              </label>
              <textarea
                id="request-changes-notes"
                value={state.feedbackNotes}
                onChange={(e) => onChange({ ...state, feedbackNotes: e.target.value })}
                rows={4}
                placeholder="ما الذي يحتاج إلى تعديل؟"
              />
            </div>

            <div className="request-changes-modal__field">
              <label className="lbl" htmlFor="request-changes-category">
                فئة السبب (اختياري)
              </label>
              <select
                id="request-changes-category"
                value={state.rejectionCategory}
                onChange={(e) =>
                  onChange({
                    ...state,
                    rejectionCategory: e.target.value as ClientRejectionCategory,
                  })
                }
              >
                <option value="">— اختر إن أمكن —</option>
                <option value="content_quality">جودة المحتوى</option>
                <option value="tone">النبرة</option>
                <option value="timing">التوقيت</option>
                <option value="imagery">الصور / التصميم</option>
                <option value="other">أخرى</option>
              </select>
            </div>

            <div className="request-changes-modal__field">
              <label className="lbl" htmlFor="request-changes-edited">
                النسخة المعدّلة المقترحة (اختياري)
              </label>
              <textarea
                id="request-changes-edited"
                value={state.editedVersion}
                onChange={(e) => onChange({ ...state, editedVersion: e.target.value })}
                rows={4}
                placeholder="إن كنت ترغب في اقتراح صياغة بديلة، اكتبها هنا."
              />
            </div>
          </fieldset>

          {state.error ? (
            <div className="banner banner-error request-changes-modal__error" role="alert">
              {state.error}
            </div>
          ) : null}
        </div>

        <div className="request-changes-modal__actions">
          <button type="button" className="btn-text" onClick={onCancel} disabled={state.saving}>
            إلغاء
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => onSave()}
            disabled={state.saving || state.feedbackNotes.trim().length === 0}
          >
            {state.saving ? 'جارٍ الإرسال…' : 'إرسال طلب التعديل'}
          </button>
        </div>
      </div>
    </div>
  );
}

function publishWorkflowPlatformLabel(p: PublishWorkflowPlatform): string {
  switch (p) {
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'snapchat':
      return 'Snapchat';
    default:
      return p;
  }
}

// Task 104C round 3 — derive the set of PublishWorkflowPlatform
// values that have a matching content_variant on the parent item.
// The schedule RPC's canonical mapping is one-to-one:
//   instagram → instagram_feed
//   tiktok    → tiktok_video
//   snapchat  → snapchat_story
// Non-canonical instagram_story / instagram_reel variants do NOT
// enable 'instagram' until the RPC is widened. This mirrors the
// schedule RPC's per-platform existence check so the FE never
// dispatches a call that would 23514 server-side.
// Task 105A — pick the earliest non-archived calendar_entries
// scheduled_for across the loaded variants (and its associated
// timezone). The publish schedule modal prefills from this when
// present so the operator's planned publish time is the default.
// Returns null when no calendar entry is on file — the modal then
// falls back to tomorrow 10:00.
function pickEarliestPlannedScheduledFor(
  variants: ReadonlyArray<ContentVariantRow>,
): { iso: string; timezone: string | null } | null {
  let bestMs = Number.POSITIVE_INFINITY;
  let best: { iso: string; timezone: string | null } | null = null;
  for (const v of variants) {
    for (const e of v.calendar_entries) {
      if (e.archived_at !== null) continue;
      const ms = Date.parse(e.scheduled_for);
      if (!Number.isFinite(ms)) continue;
      if (ms < bestMs) {
        bestMs = ms;
        best = { iso: e.scheduled_for, timezone: e.timezone ?? null };
      }
    }
  }
  return best;
}

function collectAvailablePublishPlatforms(
  variants: ReadonlyArray<ContentVariantRow>,
): ReadonlySet<PublishWorkflowPlatform> {
  // The list-content-variants endpoint already filters out
  // archived_at-set rows, so the local set passed here is effectively
  // the live variants only. The schedule RPC re-checks existence
  // server-side under FOR UPDATE — this FE check is for UX, not a
  // security gate.
  const out = new Set<PublishWorkflowPlatform>();
  for (const v of variants) {
    if (v.platform === 'instagram_feed') out.add('instagram');
    else if (v.platform === 'tiktok_video') out.add('tiktok');
    else if (v.platform === 'snapchat_story') out.add('snapchat');
  }
  return out;
}
